// Viewer3D offers public methods for developers to use.
// Viewer3DImpl is the implementation file for Viewer3D and is only used by Viewer3D.js
//
// Viewer3D does things like parameter validation.
// Viewer3DImpl does the actual work, by interfacing with other internal components, such as the MaterialManager.

/** @import { RenderModel } from "../wgs/scene/RenderModel" */
/** @import { WebGLRenderer } from "../wgs/render/WebGLRenderer" */
/** @import { OverlayOptions } from './OverlayManager' */
/** @import { LeanBufferGeometry } from "../wgs/scene/BufferGeometry" */

import { getGlobal, isNodeJS, isMobileDevice } from "../compat";
import { ScreenShot } from "./ScreenShot";
import { ProgressState } from "./ProgressState";
import { RenderScene } from "../wgs/scene/RenderScene";
import { RenderContext } from "../wgs/render/RenderContext";
import { MaterialManager } from "../wgs/render/MaterialManager";
import { MultiModelSelector } from "../tools/Selector";
import { MultiModelVisibilityManager } from "../tools/MultiModelVisibilityManager";
import { LightPresets, DefaultLightPreset, DefaultLightPreset2d, BackgroundPresets, copyLightPreset } from "./LightPresets";
import { GroundShadow } from "../wgs/render/GroundShadow";
import { OtgResourceCache } from "../file-loaders/main/OtgResourceCache";
import { logger } from "../logger/Logger";
import { TextureLoader } from "../file-loaders/main/TextureLoader";
import { unconsolidateModels, reconsolidateModels } from "../wgs/render/LostContextRecovery";
import { RenderFlags } from "../wgs/scene/RenderFlags";
import * as shadow from "../wgs/render/ShadowMap";
import { ResetFlags } from "../wgs/scene/ResetFlags";
import { GroundReflection } from "../wgs/render/GroundReflection";
import { ModelLayers } from "./ModelLayers";
import { FragmentPointer } from "../wgs/scene/FragmentList";
import { getResourceUrl } from "../globals";
import { SAOShader } from "../wgs/render/SAOShader";
import { screenPickInfo, VBIntersector } from "../wgs/scene/VBIntersector";
import { VertexBufferReader } from "../wgs/scene/VertexBufferReader";
import { BoundsCallback } from "../wgs/scene/BoundsCallback";
import THREE from "three";
import { UnifiedCamera } from "../tools/UnifiedCamera";
import * as et from "./EventTypes";
import { Navigation } from "../tools/Navigation";
import { SelectionType } from "../tools/SelectionType";
import { BubbleNode } from "./bubble";
import { SceneMath } from "../wgs/scene/SceneMath";
import { GlobalManagerMixin } from './GlobalManagerMixin';
import { Prefs3D, Prefs2D, Prefs, FPS_TARGET_MODES } from "./PreferenceNames";
import SheetRenderContext from "../wgs/render/SheetRenderContext";
import { LMVRenderer } from "../wgs/render/LMVRenderer";
import { Renderer } from "../wgs/wgpu/Renderer";
import { EdgeColor, EdgeOpacity } from "../wgs/wgpu/main/MainPass";
import { RenderContextWebGPU } from "../wgs/wgpu/RenderContextWebGPU";
import { USE_DEFERRED_CONSOLIDATION, USE_OUT_OF_CORE_TILE_MANAGER, USE_WEBGPU, USE_MULTI_MATERIAL_RENDER_CALLS, RAY_INTERSECT_HIT_MARGIN_PIXELS } from "../wgs/globals";
import { RenderLayerManager } from "./RenderLayerManager";
import { GetDefaultWebGPUDevice } from "../wgs/wgpu/globals";
import { OutOfCoreTileManager } from '../wgs/scene/out-of-core-tile-manager/OutOfCoreTileManager';
import { renderBudgetManager } from './RenderBudgetManager';
import { useEarlyConsolidation } from '../wgs/scene/consolidation/Consolidation';
import { isChrome, isMac } from '../compat';
import { CreateCubeMapFromColors } from "../wgs/render/DecodeEnvMap";
import { WebGLFenceManager, WebGPUFenceManager } from '../wgs/render/FenceManager';
import { computeWorldUpAndScaling } from '../file-loaders/lmvtk/common/SvfPlacementUtils';
import { mainLoop } from './MainLoop';
import {
  CMD_ALWAYS_DO,
  CMD_DO_AFTER, CMD_NORMAL_SEQUENCE,
  RenderCommandSystem, RENDERMODE_FULL,
  RENDERMODE_PROGRESSIVE, RENDERMODE_SILENT } from
"../wgs/scene/RenderCommandSystem";
import { FeatureFlags, InternalFeatureFlags, PublicFeatureFlags } from './FeatureFlags';
import { detectFromRendererString, detectFromWebGPUInfo } from './GpuPerformanceDetector';

/** @import {Viewer3D} from "./Viewer3D.js" */
/** @import {Model} from "./Model.js" */

//default parameters for WebGL initialization
export let InitParametersSetting = {
  canvas: null,
  antialias: false,
  alpha: false,
  premultipliedAlpha: false,
  preserveDrawingBuffer: true,
  stencil: false,
  depth: false,
  logarithmicDepthBuffer: false,
  useCameraOffset: false
};

var ModelSettingsEnvironment = null;

// Create a webgl or webgpu renderer
export function createRenderer(canvas, initParams = {}) {
  if (isNodeJS()) {
    return;
  }

  InitParametersSetting.useCameraOffset = Autodesk.Viewing.FeatureFlags.isEnabled(Autodesk.Viewing.Private.InternalFeatureFlags.EnableCameraOffset);

  var params = Object.assign({}, InitParametersSetting, initParams);
  params.canvas = canvas;
  let renderer;
  try {
    if (USE_WEBGPU) {
      // When using WebGPU, there must always be a default device available.
      const device = GetDefaultWebGPUDevice();
      renderer = new Renderer(device, params);
    } else {
      renderer = new LMVRenderer(params);
    }
  } catch (e) {
    return null;
  }

  // Simplify debugging
  renderer.name = 'MainRenderer';

  if (!renderer.getContext || renderer.getContext && !renderer.getContext()) {
    return null;
  }

  renderer.autoClear = false;

  //Turn off scene sorting by THREE -- this is ok if we
  //do progressive draw in an order that makes sense
  //transparency-wise. If we start drawing using a frustum culling
  //r-tree or there are problems with transparency we'd have to turn on sorting.
  renderer.sortObjects = false;

  return renderer;
}

// Local helper variables used in functions, preallocated to avoid reallocations
const _ray = new THREE.Ray();
const _tmpCameraMatrix = new THREE.Matrix4();
const _tmpViewMatrix = new THREE.Matrix4();
const _tmpBox = new THREE.Box3();
const _bounds = new THREE.Box3();
const _box3 = new THREE.Box3();

export class Viewer3DImpl {
  _currentLightPreset = -1;
  _oldLightPreset = -1;
  _3dLightPreset = -1; // LMV-5655: Keeps track of the last 3d light preset
  _oldCallback = null;

  _worldUp = undefined;
  _worldUpName = "y";

  _needsResize = undefined;
  _newWidth = undefined;
  _newHeight = undefined;
  _renderLoopOn = undefined;
  /** @type {MaterialManager} */_materials = undefined;
  /** @type {WebGLRenderer | Renderer} */_webglrender = undefined;
  /** @type {RenderContext | RenderContextWebGPU } */_renderer = undefined;
  _sheetRenderer = undefined;

  _shadowMaps = undefined;

  _lastRenderTime = undefined;

  // Default direction in world-space from which we get the most light from. Needed for shadow casting.
  // The default is only used if no direction is specified by light preset or model.
  _shadowLightDirDefault = null; // {THREE.Vector3}
  _shadowLightDir = null; //

  _lightDirDefault = null;

  _needsClear = false;
  _needsRender = false;
  _overlayDirty = false;

  _sceneDirty = false;

  // A "silent render" means to do a full, but interruptible, render in the background. Display the result on completion.
  // The idea is to make a good-quality render after a progressive render occurs, or after some new content has been loaded,
  // or some other situation where we don't want to "lose progress," that is, we don't want to do a progressive render but
  // rather want to add to or modify an existing render on the screen.
  _deferredSilentRender = false;
  _immediateSilentRender = false;

  _cameraUpdated = undefined;

  _explodeScale = 0;
  _explodeOptions = {
    magnitude: 4,
    depthDampening: 0
  };

  _lastHighResTimeStamp = 0;

  _frameTimeAvg = 1000.0 / 60.0;
  _frameTimeSamples = 0;

  _isLoading = true; // turned off in onLoadComplete()

  /** @type {GroundShadow} */_groundShadow = undefined;
  /** @type {GroundReflection} */_groundReflection = undefined;

  _envMapBackground = false;

  /** @type {RenderScene} */_renderScene = undefined;

  _lightsInitialized = false;
  _defaultLightIntensity = 1.0;
  _defaultDirLightColor = null; // {THREE.Color}
  _defaultAmbientColor = null; //

  _edgeColorMain = EdgeColor.Dark.clone();
  _edgeColorGhosted = EdgeColor.DarkGhosted.clone();
  _edgeColorMainOverridden = false;
  _edgeColorGhostedOverridden = false;
  _ghostingDark = true;

  _lmvDisplay = getGlobal(); // return 'window', or something else for NodeJs context that won't work anyways.

  // render command system
  /** @type {RenderCommandSystem} */
  _rcs = undefined;

  //OTG geom cache
  _geomCache = undefined;

  _isRightBtnSelectionEnabled = false;

  // keys: name of cutplane set. values: array of cutplanes
  _cutPlaneSets = {};
  // Global toggle which skips rendering any fragment inside the smallest convex shape generated by the cutplanes
  // Applies to all cutplane sets at once. Used primarily for inverted section box
  _cutPlaneHideInterior = false;

  // 2D rendering onto a cutplane can only be adjusted for a single cutplane. This key defines for which one:
  // The first cutplane in this cutPlaneSet.
  _cutPlaneSetFor2DRendering = "";

  // we assume the program starts in a "doing work" state
  _workPreviousTick = true;
  _workThisTick = undefined;
  _renderTicksSinceClear = 0;

  /**
   * True when the renderer has no more pending work -- all progressive passes are done and the frame is stable.
   * Mirrors the flags tick() gates a re-render on, minus _overlayDirty: rollover highlighting sets that on every
   * mouse move, so including it would leave the frame reported as unconverged for as long as the cursor is over
   * the canvas, without any of the G-buffer targets actually changing.
   */
  get isRenderingConverged() {
    return !this._workPreviousTick && !this._sceneDirty && !this._needsClear && !this._needsRender;
  }

  _useWebGPU = false;

  _screenSpaceLineWidth = false;
  _lastSSLWEnabled = false;
  _lastSSLWPixelsPerUnit = -1;

  _frameTimePriority = 1;

  // once we gained confidence in deferred consolidation, we can remove this flag together with parallel consolidation
  useDeferredConsolidation = false;

  loader = null;
  canvasBoundingclientRectDirty = true;

  nearRadius = 0;
  defaultLoadingAnimationDuration = 300; // Time in milliseconds to load a 2D line.
  lastTime2dGeometryCreated = 0;

  // Optional: If >0, models whose camera distance exceeds this value are excluded from near/far-plane computation.
  maxModelDistance = 0;

  // Indicates whether this viewer instance is currently visible
  _viewerVisible = null;

  /** @type {MultiModelVisibilityManager} */
  visibilityManager = null;

  _progressEvent = { type: et.PROGRESS_UPDATE_EVENT, state: ProgressState.LOADING, percent: 0 };

  // Initialize other properties
  camera = null;
  autocam = null;
  navigation = null;
  keyFrameAnimator = null;
  selector = null;
  prefs = null;

  /**
   * @constructor
   * @private
   * @param {HTMLCanvasElement} thecanvas
   * @param {Viewer3D} theapi
   */
  constructor(thecanvas, theapi) {
    this.setGlobalManager(theapi.globalManager);

    // Listener used to load StandardSurface extension when we load
    // a model with standard surface materials
    this._stdSurfaceLoadListener = (e) => {
      var model = e.model;
      // Make sure the viewr is still active and the model has standard surface materials
      if (thecanvas && model && model.getData() && model.getData().stdSurfMats &&
      model.getData().stdSurfMats["materials"]) {
        // If we have surface materials then load the extension
        this.api.loadExtension("Autodesk.StandardSurface").then((extension) => {
          // Extension loaded, process the model if the loader hasn't been destroyed
          if (extension)
          extension.processModel(model);
        });
      }
    };

    this._onModelRootLoaded = (e) => e.model.modelRootLoaded = true;

    this._onLoaderRepaintRequest = (e) => {
      if (!e?.model || this._renderScene.findModel(e.model.id)) {
        // only redraw if the source of this event is unknown or visible
        this.invalidate(false, true);
      }
    };

    if (thecanvas) {
      this._interval = setInterval(() => {
        // Only start reporting the framerate to ADP when there's been "enough" samples
        if (this._isLoading || this._frameTimeSamples < 60) {
          return;
        }
        this.track({ name: 'fps', value: Number(this.fps().toFixed(2)), aggregate: 'last' });
      }, 30000);
    }

    this.api = theapi;
    this.canvas = thecanvas;

    this._renderLayerManager = new RenderLayerManager(this);
  }

  //Slower initialization pieces can be delayed until after
  //we start loading data, so they are separated out here.
  initialize(initOptions = {}) {
    this._worldUp = new THREE.Vector3(0, 1, 0);
    this._renderScene = new RenderScene(this.api);

    this._useWebGPU = USE_WEBGPU;

    this._webglrender = initOptions.glrenderer || createRenderer(this.canvas, initOptions.webglInitParams);
    if (!this._webglrender && !isNodeJS()) {
      return;
    }

    if (this._webglrender) {
      this.onGpuContextLost = this.onGpuContextLost.bind(this);
      this.onWebGLcontextRestored = this.onWebGLcontextRestored.bind(this);
      this._webglrender.addEventListener(LMVRenderer.Events.WEBGL_CONTEXT_LOST, this.onGpuContextLost);
      this._webglrender.addEventListener(Renderer.Events.WEBGPU_DEVICE_LOST, this.onGpuContextLost);

      this._webglrender.refCount++;

      // Optional: Allow viewer to resurrect after webgl context restore
      if (initOptions.enableContextRestore) {
        this._webglrender.enableContextRestore = true;
        this._webglrender.addEventListener(LMVRenderer.Events.WEBGL_CONTEXT_RESTORED, this.onWebGLcontextRestored);
      }
    }

    this._renderer = initOptions.renderer || (this._useWebGPU ? new RenderContextWebGPU() : new RenderContext());
    this._renderer.init(this._webglrender, this.canvas ? this.canvas.clientWidth : 0, this.canvas ? this.canvas.clientHeight : 0, initOptions);

    this._adaptEdgeOpacityToDPR(); // initial edge opacity based on the current device pixel ratio

    if (this._useWebGPU) {
      this.fenceManager = new WebGPUFenceManager(this._renderer.getRenderer());
    } else {
      this.fenceManager = new WebGLFenceManager(this._renderer);
    }

    this._materials = initOptions.materialManager || new MaterialManager(this._webglrender);
    if (USE_MULTI_MATERIAL_RENDER_CALLS) {
      this._webglrender.getUBOManager().setMaterialManager(this._materials);
    }

    this._materials.refCount++;

    this.useDeferredConsolidation = initOptions && initOptions.useDeferredConsolidation !== undefined ? !!initOptions.useDeferredConsolidation : USE_DEFERRED_CONSOLIDATION;

    this.camera = new UnifiedCamera(this.canvas ? this.canvas.clientWidth : 512, this.canvas ? this.canvas.clientHeight : 512);
    this.lightsOn = false;
    // we'll fill this in later, in initLights.
    this.lights = [];
    // pass in when lightsOn is false;
    this.no_lights = [];

    this._defaultDirLightColor = new THREE.Color().setRGB(1, 1, 1);
    this._defaultAmbientColor = new THREE.Color().setRGB(1, 1, 1);

    // this.camera = this.unicam.getOrthographicCamera();
    this.cameraChangedEvent = { type: et.CAMERA_CHANGE_EVENT, camera: this.camera };

    this._shadowLightDirDefault = new THREE.Vector3(1, 1, 1); // which does not match the this._lightDirDefault
    this._shadowLightDir = new THREE.Vector3().copy(this._shadowLightDirDefault);
    this._lightDirDefault = new THREE.Vector3(-1, 0, 1); // a horizontal light, which is not a good default shadowd direction

    //This scene will just hold the camera and lights, while
    //we keep groups of progressively rendered geometry in
    //separate geometry scenes.
    this.emptyScene = new THREE.Scene();
    this.emptyScene.sortOrder = -Infinity;

    // Legacy 'scene' and 'sceneAfter' are kept for backward compatibility
    // New content should be added as render layers instead.
    this.scene = new THREE.Scene();
    this.sceneAfter = new THREE.Scene();
    this.sceneAfter.sortObjects = false;

    // This is only used for legacy overlay rendering based on Three.js scenes, keeping for backward compatibility
    this.overlayScenes = {};

    // This creates new render layers for the legacy scenes:
    // - [emptyScene, scene] with negative renderOrder
    // - [sceneAfter] with positive renderOrder
    this._setupLegacySceneLayers();

    this._setupSelectionHighlight();

    // Point selection reuses selectionMaterialBase as override (see getCustomOverrideMaterial).
    // materialPost is null intentionally: point clouds do not need the occluded-parts
    // "rendered on top" tint that mesh geometry gets via selectionMaterialTop.
    this.createOverlayScene("selection_points", this.selectionMaterialBase, null);

    this.selectionMeshes = {};

    this.fadeMaterial = new THREE.MeshPhongMaterial({ color: 0xffffff, opacity: 0.1, reflectivity: 0, transparent: true, depthWrite: false });
    this.fadeMaterial.packedNormals = true;
    this.fadePointMaterial = this._materials.createOverridePointMaterial(this.fadeMaterial, { size: 1.0 });
    this._materials.addOverrideMaterialVariants(this.fadeMaterial, this.fadePointMaterial);
    if (USE_MULTI_MATERIAL_RENDER_CALLS) {
      this.fadeMaterial.variants.forEach((mat) => mat.isFadeMaterial = true);
    }
    this._materials.addMaterial("__fadeMaterial__", this.fadeMaterial, true);

    const ghostLineMaterial = this._renderer.getGhostLineMaterial?.();
    if (ghostLineMaterial) {
      this._materials.addOverrideMaterialVariants(ghostLineMaterial);
    }

    this.setSelectionColor(0x6699ff);

    this.updatePolygonOffset();

    //Settings exposed to GUI:
    this.progressiveRender = true;
    this.swapBlackAndWhite = false;

    this.controls = {
      update: function () {
        this.camera.lookAt(this.camera.target);
        this.camera.updateProjectionMatrix();
        this.camera.dirty = false;
      },
      handleResize: function () {},
      recordHomeView: function () {},
      uninitialize: function () {},
      isToolActivated: function () {return false;}
    };

    this.selector = new MultiModelSelector(this);

    this.visibilityManager = new MultiModelVisibilityManager(this);

    this.showGhosting = true;
    this.showOverlaysWhileMoving = true;
    this.skipAOWhenMoving = false;

    this.keyFrameAnimator = null;
    this.zoomBoundsChanged = true;

    var cc = LightPresets[DefaultLightPreset].bgColorGradient;
    this.setClearColors(cc[0], cc[1], cc[2], cc[3], cc[4], cc[5]);

    if (this._useWebGPU) {
      this._groundShadow = this._webglrender.getGroundShadowPass();
    } else {
      this._groundShadow = new GroundShadow(this._webglrender);
    }
    this._groundShadow.enabled = true;

    this._rcs = new RenderCommandSystem(this);
    if (USE_OUT_OF_CORE_TILE_MANAGER) {
      this._rcs.updateTargetRenderBudget();
    }

    // WebGPU renderer doesn't need this. Unlike WebGLRenderer, it is explicitly aware of the cutplanes and updates the pass uniforms by itself.
    if (!this._useWebGPU) {
      // TODO_NOP: hack register materials for cutplanes
      this._materials.addMaterialNonHDR("groundShadowDepthMaterial", this._groundShadow.getDepthMaterial());
      this._materials.addOverrideMaterial("normalsMaterial", this._renderer.getDepthMaterial());
      this._materials.addOverrideMaterial("edgeMaterial", this._renderer.getEdgeMaterial());
    }

    //just meant to do an initial clear to the background color we want.
    this._renderer.beginScene(this.emptyScene, this.camera, this.no_lights, true);

    this._renderer.composeFinalFrame(true);
    this.api.addEventListener(et.MODEL_ROOT_LOADED_EVENT, this._stdSurfaceLoadListener);
    this.api.addEventListener(et.MODEL_ROOT_LOADED_EVENT, this._onModelRootLoaded);
    this.api.addEventListener(et.LOADER_REPAINT_REQUEST_EVENT, this._onLoaderRepaintRequest);

    if (USE_OUT_OF_CORE_TILE_MANAGER) {
      OutOfCoreTileManager.registerViewer(this);
      renderBudgetManager.registerViewer(this, this._frameTimePriority);
    }

    if (FeatureFlags.isEnabled(InternalFeatureFlags.EnableNotificationManager)) {
      this.api.loadExtension('Autodesk.NotificationManager', { enableUI: true }).then(() => this._detectGpuPerformance());
    } else {
      this._detectGpuPerformance();
    }
  }

  _detectGpuPerformance(overrideDeviceInfo = null) {
    if (!FeatureFlags.isEnabled(PublicFeatureFlags.SlowGpuDetection)) {
      return;
    }

    const vendor = overrideDeviceInfo?.webgl?.vendor || this._webglrender?._gpuVendor || '';
    const renderer = overrideDeviceInfo?.webgl?.renderer || this._webglrender?._gpuRenderer || '';

    let result = detectFromWebGPUInfo(overrideDeviceInfo);
    if (!result) {
      result = detectFromRendererString(vendor, renderer);
    }

    if (!result) {
      return;
    }

    const detectionModes = this.api.config && this.api.config.gpuWarningDetectionModes || ['integrated-gpu'];
    if (!detectionModes.includes(result.reason)) {
      return;
    }

    // Reduce FPS target so the progressive renderer draws more geometry per tick on slow hardware.
    this.api.prefs.set(Prefs.PROGRESSIVE_FPS_TARGET, FPS_TARGET_MODES.MODERATE);

    // Fire the event asynchronously so that GuiViewer3D and application event listeners
    // are all registered by the time the event fires (GuiViewer3D subscribes after
    // Viewer3DImpl.initialize() returns).
    const api = this.api;
    setTimeout(() => {
      if (!api?.impl) return; // viewer may have been destroyed
      const evt = {
        type: et.SLOW_GPU_WARNING_EVENT,
        reason: result.reason,
        rendererInfo: result.rendererInfo,
        _defaultSuppressed: false,
        suppressDefault: function () {evt._defaultSuppressed = true;}
      };
      api.dispatchEvent(evt);
    }, 0);
  }

  getSheetRenderer(createOnDemand) {
    if (!this._sheetRenderer && createOnDemand) {
      this._sheetRenderer = new SheetRenderContext(this, this._renderer, this._webglrender, this._materials);
    }

    return this._sheetRenderer;
  }

  isSheetRendererNeeded() {
    return !this.is2d && this.get2DModels().length > 0;
  }

  get2DModels() {
    return this._renderScene.getModels().filter((m) => m.is2d());
  }

  get3DModels() {
    return this._renderScene.getModels().filter((m) => m.is3d());
  }

  //Bridge between the render queue and render context
  //For passing pieces of model to the renderer during
  //timed progressive rendering, while also taking into account
  //the current rendering mode of the viewer
  renderSomeCallback(scene) {

    //Ideally, here we only want the piece of the
    //render function that specifically renders geometries,
    //and none of the camera update stuff that we already do
    //once in beginProgressive() -- but this requires
    //some refactoring of THREE.WebGLRenderer.
    const is2DScene = scene.frags?.is2d;
    var phase = this._rcs.phase;
    var wantColor = true;
    var wantSAO = phase == RenderFlags.RENDER_NORMAL;
    var wantID = this._renderer.settings.idbuffer && phase != RenderFlags.RENDER_HIDDEN;

    const isGhosted = phase === RenderFlags.RENDER_HIDDEN;

    this.prepareEdgesForGhostedPass(isGhosted);

    const ghostedEdgesOnly = this._renderer.isGhostedEdgesOnly();

    if (isGhosted && !scene.ignoreFadeMaterial) {
      // Use the ghost-line material so that line geometry still renders with the ghosted color + dithering.
      // Faces are skipped via scene.edgesOnly.
      scene.overrideMaterial = ghostedEdgesOnly ? this._renderer.getGhostLineMaterial?.() : this.fadeMaterial;
    } else if (phase === RenderFlags.RENDER_HIGHLIGHTED) {
      scene.overrideMaterial = this.highlightMaterial;
    }

    if (USE_MULTI_MATERIAL_RENDER_CALLS) {
      scene.highlightMaterial = this.highlightMaterial;
    }

    const event = {
      type: et.RENDER_SCENE_PART,
      scene, wantColor, wantSAO, wantID,
      context: this._renderer
    };

    this.api.dispatchEvent(event);

    if (!this.is2d && is2DScene) {
      this.getSheetRenderer(true).renderScenePart(scene, wantColor, wantSAO, wantID);
      scene.overrideMaterial = null;
      return;
    }

    const prevEdgesOnly = scene.edgesOnly;
    scene.edgesOnly = prevEdgesOnly || !this.is2d && isGhosted && ghostedEdgesOnly;

    this._renderer.renderScenePart(scene, wantColor, wantSAO, wantID);

    scene.edgesOnly = prevEdgesOnly;
    scene.overrideMaterial = null;
  }

  _updateFPS(highResTimeStamp) {
    this._frameTimeSamples++;

    if (this._lastHighResTimeStamp <= highResTimeStamp && this._lastHighResTimeStamp > 0) {
      this._frameTimeAvg = this._frameTimeAvg * 0.8 + (highResTimeStamp - this._lastHighResTimeStamp) * 0.2;
    }
  }

  _updateAnimations(highResTimeStamp) {
    if (this.keyFrameAnimator) {
      var delta = this._lastHighResTimeStamp > 0 ? (highResTimeStamp - this._lastHighResTimeStamp) / 1000 : 0;
      var updateFlags = this.keyFrameAnimator.update(delta);
      if (updateFlags) {
        this.sceneUpdated(true);
        if (updateFlags & this.keyFrameAnimator.UPDATE_CAMERA)
        return true;
      }
    }
    return false;
  }

  /**
   * Adjust main and ghosted edge opacity for the current device pixel ratio.
   * @param {number} [devicePixelRatio] - Device pixel ratio; defaults to window.devicePixelRatio.
   */
  _adaptEdgeOpacityToDPR(devicePixelRatio) {
    const retina = (devicePixelRatio ?? this.getWindow().devicePixelRatio) > 1.5;
    if (!this._edgeColorMainOverridden) {
      this._edgeColorMain.w = retina ? EdgeOpacity.Default.high : EdgeOpacity.Default.low;
    }
    if (!this._edgeColorGhostedOverridden) {
      this._edgeColorGhosted.w = retina ? EdgeOpacity.Ghosted.high : EdgeOpacity.Ghosted.low;
    }
  }

  /**
   * Push main and ghosted edge colors into the render context and configure it for the given phase. Ghosted phase
   * enables depth-write, dithering (if configured), and updates the ghost-line material.
   * @param {boolean} isGhosted - true for the ghosted (RENDER_HIDDEN) phase, false to restore normal state.
   */
  prepareEdgesForGhostedPass(isGhosted) {
    this._renderer.prepareEdgesForPhase(isGhosted, this._edgeColorMain, this._edgeColorGhosted);
  }

  _updateCanvasSize(noEvent) {

    if (!this._needsResize) {
      return;
    }

    this.canvasBoundingclientRectDirty = true;
    this.camera.aspect = this._newWidth / this._newHeight;
    this.camera.clientWidth = this._newWidth;
    this.camera.clientHeight = this._newHeight;
    this.camera.updateProjectionMatrix();

    // Additionally update the device pixel ratio here, in case device pixel ratio changes are not
    // properly detected. This happens, for example, when window.devicePixelRatio is set directly.
    const devicePixelRatio = this.getWindow().devicePixelRatio;
    if (this.glrenderer().getPixelRatio() !== devicePixelRatio) {
      this.glrenderer().setPixelRatio(devicePixelRatio);
    }

    this._adaptEdgeOpacityToDPR(devicePixelRatio);

    this._renderer.setSize(this._newWidth, this._newHeight);

    this.controls.handleResize();

    if (this._groundReflection)
    this._groundReflection.setSize(this._newWidth, this._newHeight);
    if (this._sheetRenderer) {
      // Uses the size of the attached renderContext (impl's _renderer, by default)
      this._sheetRenderer.setSize();
    }

    this.invalidate(true, true, true);
    this._needsResize = false;

    if (!noEvent) {
      this.api.dispatchEvent({
        type: et.VIEWER_RESIZE_EVENT,
        width: this._newWidth,
        height: this._newHeight
      });
    }
  }

  renderGroundShadow(target) {

    // If shadow maps are active, we don't use _groundShadow for the ground. Instead, the ground is
    // rendered using the shadow map as well.
    if (this._shadowMaps) {
      if (this._shadowMaps.state == shadow.SHADOWMAP_VALID) {
        this._shadowMaps.renderGroundShadow(this.camera, target || this._renderer.getColorTarget());
      }
    } else {
      this._groundShadow.renderShadow(this.camera, target || this._renderer.getColorTarget());
      this._groundShadow.rendered = true;
    }
  }

  // Set any information needed for the ground plane reflection, drop shadow, or shadow map projection
  _updateGroundTransform() {
    // if we're not using the ground shadow or reflection, or it's a 2D drawing, return
    if (!this._groundShadow.enabled && !this._groundReflection || this.is2d)
    return;

    // Get the box of all the scene's data
    var groundBox;
    if (this.model && !this.model.isLoadDone()) {
      groundBox = this.model.getData().bbox;
    } else
    {
      groundBox = this.getVisibleBounds(true, false, undefined, undefined);
    }
    // If there's nothing to see, return
    if (!groundBox)
    return;

    var camera = this.camera;
    var bbox = groundBox.clone();

    var rightAxis = new THREE.Vector3(1, 0, 0);

    var shadowDir = this._shadowLightDir.clone();

    // Transform bbox, rightAxis, and shadowDir using worldUpTransform. For the resulting box, we
    // can safely assume that y is the up-direction
    if (camera.worldUpTransform) {
      bbox.applyMatrix4(camera.worldUpTransform);
      rightAxis.applyMatrix4(camera.worldUpTransform);
      shadowDir.applyMatrix4(camera.worldUpTransform);
    }

    // expand the box downwards by 0.5%. The effect of this is just that the
    // ground plane does not touch the world box, but is slightly below it
    bbox.min.y -= 0.005 * (bbox.max.y - bbox.min.y);

    if (this._shadowMaps) {
      this._shadowMaps.expandByGroundShadow(bbox, shadowDir);
    }

    // get size and center
    var bsize = bbox.getSize(new THREE.Vector3());
    var bcenter = bbox.getCenter(new THREE.Vector3());

    // apply some adjustments specific for drop-shadow
    if (!this._shadowMaps) {
      // add some horizontal margin so that blurring is not clipped at the boundaries
      bsize.x *= 1.25;
      bsize.z *= 1.25;

      // expand to square, because the texture is squared as well
      bsize.x = bsize.z = Math.max(bsize.x, bsize.z);
    }

    // Rotate center back to world-coords.
    if (camera.worldUpTransform) {
      var worldUpInverse = camera.worldUpTransform.clone().invert();
      bcenter.applyMatrix4(worldUpInverse);

      // Note that we leave size vector as it is. I.e., only the center is transformed back to world-coords.
      // The size vector keeps as it is, i.e. the bbox defined by (center, size) is still aligned with
      // the rotated axes. In other worlds
      //  - size.x is the extent along worldUpTransform * (1,0,0) = rightAxis
      //  - size.y is the extent along worldUpTransform * (0,1,0) = camera.worldUp
      //  - size.z is the extent along worldUpTransform * (0,0,1)
    }

    this._groundShadow.setTransform(
      bcenter,
      bsize,
      camera.worldup,
      rightAxis
    );

    if (this._groundReflection) {
      var groundPos = new THREE.Vector3().subVectors(bcenter, camera.worldup.clone().multiplyScalar(bsize.y / 2));
      this._groundReflection.setTransform(groundPos, camera.worldup, bsize);
    }

    if (this._shadowMaps) {
      this._shadowMaps.setGroundShadowTransform(bcenter, bsize, camera.worldup, rightAxis);
    }
  }

  _updateScene() {
    if (this._sceneDirty) {
      // If the model had changed, the ground-plane, etc., may have changed, so recompute
      this._updateGroundTransform();
      this._groundShadow.setDirty();
      this._sceneDirty = false;
      return true;
    } else {
      return false;
    }
  }

  _updateOverlays() {

    //Update the selection set cloned meshes
    for (var id in this.selectionMeshes) {

      var m = this.selectionMeshes[id];
      if (m.model) {
        var fragList = m.model.getFragmentList();

        // If the proxy uses original geometry of the fragment, update its matrix.
        // If the geometry does not match, it is a consolidated or instanced mesh.
        // For these, the matrix is already baked into vertex buffer or
        // index buffer. We don't support animation for these.
        if (m.geometry === fragList?.getGeometry(m.fragId)) {
          fragList.getWorldMatrix(m.fragId, m.matrix);
        }
      }
    }

  }

  _invalidateShadowMap() {
    if (this._shadowMaps) {
      this._shadowMaps.state = shadow.SHADOWMAP_NEEDS_UPDATE;
    }
  }

  /**
   * Main animation loop -- update camera,
   * advance animations, render if needed.
   *
   * @param {number} highResTimeStamp
   */
  tick(highResTimeStamp) {

    // tick() does three main operations:
    // 1. Determine if anything has changed that would trigger a new render.
    // 2. If a new render of any sort is needed, set the command system to do it.
    // 3. Check if there is a command set to run, and if so, run it.

    // TODO We have a high-res time stamp from performance.now(), but, interestingly enough,
    // it comes in about 100 ticks later than whatever number we get when we call performance.now() right here.
    // TODO how's that work? Why this mis-sync?

    // In the very first render loop, 'highResTimeStamp' is not defined yet,
    // so we catch that case setting it to 0.
    if (!highResTimeStamp) {
      highResTimeStamp = 0;
    }

    // checkVisibility is a quite recently added feature, so we check here, whether it is available
    // to prevent an error in browsers that do not support it. On older browsers, we currently won't
    // trigger the VIEWER_VISIBILITY_CHANGED event.
    if (this.canvas.checkVisibility) {
      let viewerVisible = this.canvas.checkVisibility({
        visibilityProperty: true
      });
      if (viewerVisible != this._viewerVisible) {
        this._viewerVisible = viewerVisible;
        this.api.dispatchEvent({ type: et.VIEWER_VISIBILITY_CHANGED, visible: this._viewerVisible });
      }
    }

    this._rcs.highResTimeStamp = highResTimeStamp;
    this._webglrender.updateTimestamp(highResTimeStamp);

    ///////////////////////////////////////////////
    // Determine if anything has changed that would cause a new render to be performed

    // Texture uploads of newly received textures;
    // Any texture change causes a full redraw.
    var res = this._materials.updateMaterials();

    this.invalidate(res.needsClear, res.needsRender, res.overlayDirty);

    // Perform animations, return true if something animated -- this has to be done
    // before the scene update below
    const animationMoved = this._updateAnimations(highResTimeStamp);

    // update controls (e.g. view cube, home button, orbit, key press) and see if that has affected the view
    const controlsMoved = this.controls.update(highResTimeStamp);

    // see if more data was loaded.
    const sceneChanged = this._renderScene && this._renderScene.update(highResTimeStamp);


    const moved = controlsMoved || animationMoved || this._cameraUpdated || sceneChanged;
    // reset and record state of this frame
    this._cameraUpdated = false;
    // Did the window resize since last tick?
    var canvasSizeUpdated = this._needsResize;
    // checks this._needsResize to see if an update is needed.
    this._updateCanvasSize();

    this._needsClear || (this._needsClear = moved);
    this._overlayDirty || (this._overlayDirty = moved);

    var rollover = false;
    var highlightIntensityChanged = this._renderer.overlayUpdate();

    if (this._overlayDirty) {
      // Update the selection set cloned meshes (does no rendering, yet)
      this._updateOverlays(highResTimeStamp);
    } else {
      // If the overlay is not dirty, fade in the overlay update over time (rollover highlighting becomes stronger).
      // If the value changes, the _blendPass needs to be redone - the overlay itself did not change, so
      // does not need to be rerendered.
      if (highlightIntensityChanged && !this._overlayDirty) {
        // special case where all that is needed is the rollover hightlight blend pass
        this._overlayDirty = rollover = true;
      }
    }

    // In case loading animation is still in progress, force render.
    if (this.isLoadingAnimationEnabled() && this._lastHighResTimeStamp - this.lastTime2dGeometryCreated <= this._webglrender.getLoadingAnimationDuration()) {
      this._needsRender = true;
    }

    var memoryLimitedisActive = this.model && this.model.loader && this.model.loader.pagingProxy && this.model.loader.pagingProxy.getMemoryInfo();
    this._rcs.signalProgressByRendering = this.model && this.model.isLoadDone() && !this.model.isLeaflet() && !memoryLimitedisActive;

    // Has the geometry changed since the last frame?
    // Note this is not the same as just the camera moving, it indicates
    // that meshes have changed position, e.g., during explode, animation, etc.
    // The main effect is that the ground plane and shadow bounds may have changed, so adjust their location and bounds.
    if (this._updateScene()) {
      // if the scene was indeed dirty, we'll need to render from the start
      this._needsClear = true;
    }

    ///////////////////////////////////////////////
    // If a new render of any sort is needed, set the command system to do it.
    //
    // Store parameters that should not change on successive ticks, but rather control function.
    //
    // Add Command related params:
    // CMD_ALWAYS_DO - always do, no matter what. Executed every tick.
    // CMD_DO_AFTER - used in the command loop; if a command times out, any commands immediately after the timeout will be
    //              executed. This then makes progressive rendering possible: we render, timeout, and the next command(s) such as blend and present will be done.
    //              If executed, it will be executed again later when we get the next tick.
    // CMD_NORMAL_SEQUENCE - execute until done, don't come back to it once it's fully executed in the command list.

    // Is there anything at all that triggers a rerender?
    // if this is an immediate silent render, go do it. Else, check if we're still rendering; if not, then a deferred silent render can launch.
    this._immediateSilentRender = this._immediateSilentRender || this._deferredSilentRender && !this._rcs.cmdListActive;
    if (this._needsClear || this._needsRender || this._overlayDirty || this._immediateSilentRender) {

      // For rendering purposes, rcs.drawOverlay is set true whenever any (new) overlay dirty is noticed during progressive rendering.
      // We also need to redraw the overlays if anything triggers a main scene rendering as changed depth values have to be taken into account in the overlays.
      this._rcs.drawOverlay = this._overlayDirty || this._needsClear || this._needsRender;

      // restart rendering?
      if (this._needsClear || this._needsRender || this._immediateSilentRender) {

        // There are three types of render:
        // 1) full render - not (currently) interruptible, runs until completion, "locks" application
        // 2) progressive render - show a bit more each tick, runs unless interrupted by a move, control, etc.
        // 3) silent render - a full render that is done unless interrupted. Display at end if not interrupted by any other render request.
        var movedStatus = this._needsClear ? ResetFlags.RESET_RELOAD : ResetFlags.RESET_NORMAL;
        if (this._needsClear || this._needsRender) {
          if (this.progressiveRender) {
            this._rcs.renderType = RENDERMODE_PROGRESSIVE;
          } else {
            this._rcs.renderType = RENDERMODE_FULL;
          }

          if (this._needsClear) {
            // Looks like silentRender flag should only be reset when a clear happened
            this._deferredSilentRender = this._immediateSilentRender = false;
          }
        } else {
          // Must be a silent render - really, it's the same as a full render, but has a time limit per tick
          this._rcs.renderType = RENDERMODE_SILENT;
          // we must clear, just as on a full render
          this._needsClear = true;
          movedStatus = ResetFlags.RESET_REDRAW;
          this._deferredSilentRender = this._immediateSilentRender = false;
        }

        // Option to skip automatic camera update - can be used when a specific camera near-far values are desired.
        if (!this.skipCameraUpdate) {
          this.updateCameraMatrices();
        }

        //if (ENABLE_DEBUG) { console.log(" COMMAND CREATION: clearing: " + this._needsClear + ", rendering: " + this._needsRender); }

        // set to true when the render is truly done
        this._rcs.finishedFullRender = false;
        if (this._needsClear) {
          this._renderTicksSinceClear = 0;
        }

        this._rcs.beginCommandSet();

        // Set up commands for the entire sequence of possible render states. The most important thing here is to not overthink it.
        // Each command gets executed. If it runs out of time, it returns "true". On the next tick command processing will continue
        // at the same command (it's up to the command itself to keep track of where it left off). The tricky part is if a command
        // needs to be run after renders every tick, "CMD_DO_AFTER", e.g. draw overlays and present when progressive rendering is on.

        // Otherwise, just lay out the worst-case scenario for drawing the scene, "if this didn't finish here, early on, do the rest
        // later". This happens with ground reflections, for example. There's some logic in the commands themselves that check if it's
        // the first tick, for example, or if it's a progressive tick or a full-render tick.

        // Ground shadow is computed separately, if needed, so check if the feature is in use at all.
        // It is if the flag is on, it's not 2D, and we're not loading (if we are loading, the ground shadow will change
        // anyway, so we don't render it then).
        var useGroundShadow = this._groundShadow.enabled && !this.is2d && !this._isLoading;
        var useGroundReflection = !!this._groundReflection && !this.is2d && !this._isLoading;

        // build a list to do the main full pass
        var cameraChanged = moved || canvasSizeUpdated;

        // Smooth Navigation: if it's on, and "moved" is happening, and AO is on, AO is temporarily turned off in the renderer.
        // We also note this status, and use a special CMD_DO_AFTER command to turn AO back on at the end of every command execution
        // (i.e., tick that this command set runs). This avoids headaches with some other system turning off AO in between ticks -
        // it can now safely do so, without the tick() turning it back on when execution is completed or aborted.
        var suppressAO = moved && this.skipAOWhenMoving && this._renderer.getAOEnabled();

        // -----------------------------------------------------------------------------
        // Start creation of a set of commands to execute over this and following ticks.

        // Highlighting from the model browser needed?
        this._rcs.addCommand(this._rcs.cmdBeginScene);
        this._rcs.setParam("BeginScene.signalCameraChanged", cameraChanged);
        this._rcs.setParam("BeginScene.clear", this._needsClear);

        // for Smooth Navigation - turned on later by cmdRestoreAO as an CMD_ALWAYS_DO.
        // We let the clear above clear the SAO buffer, since if we're using smooth navigation
        // we know the SAO there will be invalid. This avoids the case where we're in a long
        // smooth-navigation render which gets interrupted by a "needs present" render (a rollover
        // highlight) which stops the full render we signalled for from completing.
        if (suppressAO) {
          this._rcs.addCommand(this._rcs.cmdSuppressAO, CMD_ALWAYS_DO);
        }

        // is there any geometry to render?
        if (this._renderScene) {

          // is shadow map needed? Generate only if not progressive.
          if (this._shadowMaps && this._shadowMaps.state !== shadow.SHADOWMAP_VALID) {
            this._rcs.addCommand(this._rcs.cmdUpdateShadowMap);
          }

          // is ground shadow computed at this point? If not, and this is a full
          // render, or this is a progressive render and it looks likely to finish,
          // draw it.
          if (useGroundShadow) {
            this._rcs.addCommand(this._rcs.cmdGenerateGroundShadow);
            this._rcs.setParam("GenerateGroundShadow.afterBeauty", false);
            this._rcs.setParam("GenerateGroundShadow.signalRedraw", false);
          }

          // if doing ground reflection, generate it now
          if (useGroundReflection) {
            // tell reflection system it needs to start from scratch once the commands start
            this._groundReflection.setDirty();

            this._rcs.addCommand(this._rcs.cmdGenerateGroundReflection);
            this._rcs.setParam("GenerateGroundReflection.afterBeauty", false);
          }
          // Blit ground shadow first, if in use and ground reflection not in use.
          // If ground reflection is in use, the shadow is composited with its target instead.
          // If we are truly not clearing, then don't blit ground shadow, as it was already
          // displayed in the previous frame (possibly incorrect for this frame, but the user
          // asked to have no clear, so...). See LMV-2571
          else if (useGroundShadow && this._needsClear) {
            this._rcs.addCommand(this._rcs.cmdBlitGroundShadow);
          }

          // Why not for WebGPU?
          //   The highlighting drawMode is only needed for SelectionType.REGULAR highlighting (just coloring shapes)
          //   In WebGPU, rendering the same RenderBatch twice per frame in different configurations would conflict with RenderBundle optimizations.
          //   => In WebGPU, we only do the RENDER_NORMAL and render all at once - and coloring-highlighting is just applied in the shader.
          if (!(USE_WEBGPU || USE_MULTI_MATERIAL_RENDER_CALLS) && this._renderScene.hasHighlighted()) {
            // set phase and reset
            this._rcs.addCommand(this._rcs.cmdBeginPhase);
            this._rcs.setParam("BeginPhase.phase", RenderFlags.RENDER_HIGHLIGHTED);
            this._rcs.setParam("BeginPhase.moved", movedStatus);
            // need to gather frags for the iterator, etc. only once
            movedStatus = false;

            // draw the highlighting
            this._rcs.addCommand(this._rcs.cmdMainRender);

          }

          // beauty pass
          this._rcs.addCommand(this._rcs.cmdBeginPhase);
          this._rcs.setParam("BeginPhase.phase", RenderFlags.RENDER_NORMAL);
          this._rcs.setParam("BeginPhase.moved", movedStatus);
          // need to gather frags for the iterator, etc. only once
          movedStatus = false;
          this._rcs.addCommand(this._rcs.cmdMainRender);

          // ghosting is done after the ground reflection is generated and merged, as it
          // draws transparent atop all.
          if (!this._renderScene.areAllVisible() && this.showGhosting) {

            // if we are progressive rendering, and are generating ground reflections, we do ghosting
            // after the ground reflection is done. Else, do it now, as part of the full render, since
            // we know everything's done.
            // TODO I can imagine changing this logic - seems like we should have just one "ghosting
            // after everything" bit of code insertion. The reason there is a split is that for full
            // rendering we know the ground reflection is done at this point and can simply render atop,
            // directly. For progressive rendering we need to wait for the reflection to finish, blend it
            // in under, then ghost.
            if (!useGroundReflection || this._rcs.renderType !== RENDERMODE_PROGRESSIVE) {
              // show ghosting - highly transparent, so must be rendered last, atop everything else

              //[TS] Ignore the below TODO -- I enabled rendering of sceneAfter and we will deal with the repercussions if they happen.
              // TODO note that we don't do cmdSceneAfterRender here, though it might be nice to
              // show sectioning. I don't really understand, but if we do add it here, the ghosted objects
              // are drawn normally. I guess these objects need to be drawn again for sectioning?
              this._rcs.addCommand(this._rcs.cmdBeginPhase);
              this._rcs.setParam("BeginPhase.phase", RenderFlags.RENDER_HIDDEN);
              this._rcs.setParam("BeginPhase.moved", movedStatus);
              this._rcs.addCommand(this._rcs.cmdMainRender);
            }
          }

          // Render sectioning, if any, and any additional buffers needed, such as ID.
          // TODO for progressive rendering, it seems like we should do this *after* any Present(), if
          // the buffers are not needed immediately. This command also notes rendering is done.
          this._rcs.addCommand(this._rcs.cmdSceneAfterRender);

          if (this._rcs.signalProgressByRendering) {
            this._rcs.addCommand(this._rcs.cmdSignalProcessingDone);
          }
        }

        // Overlay is always rendered. In this way if we *do* get an overlay dirty while progressive rendering,
        // the overlay will get updated.
        // This must be done after the passes above, because our global rule is "draw if z-depth matches"
        // and the z-depths must be established before the highlighted objects get drawn.
        // render them. Always do this for progressive rendering, even if we stop early, since these are important.
        this._rcs.addCommand(this._rcs.cmdRenderOverlays, this._rcs.renderType === RENDERMODE_PROGRESSIVE ? CMD_DO_AFTER : CMD_NORMAL_SEQUENCE);

        // We always need a present, since we know we're doing something. Also antialiasing and whatever blending is needed.
        // Always do this for progressive rendering.
        this._rcs.addCommand(this._rcs.cmdPostAndPresent, this._rcs.renderType === RENDERMODE_PROGRESSIVE ? CMD_DO_AFTER : CMD_NORMAL_SEQUENCE);
        this._rcs.setParam("PostAndPresent.performAO", this._renderer.getAOEnabled() && !suppressAO);

        // If this is a progressive render, make the last thing to happen the ground shadow, which if not done by now will trigger
        // a rerender once it is fully created.
        if (this._rcs.renderType === RENDERMODE_PROGRESSIVE && this._renderScene) {

          if (this._shadowMaps && this._shadowMaps.state !== shadow.SHADOWMAP_VALID) {
            // start shadow map generation from beginning
            this._rcs.addCommand(this._rcs.cmdResetShadowMap);
            this._rcs.addCommand(this._rcs.cmdUpdateShadowMap);
          }

          // Ground shadows are an entirely separate render, happening concurrently with the main renderer, and
          // done after the progressive render is performed, if not completed by then. The full render does it
          // as part of its rerender.

          // If we are done with progressive and the ground shadow is not done, do them now.
          if (useGroundShadow) {
            this._rcs.addCommand(this._rcs.cmdGenerateGroundShadow);
            this._rcs.setParam("GenerateGroundShadow.afterBeauty", true);
            // don't signal a redraw if the ground reflection is about to be finished and merged, too.
            this._rcs.setParam("GenerateGroundShadow.signalRedraw", !useGroundReflection);
            // TODO really need to fix progress meter, but at least we should show 100% done
            if (this._rcs.signalProgressByRendering) {
              this._rcs.addCommand(this._rcs.cmdSignalProcessingDone);
            }
          }

          // if the ground shadows and reflection are not done, do them now.
          if (useGroundReflection) {
            this._rcs.groundShadowInPost = false;

            // Note that ground shadow is guaranteed to be done at this point, so will be merged in correctly.
            this._rcs.addCommand(this._rcs.cmdGenerateGroundReflection);
            this._rcs.setParam("GenerateGroundReflection.afterBeauty", true);

            // ghosting is done after the ground reflection is generated and merged, as it
            // draws transparent atop all. Note that sectioning is already done.
            if (!this._renderScene.areAllVisible() && this.showGhosting) {
              // show ghosting - highly transparent, so must be rendered last, atop everything else
              // TODO note that we don't do cmdSceneAfterRender here, though it might be nice to
              // show sectioning. I don't really understand, but if we do add it here, the ghosted objects
              // are drawn normally. I guess these objects need to be drawn again for sectioning?
              this._rcs.addCommand(this._rcs.cmdBeginPhase);
              this._rcs.setParam("BeginPhase.phase", RenderFlags.RENDER_HIDDEN);
              this._rcs.setParam("BeginPhase.moved", movedStatus);
              this._rcs.addCommand(this._rcs.cmdMainRender);
            }

            // if it's done, perform a present
            this._rcs.addCommand(this._rcs.cmdFinishAllRendering);
            this._rcs.addCommand(this._rcs.cmdPostAndPresent);
            this._rcs.setParam("PostAndPresent.performAO", this._renderer.getAOEnabled() && !suppressAO);
            if (this._rcs.signalProgressByRendering) {
              this._rcs.addCommand(this._rcs.cmdSignalProcessingDone);
            }
          }
        }

        // Smooth Navigation - if on, then we need to always turn the renderer back to AO at the end of any tick;
        // it will get turned back off the next tick by the renderer.
        if (suppressAO) {
          this._rcs.addCommand(this._rcs.cmdRestoreAO, CMD_ALWAYS_DO);
          // If we get to this command, we've done all we can during smooth navigation and should now signal for a full redraw
          // without smooth navigation. This works because "moved" should be false on the next tick (unless of course the
          // user moved the view) and so a full or progressive render will occurs with smooth navigation off.
          this._rcs.addCommand(this._rcs.cmdSignalRedraw);
        }

        this._rcs.addCommand(this._rcs.cmdFinishedFullRender);

        this._rcs.endCommandSet();

        // if we reenter, by turning these off, we then will not rebuild the command list
        this._needsClear = false;
        this._needsRender = false;
        // Avoid having updateOverlays() called every tick during a progressive rendering by turning off the overlay dirty flag.
        // If we get a later overlayDirty, this will trigger updateOverlays() at the start of tick(), and will als cause the
        // cmdRenderOverlays to trigger during a progressive render.
        this.clearOverlayDirtyFlag();
      }
      ////////////////////////////////////////////////////////////////////////////

      // only case left is that overlay is dirty
      else {

        // Possibly draw the overlay, only.
        // Check if we've finished a render. If we are, we set up a short render to update the overlay.
        // We ignore overlay dirty if we're in the middle of a (more than one tick) render, since the render itself will update the overlay.
        if (this._rcs.finishedFullRender) {

          this._rcs.beginCommandSet();

          if (rollover) {
            // Do just the blend pass, having already adjusted the uniform for fading in.
            this._rcs.addCommand(this._rcs.cmdForcePresent);

          } else {
            // full overlay render and display

            // just the overlay needs to be re-rendered
            this._rcs.addCommand(this._rcs.cmdRenderOverlays, true);

          }

          // we always need a present, since we know we're doing something.
          this._rcs.addCommand(this._rcs.cmdPostAndPresent, true);
          // don't need to think about AO, since we are just fading in.
          this._rcs.setParam("PostAndPresent.performAO", this._renderer.getAOEnabled());

          if (this._rcs.signalProgressByRendering) {
            this._rcs.addCommand(this._rcs.cmdSignalProcessingDone);
          }

          this._rcs.endCommandSet();

          // Avoid having updateOverlays() called every tick during a progressive rendering by turning off the overlay dirty flag.
          // If we get a later overlayDirty, this will trigger updateOverlays() at the start of tick(), and will als cause the
          // cmdRenderOverlays to trigger during a progressive render.
          // Note that if we get an overlayDirty and rendering is occurring, _overlayDirty won't get cleared, which is good:
          // we want the command system to detect this and turn on overlay rendering at that point.
          this.clearOverlayDirtyFlag();
        }
      }
    }

    ///////////////////////////////////////////////
    // Run the command list, if any. Note whether there's any work to do, so we can see if this state has changed and send an event.
    this._workThisTick = this._rcs.cmdListActive;

    // Optional callback that's being called just before executing the render command list.
    // At this step, we already know if there is anything to render at all.
    if (this.onBeforeRender) {
      this.onBeforeRender(this._workThisTick);
    }

    if (this._workThisTick) {
      this._updateLastRenderTime();
    }

    /** @todo: review this in case overlays and other do-after commands can interrupt */
    const mainSceneOngoing = this._rcs.executeCommandList(); // returns true when command execution was interrupted

    if (this._workThisTick) {
      this._updateFPS(highResTimeStamp);
    }

    ///////////////////////////////////////////////
    // Keep it simple: this tick either did rendering, or it did not. If this differs from last frame's state, signal.
    if (this._workThisTick !== this._workPreviousTick) {
      // We skip this for WebGPU, because tracking drawcalls is currently not supported by WebGPU renderer
      if (!this._workThisTick && !this._useWebGPU) {
        this.glrenderer().notifyFinalFrameRendered();
      }
      /** @todo: deprecate this event and to "keep it simple"r review this block and merge with the one below */
      this.api.dispatchEvent({ type: et.FINAL_FRAME_RENDERED_CHANGED_EVENT, value: { finalFrame: !this._workThisTick } });
      // we're at the end of things, so the current state now becomes the "previous tick" state for testing next time.
      this._workPreviousTick = this._workThisTick;
    }

    if (this._workThisTick) {
      const isMainSceneDone = !mainSceneOngoing;
      const renderTicksSinceClear = this._renderTicksSinceClear;
      ++this._renderTicksSinceClear;
      this.api.dispatchEvent({ type: et.RENDERING_TICKED_EVENT, value: { renderTicksSinceClear, isMainSceneDone } });
    }

    // used to determine FPS
    this._lastHighResTimeStamp = this._rcs.highResTimeStamp;

    // WebGPU has cleanup actions that should occur every frame, even if interrupted or not rendered at all.
    // E.g., if we don't re-render for 10 minutes, we don't want to restrict all upload activities to a single frame budget.
    if (this._useWebGPU) {
      this._webglrender.afterTick();
      if (this._sheetRenderer) {
        this._sheetRenderer.glRenderer.afterTick();
      }
    }
  }

  _updateLastRenderTime() {
    this._lastRenderTime = performance.now();
  }

  /**
   * Main animation loop function that is called by the main loop.
   * @param {boolean} moreWorkToDoInExecuteTasks - Whether we need to do more work in the executeTasks function.
   * @returns {boolean} - Whether the viewer has ticked.
   */
  _animLoop(moreWorkToDoInExecuteTasks) {
    // Check whether we are ready to do another tick.
    // We check for the following conditions:
    // 1. In the first frame, only render a new frame once the fence for the previous frame
    //    has been resolved to avoid GPU stalls. We don't do this for later frames of the
    //    progressive rendering, because we are only interested in fast rendering for those and
    //    no longer care about latency / stuttering.
    // 2. If task processing is still in progress, we wait at least for 0.9 * TARGET_FRAME_TIME
    //    before starting a new frame, to leave enough time for tasks.
    if ((this._rcs.tickCount > 1 || !this.fenceManager.hasActiveFences()) && (
    !moreWorkToDoInExecuteTasks ||
    performance.now() > this._lastRenderTime + 0.9 * this._rcs.TARGET_FRAME_TIME)) {
      this.tick(performance.now());
      return true;
    } else {
      return false;
    }
  }

  run() {
    if (this._renderLoopOn) {
      return;
    }
    this._renderLoopOn = true;
    mainLoop.registerViewer(this);
  }

  stop() {
    this._renderLoopOn = false;
    mainLoop.unregisterViewer(this);
  }

  /**
   * Toggle progressive rendering on/off
   * @param {boolean} value - Whether to enable progressive rendering
   */
  toggleProgressive(value) {
    this.progressiveRender = value;
    if (USE_WEBGPU && this._webglrender) {
      this._webglrender.setProgressiveRendering(value);
    }
    this._needsClear = true;
  }

  /**
   * Sets the progressive frame at which rendering halts and reports completion, even if not all batches have been
   * drawn. This is useful for inspecting intermediate rendering quality at a specific progressive frame.
   *
   * @param {number} frame - The frame number at which to halt. 0 disables halting (default behavior).
   */
  setProgressiveHaltFrame(frame) {
    this._rcs.progressiveHaltFrame = frame;
    this._needsClear = true;
  }

  // Apply current clear colors to renderer while considering swapBlackAndWhite flag when in 2D
  updateClearColors() {
    let clearColorTop = this.clearColorTop;
    let clearColorBottom = this.clearColorBottom;

    // apply black/white swap to clear color if wanted
    if (this.is2d && this.swapBlackAndWhite) {
      const isWhite = (c) => c.x === 1 && c.y === 1 && c.z === 1;
      const isBlack = (c) => c.x === 0 && c.y === 0 && c.z === 0;
      const swapIfNeeded = (c) => isWhite(c) ? new THREE.Vector3(0, 0, 0) : isBlack(c) ? new THREE.Vector3(1, 1, 1) : c;

      clearColorTop = swapIfNeeded(clearColorTop);
      clearColorBottom = clearColorBottom && swapIfNeeded(clearColorBottom);
    }

    this._renderer.setClearColors(clearColorTop, clearColorBottom);
  }

  /**
   * @param {boolean} value - Whether to enable black and white swapping
   */
  toggleSwapBlackAndWhite(value) {
    this.swapBlackAndWhite = value;
    // WebGL uses a per-material uniform (see MaterialManager.updateSwapBlackAndWhite, not called here).
    // WebGPU uses a more global uniform set via the render context.
    this._useWebGPU && this._renderer.setSwapBlackAndWhite(this.swapBlackAndWhite);
    this.updateClearColors();
    this._needsClear = true;
  }

  toggleGrayscale(value) {
    this._materials.setGrayscale(value);
    this._needsClear = true;
  }

  // Screen-space 2D line width (WebGPU only): cap width at the fit-to-view ppu so lines stop
  // fattening past fit but still thin when zoomed out. Reference ppu computed in _updateScreenSpaceLineWidth.
  toggleScreenSpaceLineWidth(value) {
    this._screenSpaceLineWidth = !!value;
    this.invalidate(true, true, false);
  }

  // Push the fit-to-view ppu (deviceHeight / fit distance) to the renderer only when enabled-state
  // or fit ppu changes. Fit ppu tracks bounds + viewport, not zoom/pan — that is what stays constant.
  _updateScreenSpaceLineWidth() {
    if (!this._useWebGPU) return;

    // Off and already off: nothing to push, skip the per-frame fit-distance compute.
    if (!this._screenSpaceLineWidth && !this._lastSSLWEnabled) return;

    // PDF-only feature; treat any non-PDF model as off even when the pref is on.
    const enabled = this._screenSpaceLineWidth && !!this.model?.isPdf();
    let fitPpu = 0;
    if (enabled && this.model) {
      const bounds = this.model.getBoundingBox();
      const dist = this.api.navigation.computeOverviewDistance(bounds);
      const deviceHeight = this._renderer.settings.deviceHeight;
      fitPpu = dist > 0 ? deviceHeight / dist : 0;
    }

    if (enabled !== this._lastSSLWEnabled || fitPpu !== this._lastSSLWPixelsPerUnit) {
      this._renderer.setFrozenLineWidthPpu(enabled, fitPpu);
      this._lastSSLWEnabled = enabled;
      this._lastSSLWPixelsPerUnit = fitPpu;
    }
  }

  /**
   * Toggle ghosting on/off
   * @param {boolean} value - Whether to enable ghosting
   */
  toggleGhosting(value) {
    this.showGhosting = value;
    this._needsClear = true;
  }

  /**
   * Toggle showing overlays while moving on/off
   * @param {boolean} value - Whether to show overlays while moving
   */
  toggleOverlaysWhileMoving(value) {
    this.showOverlaysWhileMoving = value;
  }

  togglePostProcess(useSAO, useFXAA) {
    this._renderer.initPostPipeline(useSAO, useFXAA);
    this.fireRenderOptionChanged();
    this._needsClear = true;
  }

  toggleCmdMapping(value, controller) {
    controller.keyMapCmd = value;
  }

  toggleGroundShadow(value) {
    if (this._groundShadow.enabled === value)
    return;

    this._groundShadow.enabled = value;
    this._groundShadow.clear();
    if (value) {
      this._groundShadow.setDirty();
    }
    // if we're turning on the ground shadow, we need to set up the ground plane
    this._updateGroundTransform();
    this.fireRenderOptionChanged();
    this.invalidate(true, false, false);
  }


  /**
   * Check if the ground shadows are enabled
   * @private
   */
  isGroundShadowEnabled() {
    return this._groundShadow && this._groundShadow.enabled;
  }

  groundShadow() {
    return this._groundShadow;
  }

  setGroundShadowColor(color) {
    if (!this._groundShadow.enabled) return;

    this._groundShadow.setColor(color);
    this.invalidate(true, false, false);
  }

  setGroundShadowAlpha(alpha) {
    if (!this._groundShadow.enabled) return;

    this._groundShadow.setAlpha(alpha);
    this.invalidate(true, false, false);
  }

  setRightBtnSelection(enable) {
    this._isRightBtnSelectionEnabled = enable;
  }

  isRightBtnSelectionEnabled() {
    return this._isRightBtnSelectionEnabled;
  }

  groundReflection() {
    return this._groundReflection;
  }

  toggleGroundReflection(enable) {
    if (USE_WEBGPU && enable) {
      console.log('Ground reflection is not supported in WebGPU.');
      return;
    }

    if (enable && !!this._groundReflection ||
    !enable && !this._groundReflection)
    return;

    if (enable) {
      this._groundReflection = new GroundReflection(this._webglrender, this.canvas.clientWidth, this.canvas.clientHeight, { clearPass: this._renderer.getClearPass() });
      this._groundReflection.setClearColors(this.clearColorTop, this.clearColorBottom, isMobileDevice());
      this._groundReflection.toggleEnvMapBackground(this._envMapBackground);
      this._groundReflection.setEnvRotation(this._renderer.getEnvRotation());
      // if we're turning on the ground reflection, we need to set up the ground plane
      this._updateGroundTransform();
    } else
    {
      this._groundReflection.cleanup();
      this._groundReflection = undefined;
    }

    this.fireRenderOptionChanged();
    this.invalidate(true, false, false);
  }

  setGroundReflectionColor(color) {
    if (!this._groundReflection) return;

    this._groundReflection.setColor(color);
    this.invalidate(true, false, false);
  }

  setGroundReflectionAlpha(alpha) {
    if (!this._groundReflection) return;

    this._groundReflection.setAlpha(alpha);
    this.invalidate(true, false, false);
  }

  toggleEnvMapBackground(value) {
    this._envMapBackground = value;
    this._renderer.toggleEnvMapBackground(value);

    if (this._groundReflection) {
      this._groundReflection.toggleEnvMapBackground(value);
    }
    this.invalidate(true, true, false);
  }

  isEnvMapBackground() {
    return this._envMapBackground;
  }

  /**
   * Disable Ambient Occlusion when moving
   * @param {boolean} value - Whether to disable Ambient Occlusion when moving
   */
  setOptimizeNavigation(value) {
    this.skipAOWhenMoving = value;
  }

  // If we have selection meshes, this function makes sure that they use exactly the same
  // geometry as we used in the main scene rendering. This is needed to avoid z-buffer artifacts
  // when using consolidation.
  _updateSelectionProxies() {
    for (var id in this.selectionMeshes) {
      var proxy = this.selectionMeshes[id];

      // Updating proxies is only relevant when using consolidtion. Otherwise, we always use the original
      // fragment geometry and can keep static proxy geometry.
      if (proxy.model && proxy.model.isConsolidated()) {
        proxy.model.updateRenderProxy(proxy, proxy.fragId);

        // if its geometry is not valid anymore, remove the fragment from the selection
        if (!proxy.geometry)
        this.highlightFragment(proxy.model, proxy.fragId, false);
      }
    }
  }

  getOverlayScenesToRender() {
    const scenes = {};
    for (const [name, scene] of Object.entries(this.overlayScenes)) {
      if (!scene.hiddenFromMainRender) {
        scenes[name] = scene;
      }
    }
    return scenes;
  }

  /**
   * Gets all overlay data for rendering (both legacy overlayScenes and new RenderLayers).
   * @returns {{overlayScenes: Object, renderLayers: Array}} Combined overlay data
   */
  getOverlayDataForRendering() {
    return {
      overlayScenes: this.getOverlayScenesToRender(),
      renderLayers: this._renderLayerManager.getOverlayRenderLayers()
    };
  }

  renderOverlays() {

    this._updateSelectionProxies();

    //The overlays (selection, pivot, etc) get lighted using
    //the default lights, even if IBL is on
    var lightsOn = this.lightsOn;
    if (!lightsOn) {
      this.toggleLights(true, true);
    }

    var oldIntensity;
    if (this.dir_light1) {
      oldIntensity = this.dir_light1.intensity;
      this.dir_light1.intensity = 1;
    }

    const overlayData = this.getOverlayDataForRendering();
    this._renderer.renderOverlays(overlayData, this.lightsOn ? this.lights : this.no_lights);

    if (!lightsOn) {
      this.toggleLights(false, true);
    }

    if (this.dir_light1)
    this.dir_light1.intensity = oldIntensity;
  }

  setLayerVisible(layerIndexes, visible) {
    this.layers.setLayerVisible(layerIndexes, visible);
  }

  isLayerVisible(layerIndex) {
    return this.layers.isLayerVisible(layerIndex);
  }

  getVisibleLayerIndices() {
    return this.layers.getVisibleLayerIndices();
  }

  setNearRadius(radius, redraw) {
    if (this.nearRadius !== radius) {
      this.nearRadius = radius;
      if (redraw) {
        this.invalidate(true);
      }
    }
  }

  getNearRadius() {
    return this.nearRadius;
  }

  // Find model's bounds, including ground plane, if needed.
  // Fit near and far planes to the model.
  updateNearFarValues(camera, worldBox) {

    /**
     * Calculating near-far values based on empty worldBox (infinity) will result in incorrect values.
     * Better to keep previous values instead. @todo: Handle empty-case with robust camera configuration.
     */
    if (worldBox.isEmpty()) return;

    //NOTE: This is not computing the same matrix as what we use for rendering,
    //in cases where we are in ORTHO mode and the camera is inside the model,
    //which would result in negative near plane. For the purposes of computing
    //the near/far planes, we have to skip the logic that adjusts the view matrix
    //based on the near/far planes. See UnifiedCamera.updateMatrix for the related
    //adjustment to the view matrix.
    _tmpCameraMatrix.compose(camera.position, camera.quaternion, camera.scale);
    _tmpViewMatrix.copy(_tmpCameraMatrix).invert();

    _tmpBox.copy(worldBox);

    //If reflection is on, then we need to double the worldBox size in the Y
    //direction, the reflection direction, otherwise the reflected view can be
    //clipped.
    if (this._groundReflection) {
      // Increase bounding box to include ground reflection geometry. The idea
      // here is to extend the bounding box in the direction of reflection, based
      // on the "up" vector.
      var tmpVecReflect = new THREE.Vector3();
      tmpVecReflect.multiplyVectors(_tmpBox.max, camera.worldup);
      var tmpVecMin = new THREE.Vector3();
      tmpVecMin.multiplyVectors(_tmpBox.min, camera.worldup);
      tmpVecReflect.sub(tmpVecMin);
      // tmpVecReflect holds how much to increase the bounding box.
      // Negative values means the "up" vector is upside down along that axis,
      // so we increase the maximum bounds of the bounding box in this case.
      if (tmpVecReflect.x >= 0.0) {
        _tmpBox.min.x -= tmpVecReflect.x;
      } else {
        _tmpBox.max.x -= tmpVecReflect.x;
      }
      if (tmpVecReflect.y >= 0.0) {
        _tmpBox.min.y -= tmpVecReflect.y;
      } else {
        _tmpBox.max.y -= tmpVecReflect.y;
      }
      if (tmpVecReflect.z >= 0.0) {
        _tmpBox.min.z -= tmpVecReflect.z;
      } else {
        _tmpBox.max.z -= tmpVecReflect.z;
      }
    }

    // Expand the bbox based on ground shadow. Note that the horizontal extent of the ground shadow
    // may be significantly larger for flat shadow light directions.
    if (this._shadowMaps && this._shadowMaps.groundShapeBox) {
      _tmpBox.union(this._shadowMaps.groundShapeBox);
    }

    //Transform the world bounds to camera space
    //to estimate the near/far planes we need for this frame
    _tmpBox.applyMatrix4(_tmpViewMatrix);

    //Expand the range by a small amount to avoid clipping when
    //the object is perfectly aligned with the axes and has faces at its boundaries.
    var sz = 1e-5 * (_tmpBox.max.z - _tmpBox.min.z);

    //TODO: expand for ground shadow. This just matches what the
    //ground shadow needs, but we need a better way to take into account
    //the ground shadow scene's bounds
    var expand = (_tmpBox.max.y - _tmpBox.min.y) * 0.5;

    var dMin = -(_tmpBox.max.z + sz) - expand;
    var dMax = -(_tmpBox.min.z - sz) + expand;

    //Camera is inside the model?
    if (camera.isPerspective) {
      // If dMax / dMin is too large then we get z buffer fighting, which we would
      // like to avoid. In this case there are two alterniatives that we can do -
      // we can move the near plane away from the camera, or we can move the far
      // place toward the camera. If this.nearRadius is > 0, then we want to move
      // the far plane toward the camera.
      if (this.nearRadius > 0) {
        // dMin might be OK, or might be negative or close to 0. If it is so small
        // that depth precision will be bad, then we need to move it away from 0
        // but no further than this.nearRadius.
        dMin = Math.max(dMin, Math.min(this.nearRadius, Math.abs(dMax - dMin) * 1e-4));

        // If the max is still too far away, then move it closer
        dMax = Math.min(dMax, dMin * 1e4);
      } else {
        // dMin might be OK, or might be negative. If it's negative,
        // give it a value of 1/10,000 of the entire scene's size relative to this view direction,
        // or 1, whichever is *smaller*. It's just a heuristic.

        dMin = Math.max(dMin, Math.min(1, Math.abs(dMax - dMin) * 1e-4));

        // If a target is set, we try to not set it behind it to avoid clipping.
        if (camera.target) {
          const targetDist = camera.target.clone().sub(camera.position).length();
          dMin = Math.min(dMin, targetDist);
        }

        if (dMax < 0) {
          // near and far planes should always be positive numbers for perspective
          dMax = 1e-4;
        }
        // One more attempt to improve the near plane: make it 1/100,000 of the distance of the
        // far plane, if that's higher.
        // See https://autodesk.atlassian.net/wiki/spaces/LMVCORE/pages/419437698/Z-Buffer+Fighting for reasoning.
        // 1e-4 is generally good below, but inside Silver Cross we get a lot of near clipping. So, 1e-5.
        dMin = Math.max(dMin, dMax * 1e-5);
      }

      // Correct near plane so it won't be farther than the actual distance to the world bounds.
      const boxDist = Math.sqrt(SceneMath.pointToBoxDistance2(camera.position, worldBox));
      // In case the that the camera is inside the bounds, boxDist will be 0. In that case let's set the minimum distance to 1.
      const minDistToBox = Math.max(1, boxDist);
      dMin = Math.min(dMin, minDistToBox);
    } else {






      //TODO:
      //Do nothing in case of ortho. While this "fixes" near plane clipping too aggressively,
      //it effectively disallows moving through walls to go inside the object.
      //So we may need some heuristic based on how big we want the object to be
      //on screen before we let it clip out.
      //dMin = Math.max(dMin, 0);
    } //The whole thing is behind us -- nothing will display anyway?
    dMax = Math.max(dMax, dMin);camera.near = dMin;camera.far = dMax;camera.updateCameraMatrices();
  }

  getPixelsPerUnit(camera, worldBox, model = this.model) {
    var deviceHeight = this._renderer.settings.deviceHeight; // = canvas height * pixelRatio

    //If there is a cutting plane, get a point on that plane
    //for by the pixel scale computation. (only used for 3D)
    var cutPlanes = this._materials.getCutPlanesRaw();
    var cutPlane = cutPlanes[0];

    var modelBox = model ? model.getBoundingBox() : worldBox;

    return SceneMath.getPixelsPerUnit(camera, this.is2d, worldBox, deviceHeight, cutPlane, modelBox);
  }

  updateCameraMatrices() {
    const camera = this.camera;

    //TODO: Would be nice if this got called by the world up tool instead,
    //so that we don't have to update it every frame.
    if (camera.worldup)
    this.setWorldUp(camera.worldup);

    // Update near & far values according to the unified bounds of all the models in modelQueue.
    // There is a special case for underlay raster, where `this.model` is not set, but there is a temp model inside modelQueue - this
    // is why we update these values even if `this.model` is not set.
    const worldBox = this.getVisibleBounds(true, true);
    this.updateNearFarValues(camera, worldBox);

    // Optional: If a maxDistance is set, allow excluding some boxes if near/far distance gets too large.
    this.maxModelDistance && this._applyMaxModelDistanceToNearFar();

    // Update the line width scale with the new pixels per unit scale.
    const pixelsPerUnit = this.getPixelsPerUnit(camera, worldBox);
    const width = this._renderer.settings.deviceWidth;
    const height = this._renderer.settings.deviceHeight;
    if (this.is2d) {
      //If we want to take into account devicePixelRatio for line weights (so that lines are not too thin)
      //we can do this here, but it's less esthetically pleasing:
      //pixelsPerUnit /= _webglrenderer.getPixelRatio();

      this._materials.updatePixelScale(pixelsPerUnit, width, height, camera);

      // AutoCAD drawings are commonly displayed with white lines on a black background. Setting reverse swaps (just)
      // these two colors.
      this._materials.updateSwapBlackAndWhite(this.swapBlackAndWhite);

      this._updateScreenSpaceLineWidth();
    } else {// is3d

      this._materials.updatePixelScale(pixelsPerUnit, width, height, camera);
    }

    this._useWebGPU && this._webglrender.updatePixelScale(pixelsPerUnit, camera);

    // Set pixelsPerUnit according to each sheet in 3D space. In general, it is set according to the modelQueue's bounds
    // which is not related to how we want to present the sheet (i.e. the line thickness will vary when selecting a
    // floor as a result, because of the changing viewing volume)
    // Note: Previously this was done only when in 3D mode, but it's also needed in 2D in case a transform with
    // scaling is set.
    const models = this.get2DModels();
    models.forEach((model) => {
      const transform = model.getModelToViewerTransform();
      const scaling = transform ? transform.getMaxScaleOnAxis() : 1;
      if (!this.is2d || scaling !== 1) {
        const bounds = model.getVisibleBounds();
        // Sending is2d:true here because we want the calculation path done for 2D sheets
        const pixelsPerUnit = SceneMath.getPixelsPerUnit(camera, true, bounds, height, null, bounds);

        this._materials.updatePixelScaleForModel(model, pixelsPerUnit, width, height, scaling, camera);
      }
    });
  }

  /**
   * @return {void}
   */
  initLights() {
    if (this._lightsInitialized) {
      return;
    }

    this.dir_light1 = new THREE.DirectionalLight(this._defaultDirLightColor, this._defaultLightIntensity);
    this.dir_light1.position.copy(this._lightDirDefault);

    //Note this color will be overridden by various light presets
    this.amb_light = new THREE.AmbientLight(this._defaultAmbientColor);

    // Set this list only once, so that we're not constantly creating and deleting arrays each frame.
    // See https://www.scirra.com/blog/76/how-to-write-low-garbage-real-time-javascript for why.
    // use this.no_lights empty array if no lights are needed.
    this.lights = [this.dir_light1, this.amb_light];

    //We do not add the lights to any scene, because we need to use them
    //in multiple scenes during progressive render.
    //this.emptyScene.add(this.amb_light);

    // Attach the light to the camera, so that the light direction is applied in view-space.
    // Note:
    //
    //  1. For directional lights, the direction where the light comes from is determined by
    //     lightPosition - targetPosition, both in in world-space.
    //  2. The default target of dir lights is the world origin.
    //  3. Transforming the light object only affects the light position, but has no effect on the target.
    //
    // The goal is to rotate the lightDir with the camera, but keep it independent
    // of the camera position. Due to 3. above, we must also attach the light's target object to the camera.
    // Otherwise, the camera position would incorrectly be added to the light direction.

    this.camera.add(this.dir_light1);
    this.camera.add(this.dir_light1.target);

    this._lightsInitialized = true;
  }

  _setLights(amb_light, dir_light1, state, isForOverlay) {
    //Update the light colors based on the current preset
    var preset = LightPresets[this._currentLightPreset];
    var ac = preset && preset.ambientColor;
    var dc = preset && preset.directLightColor;

    ac = ac?.slice(0) ?? this._defaultAmbientColor.toArray(); // create copy to not change preset!
    dc = dc?.slice(0) ?? this._defaultDirLightColor.toArray(); // create copy to not change preset!

    if (state) {
      if (isForOverlay && amb_light)
      amb_light.color.setRGB(dc[0] * 0.5, dc[1] * 0.5, dc[2] * 0.5);else
      if (amb_light) {
        amb_light.color.setRGB(ac[0], ac[1], ac[2]);
      }

      if (dir_light1) {
        dir_light1.color.setRGB(dc[0], dc[1], dc[2]);
      }
    } else
    {
      //Restores the ambient for the main scene after drawing overlays
      if (amb_light && isForOverlay)
      amb_light.color.setRGB(ac[0], ac[1], ac[2]);
    }
  }


  /**
   * @param {boolean} state
   * @param {boolean} [isForOverlay]
   * @return {void}
   */
  toggleLights(state, isForOverlay) {

    //This can happen during initial construction
    if (!this.amb_light)
    return;

    // Don't create or remove arrays, as that's bad to do during rendering.
    // Instead, later use lightsOn to decide which array to use.
    this.lightsOn = state;

    this._setLights(this.amb_light, this.dir_light1, state, isForOverlay);
  }

  //Forces the view controller to update when the camera
  //changes programmatically (instead of via mouse events).
  syncCamera(syncWorldUp) {
    this.camera.updateCameraMatrices();

    if (syncWorldUp)
    this.setWorldUp(this.api.navigation.getWorldUpVector());

    this._cameraUpdated = true;
  }

  /**
   * Get the model's initial camera
   * @param {Model} model
   * @returns camera
   */
  getModelCamera(model) {
    if (!model) return;
    let camera;
    const defaultCamera = model.getDefaultCamera();
    if (defaultCamera) {
      camera = defaultCamera;
    } else {
      //Model has no default view. Make one up based on the bounding box.
      camera = UnifiedCamera.getViewParamsFromBox(
        model.getBoundingBox(),
        model.is2d(),
        this.camera.aspect,
        this.camera.up,
        this.camera.fov
      );
    }
    return camera;
  }


  setViewFromFile(model, skipTransition) {

    if (!model) {
      return;
    }

    var camera = this.getModelCamera(model);

    //[TS] WTF, this is a hack that should not be here, the viewer3dimpl is not supposed to know
    //about tools at all!
    // If the current orbiting mode is unconstrained (the 'freeorbit' tool),
    // use exact camera settings, otherwise (the 'orbit' tool) snap the up vector to a world axis.
    // However, if this is the initial load and the model has a free orbit navigation mode defined
    // we will use the exact camera settings no matter if the free orbit tool is active or not.
    // Note #1: that 'freeorbit' vs. 'orbit' tools are active even when the FusionOrbit extension is used.
    // Note #2: isToolActivated is not available in node-lmv, so we stub it to always return false
    var navModeHint = model.getMetadata('navigation hint', 'value', null);
    var useExactCamera = this.controls.isToolActivated('freeorbit') ||
    skipTransition && navModeHint === "Freeorbit";

    this.setViewFromCamera(camera, skipTransition, useExactCamera);
  }

  //Camera is expected to have the properties of a THREE.Camera.
  adjustOrthoCamera(camera) {
    var bbox = this.getVisibleBounds();
    UnifiedCamera.adjustOrthoCamera(camera, bbox);
  }

  /**
   * Switches to a new view based on a given camera. If the current orbiting mode is constrained,
   * the up vector may be adjusted.
   *
   * @param {THREE.Camera} camera Input camera.
   * @param {boolean} skipTransition Switch to the view immediately instead of transitioning.
   * @param {boolean} useExactCamera -- whether any up vector adjustment is to be done (to keep head up view)
   */
  setViewFromCamera(camera, skipTransition, useExactCamera) {
    this.adjustOrthoCamera(camera);

    // Choose the first up-vector we get from the main model.
    // We assume all models have identical up vectors - otherwise, the aggregated model would be weird anyway.
    var worldUp = this.model ? this.model._getAlignedUpVector() : null;

    if (!worldUp) {
      worldUp = useExactCamera ? camera.up.clone() : Navigation.snapToAxis(camera.up.clone());
    }

    const cameraDirection = camera.target.clone().
    sub(camera.position).
    normalize();

    // A degenerate (zero-length or non-finite) direction — e.g. from an empty/degenerate model
    // bounding box, where position equals or nearly equals target — carries no real orientation
    // information. Treat it like the top-down case so we don't overwrite camera.up with a
    // possibly meaningless value.
    const isTopDown = !cameraDirection.isFinite() || cameraDirection.lengthSq() === 0 ||
    cameraDirection.distanceTo(worldUp.clone().negate()) < 1E-6;

    if (useExactCamera) {
      if (this.api.prefs)
      this.api.prefs.set('fusionOrbitConstrained', worldUp.equals(camera.up));
    } else if (!isTopDown) {
      // Only apply camera up as world up if it's not the default view, which is looking downward
      camera.up = worldUp;
    }
    var navapi = this.api.navigation;
    if (navapi) {

      var tc = this.camera;

      if (!skipTransition) {
        tc.isPerspective = camera.isPerspective;

        if (!camera.isPerspective) {
          tc.saveFov = camera.fov; // Stash original fov
          camera.fov = UnifiedCamera.ORTHO_FOV;
        }

        if (useExactCamera) {
          navapi.setRequestTransitionWithUp(true, camera.position, camera.target, camera.fov, camera.up, worldUp);
        } else {

          // Fix camera's target if it is not inside the scene's bounding box.
          var bbox = this.getVisibleBounds();
          if (!bbox.containsPoint(camera.target)) {
            var distanceFromCenter = bbox.getCenter(new THREE.Vector3()).distanceTo(camera.position);
            var direction = camera.target.clone().sub(camera.position).normalize().multiplyScalar(distanceFromCenter);
            camera.target.copy(camera.position.clone().add(direction));
          }

          var up = navapi.computeOrthogonalUp(camera.position, camera.target);
          navapi.setRequestTransitionWithUp(true, camera.position, camera.target, camera.fov, up, worldUp);
        }
      } else {
        //This code path used during initial load -- it sets the view directly
        //without doing a transition. Transitions require that the camera is set explicitly

        tc.up.copy(camera.up);
        tc.position.copy(camera.position);
        tc.target.copy(camera.target);
        if (camera.isPerspective) {
          tc.fov = camera.fov;
        } else
        {
          tc.saveFov = camera.fov; // Stash original fov
          tc.fov = UnifiedCamera.ORTHO_FOV;
        }
        tc.isPerspective = camera.isPerspective;
        tc.orthoScale = camera.orthoScale;
        tc.dirty = true;

        navapi.setWorldUpVector(useExactCamera || isTopDown ? worldUp : tc.up);
        navapi.setView(tc.position, tc.target);
        navapi.setPivotPoint(tc.target);

        this.syncCamera(true);
      }
    }
    this._cameraUpdated = true;
  }

  /**
   * Performs the inverse operation than Viewer3D.setViewFromArray() using
   * the current camera values.
   *
   * @param {object} [globalOffset] - { x:Number, y:Number, z:Number } that gets substracted from position and target.
   *
   * @returns {Array} with 13 elements
   */
  getViewArrayFromCamera(globalOffset) {

    var off = globalOffset || { x: 0, y: 0, z: 0 };
    var cam = this.camera;

    var worldUp;
    var upVectorArray = this.model.getUpVector();
    if (upVectorArray) {
      worldUp = new THREE.Vector3().fromArray(upVectorArray);
    } else {
      worldUp = Navigation.snapToAxis(cam.up.clone());
    }

    var target = this.api.navigation.getPivotPoint();

    var ret = [
    cam.position.x + off.x, cam.position.y + off.y, cam.position.z + off.z,
    target.x + off.x, target.y + off.y, target.z + off.z,
    worldUp.x, worldUp.y, worldUp.z,
    cam.aspect,
    THREE.Math.degToRad(cam.fov),
    cam.orthoScale,
    cam.isPerspective ? 0 : 1];


    return ret;
  }

  setViewFromViewBox(model, viewbox, name, skipTransition) {
    if (!model.is2d()) {
      return;
    }


    var camera = {};

    var bbox = model.getBoundingBox();

    var box = {
      width: viewbox[2] - viewbox[0],
      height: viewbox[3] - viewbox[1]
    };
    box.aspect = box.width / box.height;
    box.centerX = viewbox[0] + box.width / 2;
    box.centerY = viewbox[1] + box.height / 2;

    var screenAspect = this.camera.aspect;

    //Fit the viewbox to the screen
    if (screenAspect > box.aspect)
    camera.orthoScale = box.height;else

    camera.orthoScale = box.width / screenAspect;

    const bboxCenter = bbox.getCenter(new THREE.Vector3());
    camera.isPerspective = false;
    camera.position = new THREE.Vector3(box.centerX, box.centerY, bboxCenter.z + camera.orthoScale);
    camera.target = new THREE.Vector3(box.centerX, box.centerY, bboxCenter.z);
    camera.target.y += 1e-6 * box.height;

    camera.up = new THREE.Vector3(0, 0, 1);

    this.setViewFromCamera(camera, skipTransition, false);
  }

  setWorldUp(upVector) {

    if (this._worldUp.equals(upVector))
    return;

    this._worldUp.copy(upVector);

    // get the (max) up axis and sign
    var maxVal = Math.abs(upVector.x);
    this._worldUpName = "x";
    if (Math.abs(upVector.y) > maxVal) {
      this._worldUpName = "y";
      maxVal = Math.abs(upVector.y);
    }
    if (Math.abs(upVector.z) > maxVal) {
      this._worldUpName = "z";
    }

    var getRotation = function (vFrom, vTo) {
      var rotAxis = new THREE.Vector3().crossVectors(vTo, vFrom).normalize(); // not sure why this is backwards
      var rotAngle = Math.acos(vFrom.dot(vTo));
      return new THREE.Matrix4().makeRotationAxis(rotAxis, rotAngle);
    };

    var identityUp = new THREE.Vector3(0, 1, 0);
    this.camera.worldUpTransform = getRotation(identityUp, upVector);

    this.sceneUpdated(false);
  }

  /**
   * @param {Model} model
   * @param {boolean} [isOverlay]
   * @return {void}
   */
  setUp2DMode(model, isOverlay) {
    const data = model.getData();

    // Initialize layer texture if needed. Note that some files may not have layers at all, e.g., leaflets.
    if (data?.layerCount) {
      this._materials.initLayersTexture(data.layerCount, data.layersMap, model.id);
    }

    // The id material is not specific to a model, so don't make it a model material.
    // If the id material is attached to a model, you can get into this situation:
    // Load two models m1 first and then m2 into the same RenderContext and MaterialManager.
    // At the end of this the RenderContext's id material is attached to m2. Then transfer
    // m2 to a new RenderContext and MaterialManager. Because the id material is attached
    // to m2, it is transfered to the new context, but it is still the id material in the
    // first context, too. There isn't anything in the id material that is specific to a model,
    // so keeping the id material from being attached to a model, fixes that issue.
    const idMatName = this._materials.create2DMaterial(null, {}, true, false, function () {this.invalidate(false, true, false);});
    const idMaterial = this._materials.findMaterial(null, idMatName);

    if (!isOverlay) {
      // In case the previous model was 2d, first exit the previous 2d mode, and only then re-enter it again.
      if (this.is2d) {
        this._renderer.exit2DMode();
      }

      // When loading single leaflet models, don't ask for ids.
      this._renderer.enter2DMode(idMaterial, this._materials.get2dSelectionColor());

      // If we were in 3d mode before, save lightPreset first before replacing it by 2d preset
      if (!this.is2d) {
        this.saveLightPreset();
      }

      this.is2d = true;

      this.setLightPreset(DefaultLightPreset2d);
      this.setRenderingPrefsFor2D(true);

      const svf = model.getData();
      if (svf?.hidePaper) {
        var bg = svf.bgColor;
        var r = bg >> 16 & 0xff;
        var g = bg >> 8 & 0xff;
        var b = bg & 0xff;
        this.setClearColors(r, g, b, r, g, b);
      }
    }
  }

  /**
   * @param {Model} model
   * @param {boolean} [preserveTools]
   * @return {void}
   */
  addModel(model, preserveTools) {
    if (!model)
    return;

    if (!model.isInitialized()) {
      model.initialize(this);
    }

    //Is it the first model being loaded into the scene?
    var isOverlay = !!this.model;
    var is2d = model.is2d();

    if (!this.model && !model.getData().underlayRaster) {
      this.model = model;

      this._renderer.setUnitScale(model.getUnitScale());
    }

    // Initialize layers.
    if (!this.layers) {
      this.layers = new ModelLayers(this);
    }

    if (!model.getData().underlayRaster) {
      this.layers.addModel(model, /*defer3d*/true);
    }

    //Create a render list for progressive rendering of the
    //scene fragments
    this._renderScene.addModel(model);
    this.selector.addModel(model);
    this.visibilityManager.addModel(model);

    if ((this._useWebGPU || USE_MULTI_MATERIAL_RENDER_CALLS) && !is2d) {
      this._webglrender.add3dModel(model);
    }

    this._setModelPreferences(model);

    // In case of a 2D drawing initialize the common line shader and the layers texture.
    if (is2d) {
      this.setUp2DMode(model, isOverlay);
    } else if (this.is2d) {
      // If a previous model was 2d and the newly inserted model is 3d, switch off 2d mode.
      this.is2d = undefined;
      this._renderer.exit2DMode();
    } else if (this._3dLightPreset >= 0) {
      // LMV-5655: Keep track of the last 3d light preset.
      // There was an issue when switching from 3d -> 2d -> 2d -> 3d.
      // For this case the _oldLightPreset would be overridden with environment 0.
      this._oldLightPreset = this._3dLightPreset;
    }

    // Make sure that swapBlackAndWhite toggle is only considered as long as we are in 2d
    this.updateClearColors();

    this.setupLighting(model);
    this._syncIdTargetCount();

    this.fireRenderOptionChanged();
    this.invalidate(true);

    // Fire an event for the addition of a model into the _renderScene
    this.api.fireEvent({ type: et.MODEL_ADDED_EVENT, model, preserveTools, isOverlay });
  }

  _setModelPreferences(model) {

    // This is the place to load from preferences when a new model is added

    // Apply current renderLines/renderPoints settings
    // hint: these settings might be undefined in LeechViewer contexts (see LeechViewer.prototype.setViewerProfile)
    if (this.api.prefs.get('lineRendering') !== undefined && model.is3d())
    {
      model.hideLines(!this.api.prefs.get('lineRendering'));
      model.hidePoints(!this.api.prefs.get('pointRendering'));
    }

    // selection mode
    model.selector.setSelectionMode(this.api.prefs.get(Prefs3D.SELECTION_MODE));

  }

  setupLighting(model) {

    model = model || this.model;

    if (isNodeJS() || !model || model.is2d()) {
      return;
    }

    // grab the environment preset data from the file.
    //This will usually be set for Fusion files.
    if (!this.setLightPresetFromFile(model)) {
      //When switching from a 2D sheet back to a 3D view,
      //we restore the environment map that was used for the
      //last 3D view displayed. The operation is delayed until here
      //so that switching between 2D sheets does not incur this unnecessary overhead.
      if (this._oldLightPreset >= 0) {
        this.setLightPreset(this._oldLightPreset, true, this._oldCallback);
        this._oldLightPreset = -1;
        this._oldCallback = null;
      } else {
        this.setLightPreset(this._currentLightPreset, false);
      }
    }

    // For WebGPU, we use the Tandem implementation, which
    // doesn't use the extra heuristics.
    if (!this._useWebGPU) {
      this.setAOHeuristics(model);
    }
  }

  getSvfMaterialId(fragId) {
    return this.model.getFragmentList().getSvfMaterialId(fragId);
  }

  getMaterials() {return this._materials;}


  //Creates a THREE.Mesh representation of a fragment. Currently this is only
  //used as vehicle for activating a fragment instance for rendering once its geometry is received
  //or changing the fragment data (matrix, material). So, it's mostly vestigial.
  /**
   * @param {Model} model
   * @param {THREE.Geometry | LeanBufferGeometry} threegeom
   * @param {number} materialId
   * @param {THREE.Matrix4} matrix
   * @return {void}
   */
  setupMesh(model, threegeom, materialId, matrix) {
    const m = {
      geometry: threegeom,
      matrix: matrix
    };

    // MaterialId can be null, for an invalid material.
    // In this case setupMaterial will clone the default material.
    if (materialId || materialId === null)
    m.material = this.matman().setupMaterial(model, threegeom, materialId);

    m.isLine = threegeom.isLines;
    m.isWideLine = threegeom.isWideLines;
    m.isPoint = threegeom.isPoints;
    m.is2d = threegeom.is2d;

    // Update creation time for loading animation.
    if (model.is2d()) {
      m.geometry.creationTime = this._lastHighResTimeStamp;
      this.lastTime2dGeometryCreated = this._lastHighResTimeStamp;
    }

    return m;
  }

  selection2dOverlayName(model) {
    //We have to use the material hashing from MaterialManager so that
    //the material gets cleaned up when the model is unloaded.
    return this._materials._getMaterialHash(model, "selection2d");
  }

  init2dSelection(model) {

    var overlayName = this.selection2dOverlayName(model);
    if (this.overlayScenes[overlayName]) {
      // Selection already initialized for this model
      return;
    }

    // create selection texture and material
    // Note: We assume here that the selection material for this model does not exist yet. (otherwise, the params in create2DMaterial would be ignored)
    var selectionTexture = this._materials.initSelectionTexture(model.getData().maxObjectNumber, model.id);
    const modelFrags = model.getFragmentList();
    const doNotCut = model.getDoNotCut();

    let viewportBounds;

    if (modelFrags?.viewBounds) {
      const bounds = modelFrags.viewBounds;
      viewportBounds = new THREE.Vector4(bounds.min.x, bounds.min.y, bounds.max.x, bounds.max.y);
    }

    var selMatName = this._materials.create2DMaterial(model, { doNotCut, viewportBounds },
    false, selectionTexture, function () {this.invalidate(false, true, false);});
    var selMat = this._materials.findMaterial(model, selMatName);

    this.createOverlayScene(overlayName, selMat);
  }

  // Gets called by the active Loader
  onLoadComplete(model) {
    this._isLoading = false;

    this.signalProgress(100, ProgressState.LOADING, model);

    if (this.modelVisible(model.id)) {
      // Only if ground shadows or reflections are on do we need to emit a refresh.
      if (this._groundShadow && this._groundShadow.enabled || this._groundReflection) {
        this.sceneUpdated(false, true);
      }
      this.invalidate(!!this._groundReflection, true, false);
    }

    //In the case of 2d drawing, initialize the dbIds texture
    //to be used for selection highlighting.
    // Note that we cannot do this earlier: Creating the texture requires to know model.myData.maxObjectNumber - which
    // is dynamically increased during loading and not known on model-add.
    if (model.is2d()) {
      this.init2dSelection(model);
    }

    if (!model.hasGeometry()) {
      logger.warn("Loaded model has no geometry.");
    }
    // do a silent render in case a transparent object got loaded and rendered ahead of an opaque one.
    else if (this._materials.hasTransparentMaterial()) {
      this.requestSilentRender();
    }

    // Fire the event so we know the geometry is done loading.
    this.api.dispatchEvent({
      type: et.GEOMETRY_LOADED_EVENT,
      model: model
    });
  }

  onTextureLoadComplete(model) {
    // Fire the event so we know the textures for a model are done loading.
    this.api.dispatchEvent({
      type: et.TEXTURES_LOADED_EVENT,
      model: model
    });

    // Once all the texture are loaded, we need to trigger an extra silent Render in Next Frame
    // It will fix the missing texture and avoid loading-flashing if we clear the color target everytime
    // LMV-4577 for more information
    this.requestSilentRender();
  }

  signalProgress(percent, progressState, model, totalFragments, loaded, total) {
    if (this._progressEvent.percent === percent &&
    this._progressEvent.state === progressState &&
    this._progressEvent.loaded === loaded &&
    this._progressEvent.total === total && (
    !model || !this._progressEvent.model || this._progressEvent.model.id === model.id)) {
      return;
    }

    this._progressEvent.percent = percent;
    this._progressEvent.state = progressState;

    this._progressEvent.model = model;
    this._progressEvent.loaded = loaded;
    this._progressEvent.total = total;
    this._progressEvent.totalFragments = totalFragments;

    this.api.dispatchEvent(this._progressEvent);
  }

  resize(w, h, immediateUpdate) {
    this._needsResize = true;
    this._newWidth = w;
    this._newHeight = h;

    if (immediateUpdate) {
      this._updateCanvasSize(true);
    }
  }

  unloadModel(model, keepResources) {

    this.api.dispatchEvent({ type: et.BEFORE_MODEL_UNLOAD_EVENT, model: model });

    // If model was visible, remove it.
    // If it was hidden, it has already been removed from viewer and we just have to remove it from
    // the hiddenModels list in RenderScene.
    if (!this.removeModel(model) && !this._renderScene.removeHiddenModel(model)) {
      // If neither of this works, this model is unknown.
      return;
    }

    if (!keepResources) {
      this._materials.cleanup(model);
      // Note that this just discards the GPU resources, not the model itself.
      model.dtor();

      if (model.loader) {
        model.loader.dtor?.();
        model.loader = null;
      }
    }

    // remove selection overlay (F2D models)
    if (model.is2d() && this.overlayScenes[model.id]) {
      this.removeOverlayScene(this.selection2dOverlayName(model));
    }

    if (this._progressEvent.model === model) {
      this._progressEvent.model = null;
    }

    this.api.dispatchEvent({ type: et.MODEL_UNLOADED_EVENT, model: model });

    // These functions are used for out-of-core management.
    // WebGPU has another GroundShadow implementation that doesn't support scenesPerModel yet.
    // And GroundReflection is currently not supported yet at all in WebGPU.
    if (!USE_WEBGPU) {
      this._groundShadow?.resetScenesPerModel();
      this._groundReflection?.resetScenesPerModel();
    }
  }

  _reserveLoadingFile() {
    if (!this.loaders) {
      this.loaders = [];
    }
    // The reservation is an object with a dtor function, in case
    // the load gets canceled before loader instance is created.
    var reservation = { dtor: function () {} };
    this.loaders.push(reservation);
    return reservation;
  }

  _hasLoadingFile() {
    return this.loaders && this.loaders.length > 0;
  }

  _addLoadingFile(reservation, svfLoader) {
    if (this.loaders) {
      var index = this.loaders.indexOf(reservation);
      if (index >= 0)
      this.loaders[index] = svfLoader;
    }
  }

  _removeLoadingFile(svfLoader) {
    if (this.loaders) {
      var idx = this.loaders.indexOf(svfLoader);
      if (idx >= 0) {
        this.loaders.splice(idx, 1);
      }
    }
  }


  /** Removes a model from this viewer, but (unlike unload) keeps the RenderModel usable,
   *  so that it can be added to this or other viewers later.
   *   @param {RenderModel}
   *   @returns {boolean} True if the model was known and has been successfully removed.
   */
  removeModel(model) {

    if (!this._renderScene.removeModel(model)) {
      return false;
    }

    // TODO: Removing a single model should not destroy this whole thing.
    if (this.keyFrameAnimator) {
      this.keyFrameAnimator.destroy();
      this.keyFrameAnimator = null;
    }

    this.selector.removeModel(model);
    this.visibilityManager.removeModel(model);
    this.layers.removeModel(model);

    if (model === this.model) {
      this.model = null;

      if (!this._renderScene.isEmpty())
      this.model = this._renderScene.getModels()[0];
    }

    this._syncIdTargetCount();
    this.invalidate(true, true, true);

    this.api.fireEvent({ type: et.MODEL_REMOVED_EVENT, model: model });

    // Remove 3D model from WebGPU renderer.
    // Needs to happen after MODEL_REMOVED_EVENT so listeners can perform cleanup first.
    if ((this._useWebGPU || USE_MULTI_MATERIAL_RENDER_CALLS) && model.is3d()) {
      this._webglrender.remove3dModel(model);
    }

    return true;
  }

  /**
   * Stops loading for a model url for which the RenderModel is not in memory yet.
   * TODO: This should be unified with unloadModel to a single API function, but we need a unique way first
   *       to address the model in both cases.
   *
   *  @param {string} url - Must exactly match the url used for loading
   */
  cancelLoad(url) {

    if (!this.loaders) {
      return;
    }

    // Find loader that is loading this url
    for (var i = 0; i < this.loaders.length; i++) {
      // TODO: currentLoadPath is only defined for SVF/OTG models. It would be better to have a unified way
      //       to cancel model loading.
      var loader = this.loaders[i];
      if (loader.currentLoadPath === url) {
        // Loader found - stop it
        loader.dtor();
        this.loaders.splice(i, 1);
        break;
      }
    }
  }

  _syncIdTargetCount() {
    // WebGPU doesn't support a variable id target count, it is hardcoded. See CommonRenderTargets.js.
    if (USE_WEBGPU)
    return;

    if (isMobileDevice())
    return;

    var sceneModelCount = this._renderScene.getModels().length;
    let flags = this._renderer.mrtFlags();

    if (sceneModelCount > 1 && flags.mrtIdBuffer < 2) {
      // To support more than 24 bits, the target count will have to be 2 from the get-go,
      // even for single model usage.
      var bChanged = this._renderer.setIdTargetCount(2);
      bChanged && this._materials.toggleMRTSetting(this._renderer.mrtFlags());
    }
  }

  /**
   * Removes loaded models and models that are getting loaded.
   * Method can be invoked while still loading the initial model.
   */
  unloadCurrentModel() {

    if (this.model) {
      //Before loading a new model, restore states back to what they
      //need to be when loading a new model. This means restoring transient
      //changes to the render state made when entering 2d mode,
      //like light preset, antialias and SAO settings,
      //and freeing GL objects specific to the old model.
      if (this.is2d) {
        this.is2d = undefined;
        this.removeOverlayScene(this.selection2dOverlayName(this.model));
        this._renderer.exit2DMode();
      } else {
        this._oldLightPreset = this._currentLightPreset;
      }

      if (this.model.is3d()) {
        // LMV-5655: Keep track of the 3d light preset
        this._3dLightPreset = this._currentLightPreset;
      }

      this._renderer.beginScene(this.emptyScene, this.camera, this.lightsOn ? this.lights : this.no_lights, true);
      this._renderer.composeFinalFrame(true);
    }

    // Destruct any ongoing loaders, in case the loading starts, but the model root hasn't created yet.
    if (this.loaders) {
      this.loaders.forEach(function (loader) {
        loader.dtor();
      });
      this.loaders = [];
    }

    var models = this._renderScene.getAllModels();
    for (var i = models.length - 1; i >= 0; i--)
    this.unloadModel(models[i]);

    this.model = null;
  }

  _createSelectionScene(name, materialPre, materialPost) {
    materialPre.depthWrite = false;
    materialPre.depthTest = true;
    materialPre.side = THREE.DoubleSide;

    materialPost.depthWrite = false;
    materialPost.depthTest = true;
    materialPost.side = THREE.DoubleSide;

    // make selection material support instanced geometry
    var pointPre = this._materials.createOverridePointMaterial(materialPre, { vertexColors: false, useSourcePointSize: true });
    var pointPost = this._materials.createOverridePointMaterial(materialPost, { vertexColors: false, useSourcePointSize: true });
    this._materials.addOverrideMaterialVariants(materialPre, pointPre);
    this._materials.addOverrideMaterialVariants(materialPost, pointPost);

    this.createOverlayScene(name, materialPre, materialPost);
  }

  /**
   * Force the overlay selection materials into the linear, no-MRT, no-polygon-offset state the
   * overlay pass needs. createOverlayScene registers them via addOverrideMaterial, which runs
   * _applyMRTFlags and would enable MRT/polygon offset and cause z-fighting when drawn coplanar
   * with the model, so this must run once after registration (see _setupSelectionHighlight).
   * @private
   */
  _syncSelectionOverlayMaterialHDR() {
    if (!this.selectionMaterialBase) {
      return;
    }
    // Selection materials render linear color into the 3D overlay target. The blend pass composites
    // that target 1:1 (no sqrt), and the forward shaders apply the display gamma lift via the
    // overlayGammaEncode uniform while the legacy overlay scenes render (see RenderContext.render-
    // Overlays and phong_frag.glsl). So keep these materials linear here: tonemapOutput = 0 and
    // exposureBias = 1.0, with no irradiance/envMap (the selection color is an authored LDR UI
    // color, not HDR scene lighting) and no polygon offset / MRT flags (the overlay is drawn
    // coplanar with the model using depthWrite=false, so those would cause z-fighting).
    const syncMat = (mat) => {
      mat.tonemapOutput = 0;
      mat.exposureBias = 1.0;
      mat.irradianceMap = null;
      mat.envMapExposure = 1;
      mat.envMap = null;
      mat.polygonOffset = false;
      mat.polygonOffsetFactor = 0;
      mat.polygonOffsetUnits = 0;
      mat.mrtNormals = false;
      mat.mrtIdBuffer = false;
      mat.needsUpdate = true;
    };

    syncMat(this.selectionMaterialBase);
    syncMat(this.selectionMaterialTop);
    this.selectionMaterialBase.variants?.forEach(syncMat);
    this.selectionMaterialTop.variants?.forEach(syncMat);
  }

  _setupSelectionHighlight() {

    this.selectionMaterialBase = new THREE.MeshPhongMaterial({ specular: 0x080808, opacity: 1.0, transparent: false });
    this.selectionMaterialTop = new THREE.MeshPhongMaterial({ specular: 0x080808, opacity: 0.15, transparent: true });
    this.selectionMaterialTop.packedNormals = true;
    this.selectionMaterialBase.packedNormals = true;
    this.selectionMaterialBase.disableEnvMap = true;
    this.selectionMaterialTop.disableEnvMap = true;
    // selectionMaterialBase is the visible base highlight.
    // selectionMaterialTop draws over everything.
    this._createSelectionScene("selection", this.selectionMaterialBase, this.selectionMaterialTop);

    this.highlightMaterial = new THREE.MeshPhongMaterial({ specular: 0x080808, opacity: 1.0, transparent: false });
    this.highlightMaterial.packedNormals = true;
    this.highlightMaterial.disableTheming = true;
    this.highlightPointMaterial = this._materials.createOverridePointMaterial(this.highlightMaterial, { vertexColors: true, useSourcePointSize: true });
    this.highlightPointMaterial.defines = this.highlightPointMaterial.defines || {};
    // Selected point clouds keep their per-point colors with a subtle selection tint applied in
    // the shader (see HIGHLIGHT_POINT_TINT in basic_frag.glsl), instead of a solid color override.
    this.highlightPointMaterial.defines['HIGHLIGHT_POINT_TINT'] = 1;
    // As the selection is rendered as a tint, theming needs to be applied for this highlight.
    this.highlightPointMaterial.disableTheming = false;
    this._materials.addOverrideMaterialVariants(this.highlightMaterial, this.highlightPointMaterial);
    this._materials.addMaterial("__highlightMaterial__", this.highlightMaterial, true);

    this._renderer.setHighlightMaterial?.(this.highlightMaterial);
    this._syncSelectionOverlayMaterialHDR();

  }

  _setupLegacySceneLayers() {
    this._renderLayerManager.create('legacyEmptyScene', {
      renderOrder: Number.NEGATIVE_INFINITY, // Empty scene is always rendered first
      renderTarget: 'main',
      clearDepthAfter: false,
      visible: true
    });
    this._renderLayerManager.addScene(this.emptyScene, 'legacyEmptyScene');

    this._renderLayerManager.create('legacySceneBefore', {
      // Use -Number.EPSILON (largest negative value, closest to zero) so that viewer.scene
      // renders last among before-layers. Layers added via addMainPassOverlay(scene, true) may
      // use clearDepthAfter, so viewer.scene must render last to preserve its depth values for
      // the main model pass.
      renderOrder: -Number.EPSILON,
      renderTarget: 'main',
      clearDepthAfter: false,
      visible: true
    });
    this._renderLayerManager.addScene(this.scene, 'legacySceneBefore');

    this._renderLayerManager.create('legacySceneAfter', {
      renderOrder: 1,
      renderTarget: 'main',
      clearDepthAfter: false,
      visible: true
    });
    this._renderLayerManager.addScene(this.sceneAfter, 'legacySceneAfter');
  }

  getRenderLayerManager() {
    return this._renderLayerManager;
  }

  createOverlayScene(name, materialPre, materialPost, camera, needIdTarget = false, needSeparateDepth = false, hiddenFromMainRender = false) {
    if (materialPre) {
      this._materials.addOverrideMaterial(name + "_pre", materialPre);
    }

    if (materialPost) {
      this._materials.addOverrideMaterial(name + "_post", materialPost);
    }

    var s = new THREE.Scene();
    s.name = name;
    s.__lights = this.emptyScene.__lights;

    return this.overlayScenes[name] = {
      scene: s,
      camera: camera,
      materialName: name,
      materialPre: materialPre,
      materialPost: materialPost,
      needIdTarget,
      needSeparateDepth,
      hiddenFromMainRender
    };
  }

  removeOverlayScene(name) {

    var overlay = this.overlayScenes[name];
    if (overlay) {
      var scene = this.overlayScenes[name];
      scene.materialPre && this._materials.removeMaterial(scene.materialName + "_pre");
      scene.materialPost && this._materials.removeMaterial(scene.materialName + "_post");
      delete this.overlayScenes[name];
      this.invalidate(false, false, true);
    }
  }

  addOverlay(overlayName, mesh) {
    if (this.overlayScenes[overlayName] && mesh) {
      this.overlayScenes[overlayName].scene.add(mesh);
      this.invalidate(false, false, true);
    }
  }

  addMultipleOverlays(overlayName, meshes) {
    for (var i in meshes) {
      if (!Object.prototype.hasOwnProperty.call(meshes, i)) continue;
      this.addOverlay(overlayName, meshes[i]);
    }
  }

  removeOverlay(overlayName, mesh, disposeGeometry = false) {
    if (this.overlayScenes[overlayName] && mesh) {
      this.overlayScenes[overlayName].scene.remove(mesh);
      if (disposeGeometry && mesh.geometry) {
        mesh.geometry.dispose();
      }
      this.invalidate(false, false, true);
    }
  }

  removeMultipleOverlays(overlayName, meshes, disposeGeometry = false) {
    for (var i in meshes) {
      if (!Object.prototype.hasOwnProperty.call(meshes, i)) continue;
      this.removeOverlay(overlayName, meshes[i], disposeGeometry);
    }
  }

  /**
   * @deprecated Use RenderLayerManager.create() and RenderLayerManager.addScene() instead.
   */
  addMainPassOverlay(scene, drawBefore) {
    const layerId = '__mainPassOverlay_' + scene.uuid;

    // We need to keep the same order, but ensure that drawBefore overlays have negative values.
    const renderOrder = drawBefore ?
    scene.sortOrder > 0 ? scene.sortOrder - Number.MAX_SAFE_INTEGER : scene.sortOrder :
    scene.sortOrder;
    this._renderLayerManager.create(layerId, {
      renderOrder,
      renderTarget: 'main',
      clearDepthAfter: scene.clearDepthBuffer,
      visible: true
    });
    this._renderLayerManager.addScene(scene, layerId);
  }

  /**
   * @deprecated Use RenderLayerManager.remove() instead.
   */
  removeMainPassOverlay(scene) {
    const layerId = '__mainPassOverlay_' + scene.uuid;
    this._renderLayerManager.remove(layerId);
  }

  clearOverlay(overlayName, disposeGeometry = false) {

    if (!this.overlayScenes[overlayName])
    return;

    var scene = this.overlayScenes[overlayName].scene;
    var obj, i;
    for (i = scene.children.length - 1; i >= 0; --i) {
      obj = scene.children[i];
      if (obj) {
        scene.remove(obj);
        if (disposeGeometry && obj.geometry) {
          obj.geometry.dispose();
        }
      }
    }

    this.invalidate(false, false, true);
  }

  setClearColors(r, g, b, r2, g2, b2) {
    this.clearColorTop = new THREE.Vector3(r / 255.0, g / 255.0, b / 255.0);
    this.clearColorBottom = new THREE.Vector3(r2 / 255.0, g2 / 255.0, b2 / 255.0);

    this.updateClearColors();
    if (this._groundReflection)
    this._groundReflection.setClearColors(this.clearColorTop, this.clearColorBottom, isMobileDevice());
    this._needsClear = true;
    this.fireRenderOptionChanged();
  }

  setClearAlpha(alpha) {
    this._renderer.setClearAlpha(alpha);
  }

  //Similar to THREE.Box3.setFromObject, but uses the precomputed bboxes of the
  //objects instead of doing it per vertex.
  _computeObjectBounds(dst, object, bboxFilter) {

    object.updateMatrixWorld(true);

    object.traverse(function (node) {

      var geometry = node.geometry;

      // Special-handling for selection proxies. Why needed?:
      //  - selection proxies share model geometry
      //  - A model BufferGeometry does not contain a BoundingBoxes (to save memory)
      //  - A model geometry uses interleaved buffers, which is not supported by computeBoundingBox() anyway.
      // So, the standard handling below does not work here and would just waste memory by attaching wrong bboxes to model geometry.
      const isModelGeom = node.model && typeof node.fragId === 'number';
      if (isModelGeom && !geometry.boundingBox) {

        const fragList = isModelGeom && node.model.getFragmentList();
        if (fragList) {
          fragList.getWorldBounds(node.fragId, _box3);

          if (!bboxFilter || bboxFilter(_box3)) {
            dst.union(_box3);
          }
        }

        // TODO: In case any overlay contains inverleaved geometry from anywhere else, this is still not
        //       handled properly here.
        return;
      }

      if (geometry !== undefined && node.visible) {

        if (!geometry.boundingBox)
        geometry.computeBoundingBox();

        _box3.copy(geometry.boundingBox);
        _box3.applyMatrix4(node.matrixWorld);

        if (!bboxFilter || bboxFilter(_box3)) {
          dst.union(_box3);
        }
      }

    });
  }

  _getOverlayBounds(bboxFilter) {
    _bounds.makeEmpty();

    var overlays = this.overlayScenes;

    for (var key in overlays) {
      if (!Object.prototype.hasOwnProperty.call(overlays, key))
      continue;

      this._computeObjectBounds(_bounds, overlays[key].scene, bboxFilter);
    }

    // Include bounds from all (visible) render layers
    const mainLayers = this._renderLayerManager.getMainPassRenderLayers();
    const allVisibleLayers = [...mainLayers.before, ...mainLayers.after, ...this._renderLayerManager.getOverlayRenderLayers()];
    for (const layer of allVisibleLayers) {
      if (layer.scene) {
        this._computeObjectBounds(_bounds, layer.scene, bboxFilter);
      }

      if (layer.models?.length) {
        for (const model of layer.models) {
          const modelBounds = model.getVisibleBounds();
          if (!bboxFilter || bboxFilter(modelBounds)) {
            _bounds.union(modelBounds);
          }
        }
      }
    }

    return _bounds;
  }

  getVisibleBounds(includeGhosted, includeOverlays, bboxFilter, excludeShadow) {
    let result = new THREE.Box3();
    if (!this._renderScene.isEmpty()) {
      // Always include this.scene bounds (for client-added geometry) to match old behavior
      this._computeObjectBounds(result, this.scene, bboxFilter);
      result.union(this._renderScene.getVisibleBounds(includeGhosted, bboxFilter, excludeShadow));
    }
    if (includeOverlays) {
      result.union(this._getOverlayBounds(bboxFilter));
    }
    return result;
  }

  getFitBounds(ignoreSelection) {
    var bounds;

    // If there is a valid selection, use its bounds
    if (!ignoreSelection && this.selector !== null) {
      bounds = this.selector.getSelectionBounds();
    }

    // Otherwise, if there is a valid isolation, use its bounds
    if (!bounds || bounds.isEmpty()) {
      bounds = this.getVisibleBounds();

      //sometimes during loading, bounds set to infinity, as in BLMV-6568
      if (bounds.isEmpty() && this.model.is2d()) {
        bounds = this.model.getBoundingBox();
      }
    }
    //console.log("  getFitBounds bounds are " + + bounds.min.x +", "+ bounds.min.y + " to " + bounds.max.x +", "+ bounds.max.y);

    return bounds;
  }

  getRenderProxy(model, fragId) {
    //currently there is a single model so the mapping
    //of fragId to render mesh is 1:1.
    return model.getFragmentList()?.getVizmesh(fragId);
  }

  getLayersRoot() {
    return this.layers.getRoot();
  }

  getFragmentProxy(model, fragId) {
    return new FragmentPointer(model.getFragmentList(), fragId);
  }

  getRenderProxyCount(model) {
    return model.getFragmentList().getCount();
  }

  getRenderProxyDbIds(model, fragId) {
    return model.getFragmentList().getDbIds(fragId);
  }

  isWholeModelVisible() {
    return this._renderScene ? this._renderScene.areAllVisible() : true;
  }

  isNodeVisible(nodeId, model) {
    return this.visibilityManager.isNodeVisible(model, nodeId); // swapped arguments
  }

  highlightObjectNode(model, dbId, value, simpleHighlight) {

    dbId = model.reverseMapDbIdFor2D(dbId);

    if (model.is2d()) {
      this._materials.highlightObject2D(dbId, value, model.id); //update the 2d object id texture
      this.invalidate(false, false, true);
    }

    this.renderer().setDbIdForEdgeDetection(value && !simpleHighlight ? dbId : 0, value ? model.id : 0);

    var instanceTree = model.getData().instanceTree;

    let fragIds = [];
    //TODO: There can be instance tree in the case of 2D drawing, but
    //we do not currently populate the node tree with the virtual fragment ids
    //that map 2d objects to 2d consolidated meshes, hence the use of dbId2fragId in the else condition
    if (instanceTree && !model.is2d()) {
      instanceTree.enumNodeFragments(dbId, function (fragId) {
        fragIds.push(fragId);
      }, false);
    } else {
      fragIds = model.getData().fragments?.dbId2fragId[dbId];
      if (!Array.isArray(fragIds)) {
        fragIds = [fragIds];
      }
    }
    for (const fragId of fragIds) {
      this.highlightFragment(model, fragId, value, simpleHighlight);
    }
  }

  highlightFragment(model, fragId, value, simpleHighlight) {

    if (model.isLeaflet()) {
      model.getIterator().highlightSelection(value, this.highlightMaterial.color);
      this.invalidate(true);
      return;
    }

    var mesh = this.getRenderProxy(model, fragId);

    if (!mesh)
    return;

    // And also add a mesh to the overlays in case we need that.
    // For 2D that is always the case, while for 3D it's done
    // for "fancy" single-selection where we draw an outline for the object as post-processing step.
    // Overlay is only used for 2D, Point cloud, transparent and theming colored objects.
    // Exception: RCS point clouds are too expensive to draw with anything but simple highlight, we don't force the overlay there.
    var useOverlay = !simpleHighlight || mesh.is2d || mesh.isPoint && !model.isRCS();

    var highlightId = model.id + ":" + fragId;

    if (useOverlay) {
      var overlayName = "selection";
      if (model.is2d()) overlayName = this.selection2dOverlayName(model);
      if (mesh.isPoint) overlayName += "_points";

      if (value) {
        if (mesh.is2d && Object.prototype.hasOwnProperty.call(this.selectionMeshes, highlightId)) {
          // 2d has multiple dbids in a single fragment. As each dbid
          // is highlighted we count the number so we know when the mesh
          // is no longer needed.
          ++this.selectionMeshes[highlightId]._lmv_highlightCount;
        } else {
          // Make sure it all worked
          if (!mesh || !mesh.geometry)
          return;

          // selection_points uses selectionMaterialBase as scene override via
          // getCustomOverrideMaterial; no per-mesh material override needed here.
          const selectionProxy = new THREE.Mesh(mesh.geometry, mesh.material);

          selectionProxy.matrix.copy(mesh.matrixWorld);
          selectionProxy.matrixAutoUpdate = false;
          selectionProxy.matrixWorldNeedsUpdate = true;

          selectionProxy.frustumCulled = false;
          selectionProxy.model = model;
          selectionProxy.fragId = fragId;
          selectionProxy._lmv_highlightCount = 1;

          this.addOverlay(overlayName, selectionProxy);

          this.selectionMeshes[highlightId] = selectionProxy;
        }
      } else
      if (Object.prototype.hasOwnProperty.call(this.selectionMeshes, highlightId)) {
        var proxy = this.selectionMeshes[highlightId];
        if (--proxy._lmv_highlightCount <= 0) {
          this.removeOverlay(overlayName, proxy);
          delete this.selectionMeshes[highlightId];
        }
      }
    }

    if (!useOverlay || !value) {
      //Case where highlighting was done directly in the primary render queue
      //and we need to repaint to clear it. This happens when multiple
      //nodes are highlighted using e.g. right click in the tree view
      if (model.setHighlighted(fragId, value)) //or update the vizflags in the render queue for 3D objects
        this.invalidate(true);
    }
  }

  explode(scale, options = {}) {

    const optionKeys = Object.keys(options);

    for (let key of optionKeys) {
      if (!Object.prototype.hasOwnProperty.call(this._explodeOptions, key))
      logger.warn(`Property "${key}" is not part of Explode options.`);
    }

    const optionsUnchanged = (() => {
      for (let key of optionKeys) {
        if (Object.prototype.hasOwnProperty.call(this._explodeOptions, key) &&
        options[key] != this._explodeOptions[key])
        return false;
      }
      return true;
    })();

    scale = Number(scale);
    if (scale == this._explodeScale && optionsUnchanged)
    return false;

    this._explodeScale = scale;

    // apply new options values to _explodeOptions
    // disallow adding unspecified property names to ensure consistency of viewerState
    optionKeys.
    filter((key) => Object.prototype.hasOwnProperty.call(this._explodeOptions, key)).
    forEach((key) => this._explodeOptions[key] = options[key]);

    this.refreshExplode();

    this.api.dispatchEvent({ type: et.EXPLODE_CHANGE_EVENT, scale: scale, options: options });
    return true;
  }


  refreshExplode() {

    this._renderScene.explode(this._explodeScale, this._explodeOptions);

    //force a repaint and a clear
    this.sceneUpdated(true);
  }

  /**
   * Gets the last applied explode scale
   */
  getExplodeScale() {
    return this._explodeScale;
  }

  /**
   * Gets a copy of the last applied explode options
   */
  getExplodeOptions() {
    return { ...this._explodeOptions };
  }

  /**
   * Lock dbid so it doesn't explode
   *
   * Not applicable to 2D.
   */
  lockExplode(dbids, lock, model) {
    model = model || this.model;
    const instanceTree = model.getData().instanceTree;
    if (!instanceTree)
    return false;

    const lockDbid = function (acc, dbid) {
      instanceTree.enumNodeChildren(dbid, function (child) {
        acc = instanceTree.lockNodeExplode(child, lock) || acc;
      }, true);
      return acc;
    };

    let changed;
    if (Array.isArray(dbids)) {
      changed = dbids.reduce(lockDbid, false);
    } else {
      changed = lockDbid(false, dbids);
    }

    if (changed && this._explodeScale > 0) {
      this._renderScene.explode(this._explodeScale);
      this.sceneUpdated(true);
    }
    return changed;
  }

  /**
   * Check whether a dbid is locked so it doesn't explode
   *
   * Not applicable to 2D.
   */
  isExplodeLocked(dbid, model) {
    model = model || this.model;
    const instanceTree = model.getData().instanceTree;
    return instanceTree && instanceTree.isNodeExplodeLocked(dbid);
  }

  /**
   * Changes the ghosting brightness to dark or light based on the current background colors by computing the average
   * perceptual luminance of {@link clearColorTop} and {@link clearColorBottom}. Its called automatically when the
   * light presets / environment maps change.
   */
  adaptGhostingBrightness() {
    // Perceptual luminance (ITU BT.601) of top and bottom, averaged.
    const lum = (c) => 0.299 * c.x + 0.587 * c.y + 0.114 * c.z;
    let avgLuminance = lum(this.clearColorTop);
    if (this.clearColorBottom) {
      avgLuminance = (avgLuminance + lum(this.clearColorBottom)) * 0.5;
    }
    this.setGhostingBrightness(avgLuminance > 0.35);
  }

  /** @param {{ x: number, y: number, z: number, w: number }|null} [colorAsVec4] - RGBA edge color in [0,1], or null/undefined to remove the override. */
  setEdgeColor(colorAsVec4) {
    if (colorAsVec4) {
      this._edgeColorMain.copy(colorAsVec4);
      this._edgeColorMainOverridden = true;
    } else {
      this._edgeColorMain.copy(EdgeColor.Dark);
      this._edgeColorMainOverridden = false;
      this._adaptEdgeOpacityToDPR();
    }
  }

  /** @param {{ x: number, y: number, z: number, w: number }|null} [colorAsVec4] - RGBA edge color in [0,1], or null/undefined to remove the override. */
  setEdgeColorGhosted(colorAsVec4) {
    if (colorAsVec4) {
      this._edgeColorGhosted.copy(colorAsVec4);
      this._edgeColorGhostedOverridden = true;
    } else {
      this._edgeColorGhostedOverridden = false;
      const preset = this._ghostingDark ? EdgeColor.DarkGhosted : EdgeColor.LightGhosted;
      this._edgeColorGhosted.x = preset.x;
      this._edgeColorGhosted.y = preset.y;
      this._edgeColorGhosted.z = preset.z;
      this._adaptEdgeOpacityToDPR();
    }
  }

  /**
   * Manually sets ghosting to dark or light. Bright environments need dark ghosting (0x101010)
   * so faded objects remain visible; dark environments need light ghosting (0xffffff).
   *
   * @param {boolean} dark - true for dark ghosting (bright environments), false for light.
   */
  setGhostingBrightness(dark) {
    this._ghostingDark = dark;

    let color = new THREE.Color(dark ? 0x101010 : 0xffffff);
    this.fadeMaterial.color = color;
    this.fadeMaterial.variants?.forEach((mat) => mat.color = color);
    this._materials.syncOverridePointMaterial(this.fadeMaterial);

    if (this._edgeColorGhostedOverridden) return;

    const ghostedPreset = dark ? EdgeColor.DarkGhosted : EdgeColor.LightGhosted;
    this._edgeColorGhosted.x = ghostedPreset.x;
    this._edgeColorGhosted.y = ghostedPreset.y;
    this._edgeColorGhosted.z = ghostedPreset.z;
  }

  loadCubeMap(path, exposure) {

    this._reflectionMapPath = path;

    const mapDecodeDone = (map) => {

      //If setCubeMap was called twice quickly, it's possible that
      //a texture map that is no longer desired loads after the one that was
      //set last. In such case, just make the undesirable disappear into the void.
      if (path !== this._reflectionMapPath)
      return;

      // It is possible for this load to complete after the model has been canceled
      if (!this._materials)
      return;

      this._materials.setReflectionMap(map);
      this._useWebGPU && this._webglrender.getIBL().setReflectionMap(map);
      this.invalidate(true);

      if (!map) {
        const texture = CreateCubeMapFromColors(this.clearColorTop, this.clearColorBottom);
        this._materials.setReflectionMap(texture);

        if (this._useWebGPU) {
          const ibl = this._webglrender.getIBL();
          ibl.setReflectionMap(texture);
          ibl.setIrradianceMap(texture);
        }
      } else if (!LightPresets[this._currentLightPreset].useIrradianceAsBackground) {
        this._renderer.setCubeMap(map);
      }
    };

    return TextureLoader.loadCubeMap(path, exposure, mapDecodeDone);
  }

  loadIrradianceMap(path, exposure) {

    this._irradianceMapPath = path;

    var mapDecodeDone = (map) => {

      //If setCubeMap was called twice quickly, it's possible that
      //a texture map that is no longer desired loads after the one that was
      //set last. In such case, just make the undesirable disappear into the void.
      if (path !== this._irradianceMapPath)
      return;

      // It is possible for this load to complete after the model has been canceled
      if (!this._materials)
      return;

      this._materials.setIrradianceMap(map);
      this._useWebGPU && this._webglrender.getIBL().setIrradianceMap(map);
      this.invalidate(true);

      if (LightPresets[this._currentLightPreset].useIrradianceAsBackground) {
        this._renderer.setCubeMap(map);
      }
    };

    return TextureLoader.loadCubeMap(path, exposure, mapDecodeDone);

  }

  setCurrentLightPreset(index) {
    this._currentLightPreset = index;
  }

  setLightPreset(index, force, callback) {
    //We do not have the ability to load the environment map textures on node.js yet,
    //because they use plain XHR that needs to be converted to use TextureLoader.
    //So we override the environment to zero, which does not use external environment maps.
    if (isNodeJS())
    index = 0;

    // make sure that lights are created
    this.initLights();

    if (this._currentLightPreset === index && !force) {
      callback && callback();
      return;
    }

    // Reset index in cases the index out of range.
    // This could happen, if we update the light preset list and user
    // has a local web storage which stores the last accessed preset index which is potentially
    // out of range with respect to the new preset list.
    if (index < 0 || LightPresets.length <= index) {
      index = DefaultLightPreset;
    }
    this._currentLightPreset = index;

    // If we don't have any models, then we save the light preset
    // so it is set when a model is added. This is to stop unnecessary
    // loading of environment maps for 2D models.
    if (this._renderScene.isEmpty()) {
      this._oldLightPreset = this._currentLightPreset;
      this._oldCallback = callback;
      return;
    }

    var preset = LightPresets[index];

    //if the light preset has a specific background color, set that
    //This has to be done first, because the encironment map may use
    //the background colors in case no environment map is explicitly given.
    var c = preset.bgColorGradient;
    if (!c)
    c = BackgroundPresets["Custom"];
    this.setClearColors(c[0], c[1], c[2], c[3], c[4], c[5]);

    //If allowed, display the environment as background (most likely the irradiance map will be used
    //by the AEC presets, so it will be almost like a color gradient)
    if (preset.useIrradianceAsBackground !== undefined) {
      if (this.api.prefs.hasTag('envMapBackground', 'ignore-producer')) {
        logger.debug('setLightPreset(): envMapBackground is locked. No changes.');
      } else {
        this.api.prefs.tag('no-storage', 'envMapBackground');
        this.api.setEnvMapBackground(preset.useIrradianceAsBackground);
      }
    }

    if (preset.path) {

      var pathPrefix = "res/environments/" + preset.path;
      var reflPath = getResourceUrl(pathPrefix + "_mipdrop." + (preset.type || "") + ".dds");
      var irrPath = getResourceUrl(pathPrefix + "_irr." + (preset.type || "") + ".dds");

      this.loadIrradianceMap(irrPath, preset.E_bias);
      this.loadCubeMap(reflPath, preset.E_bias);

      //Set exposure that the environment was baked with.
      //This has to be known at baking time and is applied
      //by the shader.
      this._materials.setEnvExposure(-preset.E_bias);
      this._useWebGPU && this._webglrender.getIBL().setEnvExposure(-preset.E_bias);
      this._renderer.setEnvExposure(-preset.E_bias);

      this.setTonemapExposureBias(preset.E_bias);
      this.setTonemapMethod(preset.tonemap);

      this.adaptGhostingBrightness();
    } else
    {
      var cubeMap = CreateCubeMapFromColors(this.clearColorTop, this.clearColorBottom);
      this._renderer.setCubeMap(cubeMap);
      this._materials.setIrradianceMap(null);
      // we might need to prevent this on 2D models
      this._materials.setReflectionMap(cubeMap);
      this._useWebGPU && this._webglrender.getIBL().setReflectionMap(cubeMap);

      this._useWebGPU && this._webglrender.getIBL().setIrradianceMap();

      //Set exposure that the environment was baked with.
      //This has to be known at baking time and is applied
      //by the shader.
      this._materials.setEnvExposure(-preset.E_bias || 0);
      this._useWebGPU && this._webglrender.getIBL().setEnvExposure(-preset.E_bias);
      this._renderer.setEnvExposure(-preset.E_bias || 0);

      this.setTonemapExposureBias(preset.E_bias || 0);
      this.setTonemapMethod(preset.tonemap || 0);

      this.adaptGhostingBrightness();

      this._renderer.toggleEnvMapBackground(this._envMapBackground);


      this.invalidate(true);
    }

    const dwfModels = this._renderScene?.getModels().filter((model) => {
      const documentNode = model?.getDocumentNode();
      const fileType = documentNode?.getInputFileType() || documentNode?.getRootNode()?.data?.name || '';
      return ['dwfx', 'dwf'].includes(fileType.toLowerCase());
    });
    if (dwfModels.length > 0) {
      dwfModels.forEach((model) => {
        this._materials.forEachInModel(model, false, (m) => {
          if (!m.emissiveOrig) {
            m.emissiveOrig = m.emissive;
          }
          m.emissive = m.emissiveOrig.clone().multiplyScalar(Math.pow(2.0, -preset.E_bias));
        });
      });
    }

    //To begin with, get the SAO defaults from the shader uniforms definition
    //Note the scaling we apply to inverse scaling done by the setAOOptions API internally.
    //This is not pretty....
    var saoRadius = SAOShader.uniforms.radius.value;
    var saoIntensity = SAOShader.uniforms.intensity.value;
    var saoBias = undefined;

    // The WebGL defaults for SAO don't work well with the WebGPU implementation. For now,
    // we just use different default values.
    // TODO: This only helps as long as the default values are actually used. When getting the values from settings
    //       or from client apps, the incompatiblity can still be a problem (see https://autodesk.atlassian.net/browse/VIZX-956)
    if (this._useWebGPU) {
      saoRadius = 8;
      saoIntensity = 1;
      saoBias = 0.25;
    }

    //Check if the preset overrides the SAO settings
    if (Object.prototype.hasOwnProperty.call(preset, "saoRadius"))
    saoRadius = preset.saoRadius;
    if (Object.prototype.hasOwnProperty.call(preset, "saoIntensity"))
    saoIntensity = preset.saoIntensity;

    if (this._useWebGPU) {
      this._renderer.setAOOptions(saoRadius, saoIntensity, saoBias);
    } else {
      // Note that the WebGL variant has different param (3rd is aoOpacity)
      this._renderer.setAOOptions(saoRadius, saoIntensity);
    }

    var lightIntensity = this._defaultLightIntensity;
    if (preset.lightMultiplier !== null && preset.lightMultiplier !== undefined) {
      lightIntensity = preset.lightMultiplier;
    }

    // init primary light direction used for shadows
    this._shadowLightDir.copy(this._shadowLightDirDefault);
    if (preset.lightDirection) {
      // The presets describe the direction away from the light, while _shadowLightDir
      // is the direction pointing to the light.
      this._shadowLightDir.fromArray(preset.lightDirection).negate();
    }

    // changing the shadow light direction invalidates the shadow-map
    if (this._shadowMaps) {
      this._invalidateShadowMap();
    }

    if (this.dir_light1) {
      this.dir_light1.intensity = lightIntensity;

      if (preset.lightDirection) {
        this.dir_light1.position.set(-preset.lightDirection[0], -preset.lightDirection[1], -preset.lightDirection[2]);
      } else {
        // set to default, otherwise the environment will inherit the direction from whatever previous environment was chosen
        this.dir_light1.position.copy(this._lightDirDefault);
      }

    }

    this._materials.setEnvRotation(preset.rotation || 0.0);
    this._renderer.setEnvRotation(preset.rotation || 0.0);

    if (this._groundReflection) this._groundReflection.setEnvRotation(preset.rotation || 0.0);

    // toggle lights on/off based on lightMultiplier
    this.toggleLights(lightIntensity !== 0.0);

    this.invalidate(true, false, true);

    this.fireRenderOptionChanged();

    // Call the callback
    callback && callback();
  }

  setLightPresetFromFile(model) {
    if (!model || model.is2d()) {
      return false;
    }

    let ignoreProducer;

    // TODO add more control for environments
    // the user cannot set anything expect the style from current UI
    // currently only the style can be selected.
    // TODO We cannot control these values so comment out for now
    var grndReflection = model.getMetadata('renderEnvironmentGroundReflection', 'value', null);
    ignoreProducer = this.api.prefs.hasTag(Prefs3D.GROUND_REFLECTION, 'ignore-producer');
    if (grndReflection !== null && !ignoreProducer) {
      this.api.prefs.tag('no-storage', Prefs3D.GROUND_REFLECTION);
      this.api.setGroundReflection(grndReflection);
    }

    var grndShadow = model.getMetadata('renderEnvironmentGroundShadow', 'value', null);
    ignoreProducer = this.api.prefs.hasTag(Prefs3D.GROUND_SHADOW, 'ignore-producer');
    if (grndShadow !== null && !ignoreProducer) {
      this.api.prefs.tag('no-storage', Prefs3D.GROUND_SHADOW);
      this.api.setGroundShadow(grndShadow);
    }

    var ambientShadows = model.getMetadata('renderEnvironmentAmbientShadows', 'value', null);
    ignoreProducer = this.api.prefs.hasTag(Prefs3D.AMBIENT_SHADOWS, 'ignore-producer');
    if (ambientShadows !== null && !ignoreProducer) {
      this.api.prefs.tag('no-storage', Prefs3D.AMBIENT_SHADOWS);
      // kludgey, but maintains previous API linking these two different algorithms together
      this.api.setQualityLevel(ambientShadows, this._renderer.getAntialiasing());
    }

    var displayLines = model.getMetadata('renderEnvironmentDisplayLines', 'value', null);
    ignoreProducer = this.api.prefs.hasTag(Prefs3D.LINE_RENDERING, 'ignore-producer');
    if (displayLines !== null && !ignoreProducer) {
      this.api.prefs.tag('no-storage', Prefs3D.LINE_RENDERING);
      this.api.hideLines(!displayLines);
    }

    var displayPoints = model.getMetadata('renderEnvironmentDisplayPoints', 'value', null);
    ignoreProducer = this.api.prefs.hasTag(Prefs3D.POINT_RENDERING, 'ignore-producer');
    if (displayPoints !== null && !ignoreProducer) {
      this.api.prefs.tag('no-storage', Prefs3D.POINT_RENDERING);
      this.api.hidePoints(!displayPoints);
    }

    var displayEdges = model.getMetadata('renderEnvironmentDisplayEdges', 'value', null);
    ignoreProducer = this.api.prefs.hasTag(Prefs3D.EDGE_RENDERING, 'ignore-producer');
    if (displayEdges !== null && !ignoreProducer) {
      this.api.prefs.tag('no-storage', Prefs3D.EDGE_RENDERING);
      this.api.setDisplayEdges(!isMobileDevice() && !!displayEdges);
    }

    var preset = null;
    ignoreProducer = this.api.prefs.hasTag(Prefs3D.LIGHT_PRESET, 'ignore-producer');
    if (!ignoreProducer) {
      var style = model.getMetadata('renderEnvironmentStyle', 'value', null);
      preset = LightPresets.filter(function (lightPreset) {
        return lightPreset.name === style;
      })[0];
    }
    if (preset) {
      this.api.prefs.tag('no-storage', Prefs3D.LIGHT_PRESET);

      // Create an env based on an existing preset
      // and add it at the end of the official list
      var env = ModelSettingsEnvironment;
      if (!env) {
        env = ModelSettingsEnvironment = {};
        LightPresets.push(env);
      }

      // Copy existing Preset into custom Model-Loaded preset
      copyLightPreset(preset, env);

      // Override Name for use in UI
      env.name = 'Custom Model defined';

      // Override Environment Exposure Values
      var exposureBias = model.getMetadata('renderEnvironmentExposureBias', 'value', null);
      var exposureBase = model.getMetadata('renderEnvironmentExposureBase', 'value', null);
      if (exposureBias !== null && exposureBase !== null) {
        env.E_bias = exposureBias + exposureBase;
      }

      // Override Environment Background Color
      // Note that there's a specific preset for background color
      var bgColor = model.getMetadata('renderEnvironmentBackgroundColor', 'value', null);
      ignoreProducer = this.api.prefs.hasTag(Prefs.BACKGROUND_COLOR_PRESET, 'ignore-producer');
      if (bgColor && !ignoreProducer) {
        env.bgColorGradient = [
        255.0 * bgColor[0], 255.0 * bgColor[1], 255.0 * bgColor[2],
        255.0 * bgColor[0], 255.0 * bgColor[1], 255.0 * bgColor[2]];

      }

      // Override Environment Rotation
      var envRotation = model.getMetadata('renderEnvironmentRotation', 'value', null); //assumed radians
      if (envRotation !== null) {
        env.rotation = envRotation;
      }

      var i = LightPresets.indexOf(env);
      this.setLightPreset(i, true);
    }

    var bgEnvironment = model.getMetadata('renderEnvironmentBackgroundFromEnvironment', 'value', null);
    ignoreProducer = this.api.prefs.hasTag(Prefs3D.ENV_MAP_BACKGROUND, 'ignore-producer');
    if (bgEnvironment !== null && !ignoreProducer) {
      this.api.prefs.tag('no-storage', Prefs3D.ENV_MAP_BACKGROUND);
      this.api.setEnvMapBackground(bgEnvironment);
    }

    // Important to return the model defined preset
    return preset;
  }

  setLightPresetForAec() {

    //Find the AEC light preset
    var presetName = getGlobal().DefaultLightPresetAec || "Boardwalk";
    var idx = -1;
    for (var i = 0; i < LightPresets.length; i++) {
      if (LightPresets[i].name === presetName) {
        idx = i;
        break;
      }
    }

    if (idx >= 0) {
      if (this.api.prefs.hasTag('lightPreset', 'ignore-producer')) {
        logger.debug('setLightPresetForAec(): lightPreset is locked. No changes.');
      } else {
        this.api.prefs.tag('no-storage', 'lightPreset');

        this.setLightPreset(idx, true, function () {
          //When AEC preset is set, we lock changes to the envMapBackground in order to prevent
          //setLightPresetFromFile from changing our settings. This is ugly, but it works around the
          //followng issue: Revit files do not set renderEnvironmentStyle in the metadata, which
          //indicates that we should not override any environment settings from the file.
          //However, Max files would like to have a null environment style with non-null specific overrides.
          //The issue comes that the other overrides have a default value of false in LMV, so
          //we mistakenly use those overrides to nuke the AEC settings that were set here.
          //So here, we lock the setting to prevent it from being nuked in setLightPresetFromFile.
          this.api.prefs.tag('ignore-producer', 'envMapBackground');
        }.bind(this));
        this.saveLightPreset();
      }
    }

    //If allowed, display edge topology
    if (this.api.prefs.hasTag('edgeRendering', 'ignore-producer')) {
      logger.debug('setLightPresetForAec(): edgeRendering is locked. No changes.');
    } else {
      this.api.prefs.tag('no-storage', 'edgeRendering');
      this.api.setDisplayEdges(!isMobileDevice());
    }

    return true;
  }


  setAOHeuristics(model) {

    //Decide on what SSAO settings to use.

    var metersPerModelUnit = model.getUnitScale();

    // First, check the metadata for explcit AO settings and use them if present.
    var aoRadius = model.getMetadata('renderEnvironmentAmbientShadows', 'radius', undefined);
    var aoIntensity = model.getMetadata('renderEnvironmentAmbientShadows', 'intensity', undefined);
    var aoOpacity = model.getMetadata('renderEnvironmentAmbientShadows', 'opacity', undefined);
    if (aoRadius !== undefined || aoIntensity !== undefined || aoOpacity !== undefined) {
      this._renderer.setAOOptions(aoRadius / metersPerModelUnit, aoIntensity, aoOpacity);
    } else if (model.isAEC()) {
      var largeRadius = metersPerModelUnit > 0.3;
      if (largeRadius) {
        //AEC model in meters or feet -- probably building
        //use room-sized radius
        this._renderer.setAOOptions(4.0 / metersPerModelUnit, 1.0, 0.625);
      } else {
        //AEC model in inches or cm -- most likely a factory floor with lots
        //of small things / pipes / nuts / bolts, use smaller radius.
        this._renderer.setAOOptions(0.25 / metersPerModelUnit, 1.0, 0.625);
      }
    } else {
      // Compute a rough size for the model, so that we can set a reasonable AO radius.
      // This simple approach is reasonable for mechanical models, but is probably too
      // large a value for architectural models, where the viewer is inside the model
      // and so the model itself is relatively large compared to the viewer.
      var bbox = model.getData().bbox;
      var diagonalLength = bbox.getSize(new THREE.Vector3()).length();

      // 10 works well as a default for most models, including
      // architectural scenes. Surprising! But, for small models,
      // where for some reason the model is not in "human-sized units",
      // 0.05 says the ambient occlusion should extend 5% of the
      // diagonal length of the model.
      // The 10 here should match the SAOShader.js radius of 10.
      this._renderer.setAOOptions(Math.min(10.0, 0.05 * diagonalLength));
    }


  }


  setTonemapMethod(index) {

    if (index == this._renderer.getToneMapMethod())
    return;

    this._renderer.setTonemapMethod(index);
    this._materials.setTonemapMethod(index);
    this._useWebGPU && this._webglrender.getIBL().setTonemapMethod(index);

    this.fireRenderOptionChanged();
    this.invalidate(true);
  }

  setTonemapExposureBias(bias) {

    if (bias == this._renderer.getExposureBias())
    return;

    this._renderer.setTonemapExposureBias(bias);
    this._useWebGPU && this._webglrender.getIBL().setExposureBias(bias);
    this._materials.setTonemapExposureBias(bias);

    this.fireRenderOptionChanged();
    this.invalidate(true);
  }

  setRenderingPrefsFor2D(is2D) {

    if (!isNodeJS()) {
      var value = is2D ? false : !!this.api.prefs.get('envMapBackground');
      this.toggleEnvMapBackground(value);
    }
  }

  /**
   * Unloads model, frees memory, as much as possible.
   */
  dtor() {
    this.stop();
    this.api.removeEventListener(et.MODEL_ROOT_LOADED_EVENT, this._stdSurfaceLoadListener);
    this.api.removeEventListener(et.MODEL_ROOT_LOADED_EVENT, this._onModelRootLoaded);
    this.api.removeEventListener(et.LOADER_REPAINT_REQUEST_EVENT, this._onLoaderRepaintRequest);

    // Unload models BEFORE unregistering from the OutOfCoreTileManager. Unloading fires
    // MODEL_REMOVED_EVENT, whose listener (OutOfCoreTileManager.#onModelRemoved) removes the
    // model from the process-wide modelManagerRegistry and its nodes from the shared stages.
    // Unregistering first would detach that listener, leaking destroyed models into the global
    // scheduler and poisoning the next viewer's promotion loop.
    this.unloadCurrentModel();

    if (USE_OUT_OF_CORE_TILE_MANAGER) {
      renderBudgetManager.unregisterViewer(this);
      OutOfCoreTileManager.unregisterViewer(this);
    }

    // this.controls is uninitialized by Viewer3D, since it was initialized there
    this.controls = null;
    this.canvas = null;
    clearInterval(this._interval);

    this.loader = null;

    this.selector.dtor();
    this.selector = null;

    this.model = null;
    this.layers = null;
    this.visibilityManager = null;

    if (this._geomCache) {
      this._geomCache.removeViewer(this.api);
      this._geomCache = null;
    }

    this._renderScene = null;

    this._renderer?.cleanup();
    this._renderer = null;

    this._materials.refCount--;

    if (this._materials.refCount === 0) {
      this._materials.dtor();
    }

    this._materials = null;

    this._groundShadow = null;
    this._groundReflection = null;

    if (this._webglrender) {
      this._webglrender.refCount--;

      this._webglrender.removeEventListener(LMVRenderer.Events.WEBGL_CONTEXT_LOST, this.onGpuContextLost);
      this._webglrender.removeEventListener(LMVRenderer.Events.WEBGL_CONTEXT_RESTORED, this.onWebGLcontextRestored);

      if (this._webglrender.refCount === 0) {
        if (!USE_WEBGPU) {
          let context = this._webglrender.getContext();
          this._webglrender.dtor();

          if (isChrome() && isMac()) {
            // This is a workaround for a Chrome bug on Mac where not all memory is freed
            // when the viewer is being closed (even though we released the buffers). By
            // losing the context we enforce freeing all memory.
            context?.getExtension('WEBGL_lose_context')?.loseContext();
          }
        }
        this._webglrender.domElement = null;
        this._webglrender.context = null;
      }

      this._webglrender = null;
    }
  }

  hideLines(hide) {
    if (this._renderScene && !this._renderScene.isEmpty()) {
      this._renderScene.hideLines(hide);
      this.sceneUpdated(true);
    }
  }

  hidePoints(hide) {
    if (this._renderScene && !this._renderScene.isEmpty()) {
      this._renderScene.hidePoints(hide);
      this.sceneUpdated(true);
    }
  }

  setPointSizeOverride(value) {
    // Point size override is only supported in WebGPU.
    if (this._useWebGPU) {
      this._webglrender.setPointSizeOverride(value);
      this.invalidate(true);
    }
  }

  setDisplayEdges(show) {
    this._renderer.toggleEdges(show);
    this.updatePolygonOffset();
    this.invalidate(true);
  }

  updatePolygonOffset() {
    const showEdges = this.api.prefs.get(Prefs3D.EDGE_RENDERING);
    // as an optimization, PO is disabled if edges are disabled and we're sure no model has lines.
    // See also GEOMETRY_LOADED_EVENT listener in Viewer3D.js.
    const allModels = this._renderScene.getModels();
    const needsPO = showEdges || allModels.length === 0 || allModels.some((model) => !model.isLoadDone() || model.getData().hasLines);

    if (needsPO !== this._materials.getPolygonOffsetOn()) {
      this._materials.togglePolygonOffset(needsPO);
      this._renderer.setDepthMaterialOffset(this._materials.getPolygonOffsetOn(), this._materials.getPolygonOffsetFactor(), this._materials.getPolygonOffsetUnits());
    }
  }

  /**
   * Sets surface materials to double sided or single sided.
   * @param {boolean} enable - sets materials to double sided if set to true.
   * @param {Autodesk.Viewing.Model} model - model instance
   * @param {boolean} [update=true] - Updates the scene
   */
  setDoubleSided(enable, model, update = true) {
    model = model || this.model;

    // Double sided materials will only be set to 3d models.
    if (model.is2d()) {
      return;
    }

    const modelData = model.getData();

    // Do not apply if the model data is not available.
    if (!modelData) {
      return;
    }

    // Sets surface materials to either double sided or single sided
    this.matman().setDoubleSided(enable, model);
    update && this.sceneUpdated();
  }

  getAllCutPlanes() {
    // create array of the planes by combining all cut plane sets
    var allPlanes = undefined;
    for (var key in this._cutPlaneSets) {
      var cps = this._cutPlaneSets[key];
      if (cps && cps.length) {
        if (!allPlanes) {
          allPlanes = cps;
        } else if (key === this._cutPlaneSetFor2DRendering) {
          // UnitsPerPixel only consider the first cutplane. So, this one must go first.
          allPlanes = cps.concat(allPlanes);
        } else {
          // append cutplanes
          allPlanes = allPlanes.concat(cps);
        }
      }
    }
    return allPlanes;
  }

  setCutplanesHideInterior(enabled) {
    this._cutPlaneHideInterior = Boolean(enabled);
  }

  cutplanesHideInteriorEnabled() {
    return this._cutPlaneHideInterior;
  }

  // Set cutplane array by combining the cutplanes specified by different tools
  updateCutPlanes() {
    var allPlanes = this.getAllCutPlanes();
    this.setCutPlanes(allPlanes);
  }


  /**
   * A cutplane set is an array of cutplanes that can be controlled individually by a single tool
   * without affecting other tools' cutplanes.
   *  @param {string} cutPlaneSetName
   *  @param {Vector4[]|null} [planes]
   *  @param {Boolean} [fireEvent] - if set to false the av.CUTPLANES_CHANGE_EVENT event will not be fired.
   *  @param {Boolean|undefined} [isSectionBox] - to set 6 planes as a box. When undefined, SmartSection
   * will try to create a box from 6 planes automatically.
   */
  setCutPlaneSet(cutPlaneSetName, planes, fireEvent = true, isSectionBox) {
    if (cutPlaneSetName === '__set_view') {
      const sectionExt = this.api.getExtension('Autodesk.SmartSection');
      if (sectionExt) {
        sectionExt.fromViewerDefaultCutplaneSet(planes, fireEvent, isSectionBox);
        return;
      }
    }

    // store copy of plane array
    this._cutPlaneSets[cutPlaneSetName] = planes ? planes.slice() : undefined;
    if (fireEvent) {
      this.updateCutPlanes();
    } else {
      var allPlanes = this.getAllCutPlanes();
      this.setCutPlanesInScene(allPlanes);
    }
  }

  /** Defines which cutplane is used to adjust 2D rendering. This is used by SectionTool
   * to make sure that 2D rendering resolution is properly adjusted for its cutplane.
   *  @param {string} cutPlaneSetName */
  setCutPlaneSetFor2DRendering(cutPlaneSetName) {
    this._cutPlaneSetFor2DRendering = cutPlaneSetName;
    this.updateCutPlanes();
  }

  getCutPlaneSet(cutPlaneSetName) {
    return this._cutPlaneSets[cutPlaneSetName] || [];
  }

  /* @returns {string[]} names - names of all active (non-empty) cutplane sets. */
  getCutPlaneSets() {
    var result = [];
    for (var key in this._cutPlaneSets) {
      var cp = this._cutPlaneSets[key];
      if (cp && cp.length) {
        result.push(key);
      }
    }
    return result;
  }

  getCutPlanes() {
    return this._materials.getCutPlanes();
  }

  /**
   * Sets the material cutplanes and updates the scene.
   * This function does not fire the Autodesk.Viewing.CUTPLANES_CHANGE_EVENT
   * @see Viewer3DImpl#setCutPlanes
   */
  setCutPlanesInScene(planes) {
    this._renderer.toggleTwoSided(this._materials.setCutPlanes(planes, this._cutPlaneHideInterior ? 1 : 0));
    this._useWebGPU && this._renderer.setCutPlanes(planes);
    this.sceneUpdated();
  }

  setCutPlanes(planes) {
    this.setCutPlanesInScene(planes);
    this.api.dispatchEvent({ type: et.CUTPLANES_CHANGE_EVENT, planes: planes });
  }

  fireRenderOptionChanged() {

    //If SAO is changing and we are using multiple
    //render targets in the main material pass, we have
    //to update the materials accordingly.
    this._materials.toggleMRTSetting(this._renderer.mrtFlags());

    this.api.dispatchEvent({ type: et.RENDER_OPTION_CHANGED_EVENT });
  }

  viewportToRay(vpVec, ray, camera = this.camera) {
    return camera.viewportToRay(vpVec, ray);
  }

  /**
   *
   * @param {THREE.Ray} ray               - Ray to intersect from
   * @param {bool} [ignoreTransparent]    - Shoot trough transparent objects.
   * @param {number[]|number[][]} [dbIds] - Optional filter of dbIds to be considered for testing. See RenderModel.rayIntersect().
   *                                      - If modelIds is set, dbIds[i] must provide a separate dbId array for modelIds[i].
   * @param {number[]} [modelIds]         - Optional list of modelIds to be considered for rayIntersection. (default is to consider all)
   *                                      - Render-layer models (RenderLayerManager.addModel()) are scoped by modelIds/dbIds too.
   * @param {Array} [intersections]       - Optional return array with all found intersections.
   * @param {Object} [options]            - Optional Rayintersection options:
   * @param {function} [options.filter]           - Filter function (hitResult) => bool. (see VBIntersector for hitresult content)
   * @param {bool}     [options.ignore2dModelsOn3d]   - Whether to ignore 2d models when in 3d mode.
   * @returns {Object|null} Intersection result object (see RenderModel.rayIntersect)
   */
  rayIntersect(ray, ignoreTransparent, dbIds, modelIds, intersections, options = {}) {

    // If the ray is not finite, return null.
    // Note that a ray with NaN values in the direction may freeze the viewer for a long time, because
    // it accepts all boxes as "hit" and does per triangle-checks for the entire scene.
    if (!ray.origin.isFinite() || !ray.direction.isFinite()) {
      console.warn('Trying hit test with invalid ray: ', ray);
      return null;
    }

    const getDbIdAtPointFor2D = (point) => {
      const vpVec = new THREE.Vector3().copy(point);
      vpVec.project(this.camera);
      const res = [];
      this._renderer.idAtPixel(vpVec.x, vpVec.y, res);

      return res;
    };

    // The caller's original scope, captured before ignore2dModelsOn3d rewrites modelIds below.
    // Layer-model ids aren't in get3DModels(), so we filter them against this, not the rewrite.
    const callerModelIds = modelIds;

    if (options.ignore2dModelsOn3d) {
      const model3DIds = this.get3DModels().map((m) => m.id);
      modelIds = modelIds ? modelIds.filter((id) => model3DIds.includes(id)) : model3DIds;
    }

    if (this.camera && this.camera.clientHeight > 0) {
      const cam = this.camera;
      // Compute scaling factors that are needed to convert from world space units to
      // screen space. This is used during ray-casting to decide whether a point or line is within
      // their on-screen pixel size (i.e. we are using a cone/cylinder for the intersection computation
      // and radius / angle depends on screen space resolution)
      options[screenPickInfo] = {
        isPerspective: cam.isPerspective,
        unitsPerPixelFactor: cam.isPerspective ?
        2 * Math.tan(THREE.Math.degToRad(0.5 * cam.fov)) / cam.clientHeight :
        cam.orthoScale / cam.clientHeight,
        hitMarginPixels: RAY_INTERSECT_HIT_MARGIN_PIXELS // extra leeway in pixels to improve the picking UX for points / thin lines.
      };
    }

    var result = this._renderScene.rayIntersect(ray.origin, ray.direction, ignoreTransparent, dbIds, modelIds, intersections, getDbIdAtPointFor2D, options);

    const extraScenes = [];
    const tmpSize = new THREE.Vector3();

    // Intersect all non-overlay compositing layers (including legacy scene and sceneAfter)
    // and the SceneAPI models attached to them via RenderLayerManager.addModel().
    const mainLayers = this._renderLayerManager.getMainPassRenderLayers();
    const nonOverlayLayers = [...mainLayers.before, ...mainLayers.after];
    // Allocated lazily on first use, since most viewers have no render layers.
    let raycaster = null;

    for (const layer of nonOverlayLayers) {
      if (layer.scene) {
        extraScenes.push(layer.scene);
      }

      for (const layerModel of layer.models) {
        // Layer models bypass the get3DModels() rewrite above, so honor ignore2dModelsOn3d here.
        if (options.ignore2dModelsOn3d && layerModel.is2d()) {
          continue;
        }

        // Scope layer models by modelIds/dbIds like RenderScene does for regular models:
        // when scoped, skip non-matching models and pass the parallel dbIds[i] for matches.
        let modelDbIds = dbIds;
        if (callerModelIds) {
          const idx = callerModelIds.indexOf(layerModel.id);
          if (idx === -1) {
            continue;
          }
          modelDbIds = dbIds && dbIds[idx];
        }
        raycaster ??= new THREE.Raycaster(ray.origin, ray.direction, this.camera.near, this.camera.far);
        const hit = layerModel.rayIntersect(raycaster, ignoreTransparent, modelDbIds, intersections, getDbIdAtPointFor2D, options);
        if (hit && (!result || hit.distance < result.distance)) {
          result = hit;
        }
      }
    }

    for (let i = 0; i < extraScenes.length; i++) {
      let scene = extraScenes[i];

      if (scene.doNotIntersect) {
        continue;
      }

      if (scene.children.length) {
        raycaster ??= new THREE.Raycaster(ray.origin, ray.direction, this.camera.near, this.camera.far);
        this._computeObjectBounds(_bounds, scene);
        //TODO: This math approximately matches the heuristic in RenderScene.recomputeLinePrecision
        //but it applies per scene. It might be good to unify the two once we know this works well.
        raycaster.params.Line.threshold = Math.min(1.0, _bounds.getSize(tmpSize).length() * 0.5 * 0.001);
        raycaster.params.PointCloud.threshold = raycaster.params.Line.threshold;

        var intersects = intersections || [];
        VBIntersector.intersectObject(scene, raycaster, intersects, true);

        if (intersects.length) {
          const intersectRes = intersects[0];

          if (!result || intersectRes.distance < result.distance) {
            if (intersectRes.modelId !== undefined) {
              intersectRes.model = this.findModel(intersectRes.modelId);
            }
            result = intersectRes;
          }
        }
      }
    }

    if (!result)
    return null;

    if (result.dbId === undefined && result.fragId !== undefined /* 0 is a valid fragId */) {

      result.dbId = result.model.getFragmentList().getDbIds(result.fragId);

      if (!result.model.getData().instanceTree) {
        //Case where there is no dbid to fragment id map. Create a 'virtual' node
        //with node Id = fragment Id, so that selection works like
        //each scene fragment is a scene node by itself.
        result.dbId = result.fragId;
      }
    }

    result.intersectPoint = result.point; // Backwards compatibility

    return result;
  }

  castRayViewport(vpVec, ignoreTransparent, dbIds, modelIds, intersections, options) {
    if (!this._renderScene) {
      return {};
    }

    this.viewportToRay(vpVec, _ray);

    return this.rayIntersect(_ray, ignoreTransparent, dbIds, modelIds, intersections, options);
  }

  /**
   * @return {DOMRect}
   */
  getCanvasBoundingClientRect() {
    if (this.canvasBoundingclientRectDirty) {
      this.canvasBoundingclientRectDirty = false;
      this.boundingClientRect = this.canvas.getBoundingClientRect();
    }
    return this.boundingClientRect;
  }

  /**
   * Converts client (pixel) coordinates - top-left (0, 0), bottom-right (width, height) -
   * to normalized viewport coordinates  - top-left (-1, 1, 1), bottom-right (1, -1, 1).
   *
   * @returns {THREE.Vector3} Viewport coordinates. Top-left is (-1, 1, 1), bottom-right is (1, -1, 1).
   */
  clientToViewport(clientX, clientY) {
    var rect = this.getCanvasBoundingClientRect();
    return new THREE.Vector3(
      (clientX + 0.5) / rect.width * 2 - 1,
      -((clientY + 0.5) / rect.height) * 2 + 1, 1);
  }

  /**
   * Converts normalized viewport coordinates  - top-left (-1, 1), bottom-right (1, -1) -
   * to client (pixel) coordinates - top-left (0, 0), bottom-right (width, height).
   *
   * @returns {THREE.Vector3} Client (pixel) coordinates. Top-left (0, 0, 0), bottom-right (width, height, 0).
   */
  viewportToClient(viewportX, viewportY) {
    var rect = this.getCanvasBoundingClientRect();
    return new THREE.Vector3(
      (viewportX + 1) * 0.5 * rect.width - 0.5,
      (viewportY - 1) * -0.5 * rect.height - 0.5, 0);
  }

  castRay(clientX, clientY, ignoreTransparent, options) {
    // Use the offsets based on the client rectangle, which is relative to the browser's client
    // rectangle, unlike offsetLeft and offsetTop, which are relative to a parent element.
    //
    return this.castRayViewport(this.clientToViewport(clientX, clientY), ignoreTransparent, undefined, undefined, undefined, options);
  }

  // Note: The camera world matrix must be up-to-date
  intersectGroundViewport(vpVec) {

    var worldUp = "z";

    //In 2D mode, the roll tool can be used to change the orientation
    //of the sheet, which will also set the world up vector to the new orientation.
    //However, this is not what we want in case of a 2d sheet -- its ground plane is always Z.
    //TODO: It's not clear if checking here or in setWorldUp is better. Also I don't see
    //a way to generalize the math in a way to make it work without such check (e.g. by using camera up only).
    if (!this.is2d) {
      worldUp = this._worldUpName;
    }

    var modelBox = this.model && this.model.getBoundingBox();
    return SceneMath.intersectGroundViewport(vpVec, this.camera, worldUp, modelBox);
  }

  intersectGround(clientX, clientY) {
    return this.intersectGroundViewport(this.clientToViewport(clientX, clientY));
  }

  _2dHitTestViewport(vpVec, searchRadius, minPixelId) {
    const _idRes = [0, 0]; // idAtPixels will write the result into this array.
    const pixelId = this._renderer.idAtPixels(vpVec.x, vpVec.y, searchRadius, _idRes);
    if (pixelId < minPixelId)
    return null;

    const model = this._renderScene.findModel(_idRes[1]) || this.model;
    if (!model)
    return null;

    //Note this function will destructively modify vpVec,
    //so it's unusable after that.
    const point = this.intersectGroundViewport(vpVec);

    // get fragment ID if there is a fragment list
    const fragments = model.getData().fragments;
    const fragId = fragments ? fragments.dbId2fragId[pixelId] : -1;

    return {
      intersectPoint: point,
      dbId: model.remapDbIdFor2D(pixelId),
      fragId: fragId,
      model: model
    };
  }

  // Filter to exclude 3D line hit tests if they are outside the given searchRadius
  _getSearchRadiusFilter(searchRadius) {
    return (hit) => {
      // We only care for line hits. Note that we exclude wideLines here as well, because
      // (unlike regular ones), they have a true world-space width.
      const isLine = hit && hit.object && hit.object.isLine;
      if (!isLine) {
        return true;
      }

      // Exclude its if projected distance from ray is beyond search radius
      const unitsPerPixel = 1.0 / this.camera.pixelsPerUnitAtDistance(hit.distance);
      const maxWorldDist = searchRadius * unitsPerPixel;
      return hit.distanceToRay < maxWorldDist;
    };
  }

  hitTestViewport(vpVec, ignoreTransparent, dbIds, modelIds, intersections, options) {
    let result;

    if (this.is2d) {
      const searchRadius = isMobileDevice() ? 45 : 5;
      result = this._2dHitTestViewport(vpVec, searchRadius, 1);
    } else
    {
      result = this.castRayViewport(vpVec, ignoreTransparent, dbIds, modelIds, intersections, options);
    }

    return result;
  }

  hitTest(clientX, clientY, ignoreTransparent, dbIds, modelIds, options) {

    return this.hitTestViewport(this.clientToViewport(clientX, clientY), ignoreTransparent, dbIds, modelIds, undefined, options);
  }

  hitBoxTestViewport(vpVec, widthRatio, heightRatio) {
    const results = [];
    const ids = [];
    this._renderer.idsAtPixelsBox(vpVec.x, vpVec.y, widthRatio, heightRatio, ids);

    for (let i = 0; i < ids.length; i++) {
      const model = this._renderScene.findModel(ids[i][1]) || this.model;
      if (model) {
        const dbId = model.remapDbIdFor2D(ids[i][0]);
        results.push({ dbId, model });
      }
    }

    return results;
  }

  snappingHitTestViewport(vpVec, ignoreTransparent) {
    let result;

    //Notice: The amount of pixels per line should correspond to pixelSize in setDetectRadius of Snapper.js,
    //the shape of detection area is square in idAtPixels, but circle in snapper, should make their areas match roughly.
    const searchRadius = isMobileDevice() ? 45 : 17;

    if (this.is2d) {
      if (this.model && this.model.isLeaflet()) {// Assumming that in 2d there's only one model
        let point = this.intersectGroundViewport(vpVec);
        result = { intersectPoint: point };
      } else {
        result = this._2dHitTestViewport(vpVec, searchRadius, 0);
      }
    } else {// Is 3d
      const res = [];
      const dbId = this._renderer.idAtPixels(vpVec.x, vpVec.y, searchRadius, res);

      // Adjust vp position according to hit.
      if (res[2] && res[3]) {
        vpVec.setX(res[2]);
        vpVec.setY(res[3]);
      }

      const options = {
        filter: this._getSearchRadiusFilter(searchRadius)
      };
      result = this.castRayViewport(vpVec, ignoreTransparent, dbId > 0 ? [dbId] : null, undefined, undefined, options);
    }

    return result;
  }

  // Used for snapping
  // firstly, find the intersect object using pre-computed ID buffer
  // secondly, find the intersect point and face using intersection test
  snappingHitTest(clientX, clientY, ignoreTransparent) {

    return this.snappingHitTestViewport(this.clientToViewport(clientX, clientY), ignoreTransparent);
  }

  /**
   * Clears the current highlighted object.
   */
  clearHighlight() {
    this._renderer.rolloverObjectId(-1);
    this.invalidate(false, false, true);
  }

  rollOverIdChanged() {
    this.api.fireEvent({
      type: et.OBJECT_UNDER_MOUSE_CHANGED,
      dbId: this._renderer.getRollOverDbId(),
      modelId: this._renderer.getRollOverModelId()
    });
    this.invalidate(false, false, true);
  }

  //Used for rollover highlighting using pre-computed ID buffer
  rolloverObjectViewport(vpVec) {

    // Handles BLMV-5652, we falsely continue to highlight PDF if underlayRaster exists
    if (!this.model) {
      return;
    }

    if (this.is2d && this.model && (
    this.model.isLeaflet() ||
    this.model.isPdf(true) && this.api.prefs.get(Prefs2D.DISABLE_PDF_HIGHLIGHT)))

    {
      return;
    }

    const _idRes = [];

    // Disable highlight for dbids that have a selection lock.
    const dbId = this._renderer.idAtPixel(vpVec.x, vpVec.y, _idRes);

    if (this.selector && this.selector.isNodeSelectionLocked(dbId, this.model)) {
      this.clearHighlight();
      return;
    }

    const modelId = _idRes[1];
    const hoveredModel = this.findModel(modelId);

    // Since Leaflet has only one fragment, highlight it entirely.
    if (hoveredModel?.isLeaflet()) {
      if (this._renderer.rollOverModelId(modelId)) {
        this.rollOverIdChanged();
      }

      return;
    }

    // RCS point cloud fragments use a single dbId. As highlighting the whole point cloud can be distracting,
    // we suppress the rollover highlight for them.
    // NOTE: hoveredModel is null on WebGL if the modelId buffer is not available (single models / mobile devices).
    // In the multi-model cases where we can't identify the model, the point cloud highlight will not be suppressed.
    const isSingleOrNoModel = this._renderScene.getModels().length <= 1;
    const rcsCandidate = isSingleOrNoModel ? this.model : hoveredModel;
    if (dbId > 0 && rcsCandidate?.isRCS()) {
      this.clearHighlight();
      return;
    }

    // Otherwise, highlight the hovered object only.
    if (this._renderer.rolloverObjectViewport(vpVec.x, vpVec.y)) {
      this.rollOverIdChanged();
    }
  }

  rolloverObject(clientX, clientY) {

    if (!this.selector.highlightPaused && !this.selector.highlightDisabled)
    this.rolloverObjectViewport(this.clientToViewport(clientX, clientY));
  }

  //This method is intended to be used by Tools
  pauseHighlight(disable) {

    this.selector.highlightPaused = disable;
    if (disable) {
      this.clearHighlight();
    }
  }

  disableHighlight(disable) {

    this.selector.highlightDisabled = disable;
    if (disable) {
      this.clearHighlight();
    }
  }

  disableSelection(disable) {

    this.selector.selectionDisabled = disable;
  }

  // Downloading all the relevant leaflet tiles might take time, so canceling it in the middle should be an option.
  // Using this API, the screenshot calculation will be canceled.
  // Note that this function is only for the old getScreenShot function. getScreenShotProgressive() returns a control object for canceling.
  cancelLeafletScreenshot() {
    this.api.dispatchEvent({ type: et.CANCEL_LEAFLET_SCREENSHOT });
  }

  // See ScreenShot.js for documentation
  getScreenShotProgressive(w, h, onFinished, options) {
    return ScreenShot.getScreenShot(w, h, onFinished, options, this);
  }

  //This accessor is only used for debugging purposes a.t.m.
  modelQueue() {return this._renderScene;}

  //This accessor is only used for debugging purposes a.t.m.
  _getRcs() {return this._rcs;}

  glrenderer() {return this._webglrender;}

  renderer() {return this._renderer;}

  setGeomCache(geomCache) {
    this._geomCache = geomCache;
    this._geomCache.addViewer(this.api);
  }

  /**
   * Returns the Geom Cache
   * @param {boolean} create - If true, creates a new cache if it doesn't exist
   * @returns {OtgResourceCache} - The Geom Cache
   */
  geomCache(create = true) {
    // OtgResourceCache should be generated only when OTG loader starts to request for geometries.
    // Otherwise, there is a bug related to the web-workers creation time: https://git.autodesk.com/A360/firefly.js/pull/4163.
    if (!this._geomCache && create) {
      this.setGeomCache(new OtgResourceCache(this.api.config));
    }

    return this._geomCache;
  }

  // only for debugging purposes
  shadowMaps() {return this._shadowMaps;}

  worldUp() {return this._worldUp;}
  worldUpName() {return this._worldUpName;}

  setUserRenderContext(ctx, isInitialized) {
    this._renderer = ctx ? ctx : new RenderContext();
    if (!isInitialized) {
      this._renderer.init(this._webglrender, this.canvas.clientWidth, this.canvas.clientHeight);
      this._renderer.setClearColors(this.clearColorTop, this.clearColorBottom);
    }

    this.invalidate(true);
    this.sceneUpdated(false); //to reset world boxes needed by new RenderContext for shadows, etc
  }

  setUserGroundShadow(groundShadow) {
    var replaced = this._groundShadow;
    this._groundShadow = groundShadow;
    return replaced; // Return GroundShadow object that we replaced.
  }

  invalidate(needsClear, needsRender, overlayDirty) {
    this._needsClear || (this._needsClear = needsClear);
    this._needsRender || (this._needsRender = needsRender);
    this._overlayDirty || (this._overlayDirty = overlayDirty);
  }

  // needed for command system
  isOverlayDirty() {
    return this._overlayDirty;
  }
  clearOverlayDirtyFlag() {
    this._overlayDirty = false;
  }


  sceneUpdated(objectsMoved, skipRepaint, skipFireEvent, skipBBoxUpdate) {

    this.invalidate(!skipRepaint, false, !skipRepaint);

    // Mark the scene bounds for update
    if (this._renderScene && objectsMoved && !skipBBoxUpdate) {
      this._renderScene.invalidateVisibleBounds();
      this.zoomBoundsChanged = true;
    }

    this._sceneDirty = true;

    this._invalidateShadowMap();

    if (!skipFireEvent) {
      this.api.fireEvent({ type: et.SCENE_UPDATED_EVENT, objectsMoved });
    }
  }

  // immediately restart rendering, make it interruptible like progressive, displaying only when done
  requestSilentRender() {
    this._deferredSilentRender = this._immediateSilentRender = true;
  }

  // restart rendering only when the previous render is done, make it interruptible like progressive, itself displaying only when done
  requestDeferredSilentRender() {
    this._deferredSilentRender = true; // but not immediate
  }

  currentLightPreset() {return this._currentLightPreset;}

  /**
   * @private
   */
  saveLightPreset() {
    this._oldLightPreset = this._currentLightPreset;
  }

  matman() {return this._materials;}

  fps() {return 1000.0 / this._frameTimeAvg;}

  setFPSTargets(min, target, max) {
    this._rcs.setFPSTargets(min, target, max);
    if (USE_WEBGPU) {
      this._webglrender.setFpsTarget(target);
    }
  }

  //@deprecated This is only used by CrossFadeEffects and probably shouldn't be used in new code
  getTargetRenderBudget() {
    return this._rcs.targetRenderBudget;
  }

  //@deprecated This is only used by CrossFadeEffects and probably shouldn't be used in new code
  setTargetRenderBudget(newTargetRenderBudget) {
    this._rcs.setTargetRenderBudget(newTargetRenderBudget);
  }

  // @deprecated This is only used by CrossFadeEffects and probably shouldn't be used in new code.
  // Sets the render budget from a desired frame time in milliseconds. With the batch-based
  // RenderBudgetManager the value is estimated from the last measured slope and frame time.
  setTargetFrameBudget(newTargetFrameBudget) {
    this._rcs.setTargetFrameBudget(newTargetFrameBudget);
  }

  _updateTargetRenderBudget(newTargetRenderBudget, newRenderBudgetFraction) {
    this._rcs.updateTargetRenderBudget(newTargetRenderBudget, newRenderBudgetFraction);
  }


  _getMaxTaskProcessingTime() {
    if (USE_OUT_OF_CORE_TILE_MANAGER) {
      return this._rcs.getRemainingFrameTime();
    }
    return this._rcs.MAX_RENDER_BUDGET - this._rcs.TARGET_FRAME_TIME;
  }

  //========================================================================


  // Record fragments transformation in explode mode for RaaS rendering
  //this.fragTransformConfig = [];

  track(event) {
    logger.track(event);
  }

  worldToClient(point, camera = this.camera) {
    var p = new THREE.Vector4(point.x, point.y, point.z, 1);
    p.applyMatrix4(camera.matrixWorldInverse);
    p.applyMatrix4(camera.projectionMatrix);

    // Don't want to mirror values with negative z (behind camera)
    if (p.w > 0) {
      p.x /= p.w;
      p.y /= p.w;
      p.z /= p.w;
    }

    return this.viewportToClient(p.x, p.y);
  }

  clientToWorld(clientX, clientY, ignoreTransparent, ignore2dModelBounds, ignore2dModelsOn3d) {

    var result = null;

    if (this.is2d) {
      var model = this.model;
      var modelData = model?.getData();

      var collision = this.intersectGround(clientX, clientY);
      if (collision) {
        collision.z = 0;
        if (ignore2dModelBounds || modelData.hidePaper || modelData.bbox.containsPoint(collision)) {
          result = {
            point: collision,
            model: model
          };
        }
      } else if (ignore2dModelBounds) {
        // clientToWorld() should usually never return null for 2d and ignore2dModelBounds=true,
        // particularly because the view direction is normally supposed to be orthogonal to the sheet plane.
        //
        // This section is only entered in the rare edge case that the view direction is orthogonal to the plane.
        // To avoid an exception for that case (https://sentry.io/organizations/fluent-y0/issues/2392259972/?referrer=slack)
        // we just fall back by using camera-position x/y, which corresponds to projecting the camera position to the sheetplane z=0.
        result = { point: this.camera.position.clone(), model };
      }
    } else {
      // hitTest handles multiple scenes
      result = this.hitTest(clientX, clientY, ignoreTransparent, undefined, undefined, { ignore2dModelsOn3d });
      if (result) {
        result.point = result.intersectPoint; // API expects attribute point to have the return value too.
      }
    }

    return result;
  }

  /**
   * Sets selection highlight color and opacity for 2D models
   * @param {THREE.Color} color
   * @param {number} opacity
   */
  set2dSelectionColor(color, opacity) {
    this.matman().set2dSelectionColor(color, opacity);
    this.invalidate(false, false, true /* overlay */);
  }

  /**
   * Sets selection highlight color for 3D models
   * @param {THREE.ColorRepresentation} color
   * @param {number} selectionType
   */
  setSelectionColor(color, selectionType) {
    selectionType = selectionType || SelectionType.MIXED;
    var emissive = new THREE.Color(color);
    emissive.multiplyScalar(0.5);

    const setColors = (material) => {
      material.color.set(color);
      material.emissive.set(emissive);
      material.variants?.forEach(setColors);
      this._materials.syncOverridePointMaterial(material);
    };

    // Flags to decide which selection colors to update
    let regular = false;
    let overlayed = false;

    switch (selectionType) {
      default:
      case SelectionType.MIXED:
        regular = true;
        overlayed = true;
        break;
      case SelectionType.REGULAR:
        regular = true;
        break;
      case SelectionType.OVERLAYED:
        overlayed = true;
        break;
    }

    if (overlayed) {

      // WebGL uses 2 override materials for overlay highlighting - both need to be updated
      setColors(this.selectionMaterialBase); // Base highlighting for visible (parts of) objects
      setColors(this.selectionMaterialTop); // Rendered on top - only for occluded object parts

      // For WebGPU, it's fully handled by the renderer.
      // Note: WebGL uses this call too to forward the color to the BlendShader.
      if (USE_WEBGPU) {
        this._renderer.setSelectionColorOverlay(color);
      } else {
        this._renderer.setSelectionColor(color);
      }

      // invalidate overlays
      this.invalidate(false, false, true);
    }

    if (regular) {
      if (USE_WEBGPU) {
        this._renderer.setSelectionColorRegular(color);
      } else {
        setColors(this.highlightMaterial);
      }

      // invalidate main rendering
      this.invalidate(true);
    }
  }

  // Update the viewport Id for the first selection in 2d measure
  updateViewportId(vpId) {
    this._materials.updateViewportId(vpId);
    this.invalidate(true);
  }

  /**
   * Find model based on modelId, BubbleNode, or filter function.
   *  @param {number|av.BubbleNode|function(av.Model)} value
   *  @param {boolean}[includeHidden] - By default, we only consider visible models for search
   *  @returns {RenderModel|null}
   */
  findModel(value, includeHidden) {

    // define filter function
    let filter;
    if (typeof value == 'number') {
      filter = (m) => m.id == value;
    } else if (value instanceof BubbleNode) {
      filter = (m) => m.getDocumentNode() == value;
    } else {
      if (!value) {
        return null;
      }

      filter = value; // value must be a filter function already
    }

    // Search visible models
    let model = this._renderScene.getModels().find(filter);

    // Optional: Search hidden models
    if (includeHidden && !model) {
      model = this._renderScene.getHiddenModels().find(filter);
    }

    return model;
  }

  /**
   *  get frame rate for progressive rendering, i.e, how many ticks go by before an update occurs
   *  @returns {number}
   */
  getFrameRate() {
    return this._rcs.frameDisplayRate;
  }

  /**
   * set frame rate for progressive rendering, i.e, how many ticks go by before an update occurs
   *  @param   {number} rate
   */
  setFrameRate(rate) {
    // don't let rate < 1, just in case user sets 0.
    this._rcs.frameDisplayRate = rate < 1 ? 1 : rate;
  }

  /**
   *  For shadow casting, we assume a single directional light. Shadow light direction is the direction
   *  that this light comes from, i.e., shadows are casted to the opposite direction.
   *  This function changes the direction and triggers a shadow update.
   *
   *  Note that the directional light source is only assumed for shadow casting. The actual lighting usually comes from
   *  several directions when using environment lighting, but we need a fixed direction for shadow mapping.
   *
   *   @param {THREE.Vector3} lightDir - direction in world space
   */
  setShadowLightDirection(lightDir) {
    this._shadowLightDir.copy(lightDir);
    this._invalidateShadowMap();
    this.invalidate(true, false, false);

    // update ground transform to make sure that the ground shape is large enough
    // to make the whole shadow visible.
    this._updateGroundTransform();
  }

  /**
   *  The result is the internal vector, modifying it will change the shadow direction
   *  @returns {THREE.Vector3} Either target object or new Vector3 instance.
   */
  getShadowLightDirection() {
    return this._shadowLightDir;
  }

  /**
   * @param {boolean} enable
   * Note that viewer must be initialized first.
   */
  toggleShadows(enable) {
    if (!!this._shadowMaps == !!enable) {
      // no change
      return;
    }

    if (enable) {
      this._shadowMaps = new shadow.ShadowMaps(this._webglrender);
    } else {
      this._shadowMaps.cleanup(this._materials);
      this._shadowMaps = null;
    }

    // Adjust ground plane box if the shadows are getting turned on.
    this._updateGroundTransform();

    this.invalidate(true, true, false);
  }


  fitToView(aggregateSelection, immediate) {

    immediate = !!immediate;
    if (aggregateSelection.length === 0) {
      // If the array is empty, assume that we want
      // all models and no selection
      var allModels = this._renderScene.getModels();
      aggregateSelection = allModels.map(function (model) {
        return {
          model: model,
          selection: []
        };
      });
    }

    if (aggregateSelection.length === 0) {
      return false;
    }

    // Early exit if parameters are not right
    var count2d = 0;
    for (var i = 0; i < aggregateSelection.length; ++i) {

      var model = aggregateSelection[i].model;
      if (!model)
      return false;

      if (model.is2d()) {
        count2d++;
      }
    }

    // Start processing.
    var processed = false;
    if (count2d === aggregateSelection.length) {
      // Aggregate selection on 2d models.
      processed = this._fitToView2d(aggregateSelection, immediate);
    } else {
      // Aggregate selection on 3d models or 2d/3d hybrid.
      processed = this._fitToView3d(aggregateSelection, immediate);
    }

    if (!processed)
    return false;

    if (this._renderScene.getModels().length === 1) {
      // Single Model (backwards compatibility)
      this.api.dispatchEvent({
        type: et.FIT_TO_VIEW_EVENT,
        nodeIdArray: aggregateSelection[0].selection,
        immediate: immediate,
        model: aggregateSelection[0].model
      });
    }

    // Dispatches in both single and multi-model context
    this.api.dispatchEvent({
      type: et.AGGREGATE_FIT_TO_VIEW_EVENT,
      selection: aggregateSelection,
      immediate: immediate
    });

    return true;
  }

  /**
   * Used internally only by Viewer3DImpl::fitToView()
   * For now, only support a single 2D model.
   * @private
   */
  _fitToView2d(aggregateSelection, immediate) {

    if (aggregateSelection.length > 1) {
      logger.warn('fitToView() doesn\'t support multiple 2D models. Using the first one...');
    }

    // Selection
    var model = aggregateSelection[0].model;
    var selection = aggregateSelection[0].selection;

    // Helpers
    var bounds = new THREE.Box3();
    var bc = new BoundsCallback(bounds);

    if (!selection || selection.length === 0) {
      if (this.api.anyLayerHidden()) {

        // Fit only to the visible layers
        var frags = model.getData().fragments;
        var visibleLayerIndices = this.getVisibleLayerIndices();
        for (var i = 0; i < frags.length; i++) {
          this._find2DLayerBounds(model, i, visibleLayerIndices, bc);
        }

      } else {
        // Fit to the whole page
        bounds = this.getFitBounds(true);
      }
    } else
    {
      this.computeSelectionBounds(selection, model, bc);
    }


    if (!bounds.isEmpty()) {
      this.api.navigation.fitBounds(immediate, bounds);
      return true;
    }

    // Unhandled 2D
    return false;
  }

  /**
   * Compute the 2d bounds of selected object.
   * @public
   * @param {Array} selection array of object's ids
   * @param {RenderModel} model
   * @param {BoundsCallback} bc
   */
  computeSelectionBounds(dbIds, model, bc) {

    if (!bc) {
      bc = new BoundsCallback(new THREE.Box3());
    }

    var dbId2fragId = model.getData().fragments?.dbId2fragId;

    // Leaflets dont have fragments. In that case, just return the entire bounding box of the model.
    if (!dbId2fragId) {
      bc.bounds.copy(model.getBoundingBox());
    } else {
      for (var i = 0; i < dbIds.length; i++) {
        var remappedId = model.reverseMapDbIdFor2D(dbIds[i]);
        var fragIds = dbId2fragId[remappedId];
        // fragId is either a single vertex buffer or an array of vertex buffers
        if (Array.isArray(fragIds)) {
          for (var j = 0; j < fragIds.length; j++) {
            // go through each vertex buffer, looking for the object id
            this._find2DBounds(model, fragIds[j], remappedId, bc);
          }
        } else if (typeof fragIds === 'number') {
          // go through the specific vertex buffer, looking for the object id
          this._find2DBounds(model, fragIds, remappedId, bc);
        }
      }

      // Apply model transform to the bounds, since in find2DBounds the transform is not being taken into account.
      bc.bounds.applyMatrix4(model.getPlacementTransform());
    }

    return bc.bounds;
  }

  /**
  * Used internally only by Viewer3DImpl::get3DBounds()
  * Support multiple 3D models.
  * @private
  */
  get3DBounds(aggregateSelection) {

    // First, check if there's anything selected.
    var bNodeSelection = false;
    for (var j = 0; j < aggregateSelection.length; ++j) {
      if (aggregateSelection[j].selection.length > 0) {
        bNodeSelection = true;
        break;
      }
    }

    var bounds = new THREE.Box3();
    var box = new THREE.Box3();

    if (!bNodeSelection) {
      // When there is no node selection, then we need to fit to the whole model(s)
      bounds.union(this.getVisibleBounds(false, false));
    } else {

      // Fit to selected elements only
      for (var i = 0; i < aggregateSelection.length; ++i) {

        var selection = aggregateSelection[i].selection;
        if (selection.length === 0)
        continue;

        // Specific nodes
        var model = aggregateSelection[i].model;
        var objectTree = model.getObjectTree();
        var fragList = model.getFragmentList();

        // objectTree may be null while loading. Without it, dbId2fragId is used for lookup if available.
        const accumulate = (fragId) => {fragList.getWorldBounds(fragId, box);bounds.union(box);};
        const fragments = objectTree ? null : model.getData().fragments;

        for (let s = 0; s < selection.length; ++s) {
          const dbId = parseInt(selection[s]);
          if (objectTree) {
            objectTree.enumNodeFragments(dbId, accumulate, true);
          } else {
            const fragIds = fragments?.dbId2fragId?.[dbId];
            // Fall back to treating dbID as fragID, as established in RenderModel.rayIntersect.
            (Array.isArray(fragIds) ? fragIds : [fragIds ?? dbId]).forEach(accumulate);
          }
        }
      }

    }

    return bounds;
  }

  // Must be triggered if the transform of a model has changed while viewing it.
  onModelTransformChanged(model) {

    // Needed in order to invalidate scene and shadow map.
    this.sceneUpdated();

    // The code below is only needed for selection proxies of consolidated meshes
    if (model.isConsolidated()) {
      // Invalidate affected selection proxies, so that they are updated on next overlay render
      for (var id in this.selectionMeshes) {
        var proxy = this.selectionMeshes[id];
        if (proxy.model === model) {
          proxy.needsUpdate = true;
        }
      }
    }

    this.invalidate(true, true, true);

    this.api.fireEvent({ type: et.MODEL_TRANSFORM_CHANGED_EVENT, model, matrix: model.getModelTransform() });
  }

  /**
   * Change the placement matrix of the model. This overrides the placement transform applied at loadTime.
   *  @param {LmvMatrix4} matrix         - Note that you need 64-Bit precision for large values.
   *  @param {Vector3}    [globalOffset] - Optionally, the globalOffset can be reset in the same step.
   */
  setPlacementTransform(model, matrix) {
    model.setPlacementTransform(matrix);

    this.api.fireEvent({ type: et.MODEL_PLACEMENT_CHANGED_EVENT, model, matrix: model.getPlacementTransform() });

    this.onModelTransformChanged(model);
  }

  /**
   * Change the model's up-direction override at runtime. Recomputes the placement matrix
   * (including world-up rotation) and updates the viewer camera accordingly if the model is the primary model.
   *
   * @param {RenderModel}   model        - The model to update.
   * @param {number[]|null} upDirection  - Axis-aligned up vector [x,y,z], or null to revert to metadata default.
   */
  setModelUpDirectionOverride(model, upDirection) {
    this.applyModelOverrides(model, { overrideModelUpDirection: upDirection });
  }

  /**
   * Change the model's distance-unit override at runtime. Recomputes the placement matrix
   * (including unit scaling) and updates the renderer unit scale accordingly if the model is the primary model.
   *
   * @param {RenderModel}  model - The model to update.
   * @param {string|null}  unit  - Forge unit string (e.g. 'ft', 'm'), or null to revert to metadata default.
   */
  setModelUnitOverride(model, unit) {
    this.applyModelOverrides(model, { overrideDistanceUnit: unit });
  }

  /**
   * Applies up-direction and/or unit overrides to a model's runtime data and returns
   * the resulting placement matrix. The caller is responsible for passing the matrix to
   * setPlacementTransform when ready.
   *
   * Mutations to model data:
   *   - data.overrideModelUpDirection — stores the active up-direction override (or null)
   *   - data.overrideDistanceUnit     — stores the active unit override (or null)
   *   - data.worldUpRotation          — rotation matrix aligning model up to world up (or cleared)
   *   - data.scalingUnit / data.scalingFactor — unit scaling derived from the override
   *   - data.bbox                     — world-space bounding box recomputed with the new placement
   *
   * Side effects:
   *   - Camera world-up (setWorldUp + navigation.setWorldUpVector) updated when the primary model's up-direction changes.
   *   - Renderer unit scale (renderer.setUnitScale) updated when the primary model's unit changes.
   *
   * @param {RenderModel} model
   * @param {Object} overrides - Overrides to apply.
   * @param {number[]|null} [overrides.overrideModelUpDirection] - New up direction, or undefined to keep current.
   * @param {string|null}  [overrides.overrideDistanceUnit]      - New unit, or undefined to keep current.
   * @param {boolean} [applyPlacement=true] - If true (default), automatically calls setPlacementTransform
   *   with the resulting matrix. Set to false to only update model data without changing the visual placement
   *   (useful when the caller will set the placement separately, e.g. during discardChanges).
   * @returns {LmvMatrix4|null} The resulting placement matrix, or null if the model has no placement options.
   */
  applyModelOverrides(model, overrides, applyPlacement = true) {
    const data = model.getData();
    if (!data || !data.loadPlacementOptions) {
      return null;
    }

    const opts = data.loadPlacementOptions;

    // Determine current override values, applying new overrides on top
    const newUpDirection = overrides.overrideModelUpDirection !== undefined ?
    overrides.overrideModelUpDirection :
    data.overrideModelUpDirection || null;

    const newUnit = overrides.overrideDistanceUnit !== undefined ?
    overrides.overrideDistanceUnit :
    data.overrideDistanceUnit || null;

    // Update the override fields on model data
    data.overrideModelUpDirection = newUpDirection;
    data.overrideDistanceUnit = newUnit;

    // Snapshot previous rotation and scaling before recomputing, so we can compute
    // deltas against the current runtime placement instead of recomputing from scratch.
    const prevWorldUpRotation = data.worldUpRotation ? data.worldUpRotation.clone() : null;
    const prevScalingFactor = data.scalingFactor ?? 1;

    // Recompute world-up rotation and unit scaling from the stored base placement
    const result = computeWorldUpAndScaling({
      basePlacement: opts.inputPlacementTransform,
      metadata: data.metadata,
      overrideModelUpDirection: newUpDirection,
      forceWorldUpDirection: opts.forceWorldUpDirection,
      overrideDistanceUnit: newUnit,
      applyScaling: opts.applyScaling,
      applyPlacementInModelUnits: opts.applyPlacementInModelUnits
    });

    // Apply world-up rotation (or clear it)
    data.worldUpRotation = result.worldUpRotation || undefined;

    // Apply scaling results
    if (result.scalingUnit !== undefined) {
      data.scalingUnit = result.scalingUnit;
    } else {
      delete data.scalingUnit;
    }
    if (result.scalingFactor !== undefined) {
      data.scalingFactor = result.scalingFactor;
    } else {
      delete data.scalingFactor;
    }

    // Compute the new placement by applying rotation and scaling deltas to the
    // current runtime placement. This preserves any manual position/rotation edits
    // the user made (e.g. via ModelAlignment), as well as refPointTransform which
    // is already baked into the current placement.
    //
    // Rotation delta:  strip old world-up rotation, apply new one.
    // Scaling delta:   uniform ratio (newFactor / oldFactor). Since uniform scaling
    //                  commutes with rotation, it can be applied after the rotation delta.
    //
    // NOTE: This means this delta update will break if we loose the commutative property of transformations.
    //       E.g. by adding a non-uniform scaling or applying additionnal transformations after rotation/scale.
    //       More information about the current tranformation chain can be found in this ADR:
    //       https://autodesk.atlassian.net/wiki/spaces/visualizationx/pages/300709281/VIZ-ADR-25042+-+Aggregating+Models+with+Varying+Up+Directions
    //
    // These alignment overrides always replace existing refPointTransforms,
    // (which will enable `applyPlacementInModelUnits` on reload and thus flip the order of application)
    // => We need to apply overrides from the left:
    //    newPlacement = S_delta * R_new * R_old⁻¹ * currentPlacement

    let newPlacement = model.getPlacementTransform().clone();
    {
      const newScalingFactor = result.scalingFactor ?? 1;
      const scaleDelta = prevScalingFactor !== 0 ? newScalingFactor / prevScalingFactor : 1;
      const nextR = result.worldUpRotation;

      if (prevWorldUpRotation) {
        newPlacement = newPlacement.multiplyMatrices(prevWorldUpRotation.invert(), newPlacement);
      }
      if (nextR) {
        newPlacement = newPlacement.multiplyMatrices(nextR, newPlacement);
      }
      if (scaleDelta !== 1) {
        const scalingTransform = new Autodesk.Viewing.Private.LmvMatrix4(true).makeScale(scaleDelta, scaleDelta, scaleDelta);
        newPlacement = newPlacement.multiplyMatrices(scalingTransform, newPlacement);
      }
    }

    // NOTE: We do NOT update data.placementTransform or data.placementWithOffset here.
    // Those are load-time baselines that DynamicGlobalOffset.updateModelMatrix uses
    // to compute the delta between the original loaded position and the desired position.
    // Updating them here would cause the delta to collapse to identity, producing no visual change.

    // Recompute model-space bounding box with new placement
    if (data.modelSpaceBBox && !data.modelSpaceBBox.isEmpty()) {
      data.bbox = data.modelSpaceBBox.clone();
      if (newPlacement) {
        data.bbox.applyMatrix4(newPlacement);
      }
      if (data.globalOffset) {
        data.bbox.min.sub(data.globalOffset);
        data.bbox.max.sub(data.globalOffset);
      }
    }

    const isPrimaryModel = model === this.model;

    // Side effect 1: update camera world-up if the primary model's up-direction changed.
    // World-up is governed by the primary model, consistent with setViewFromCamera.
    if (overrides.overrideModelUpDirection !== undefined && isPrimaryModel) {
      const worldUp = model._getAlignedUpVector();
      if (worldUp) {
        this.setWorldUp(worldUp);
        this.api.navigation.setWorldUpVector(worldUp, false, true);
      }
    }

    // Side effect 2: update renderer unit scale if the primary model's unit changed.
    if (overrides.overrideDistanceUnit !== undefined && isPrimaryModel) {
      this._renderer.setUnitScale(model.getUnitScale());
    }

    const finalPlacement = newPlacement || new Autodesk.Viewing.Private.LmvMatrix4(true);

    if (applyPlacement) {
      this.setPlacementTransform(model, finalPlacement);
    }

    return finalPlacement;
  }

  /**
   * Used internally only by Viewer3DImpl::fitToView()
   * Support multiple 3D models.
   * @private
   */
  _fitToView3d(aggregateSelection, immediate) {

    const bounds = this.get3DBounds(aggregateSelection);

    if (!bounds.isEmpty()) {
      this.api.navigation.fitBounds(immediate, bounds);
      return true;
    }

    // Unhandled 3D
    return false;
  }

  /**
   * Supports Fit-To-View for 2D models
   * @private
   */
  _find2DLayerBounds(model, fragId, visibleLayerIds, bc) {
    var geometry = model.getFragmentList().getGeometry(fragId);
    var vbr = new VertexBufferReader(geometry);
    vbr.enumGeomsForVisibleLayer(visibleLayerIds, bc);
  }

  /**
   * Supports Fit-To-View for 2D models
   * @private
   */
  _find2DBounds(model, fragId, dbId, bc) {
    var geometry = model.getFragmentList().getGeometry(fragId);
    var vbr = new VertexBufferReader(geometry);
    vbr.enumGeomsForObject(dbId, bc);
  }

  /**
   * Invoked when WebGL loses the rendering context.
   * Only happens during an unrecoverable error.
   */
  onGpuContextLost() {

    // Drop any model consolidations, because they contain GPU-only meshes
    // If the context restores, we recompute them.
    this._reconsolidateOnRestore = unconsolidateModels(this.api);

    // Clear all fences that might have been active during the context lost
    this.fenceManager.clear();

    this.stop();
    this.api.fireEvent({ type: et.GPU_CONTEXT_LOST_EVENT });
  }

  async onWebGLcontextRestored() {
    // recompute consolidations
    await reconsolidateModels(this.api, this._reconsolidateOnRestore);
    this._reconsolidateOnRestore = null;
    // Clear overlays, because old overlay target renderings cannot be used anymore.
    this._renderer.clearAllOverlays();

    // shadow and reflection targets must be re-rendered before next use
    this._groundShadow.setDirty();
    this._groundReflection && this._groundReflection.setDirty();

    // Bring image back
    this.invalidate(true, true, true);

    this.run();

    this.api.fireEvent({ type: et.WEBGL_CONTEXT_RESTORED_EVENT });
  }

  /** @returns {bool} Check if the model is in the array of visible ones */
  modelVisible(modelId) {
    var model = this._renderScene.findModel(modelId);
    return !!model;
  }

  /**
   * @deprecated
   * Set initial visibility state of nodes.
   * This ensures the UI (Model Browser) matches the main display.
   * Note this function may not work well with transparency cases.
   * @private
   */
  handleInitialVisibility(model) {
    logger.info("Hiding objects that are marked as 'hidden'. This can take up to minutes depending on model size ...");

    var that = this;

    // If the model is just loaded, but not shown, selector is null and this
    // function would crash.
    // Todo: The whole initialVisibility handling needs some major revision in order to properly
    //       work - particularly for aggregated viewing scenarios:
    //        - It doesn't work if a model was toggled off (in an AggregatedView) before the geometry-load was finished.
    //        - It doesn't work if models are just loaded without showing (e.g. diff support models)
    //        - Whether it's applied or not depends on whether a model was in cache or not. Actually, the application
    //          should control it, so that it is applied on an actual view switch, but not when toggling visibility of a model on/off.
    //        - Since it is triggered after geometry loading, but assumes that the model visibility is untouched so far.
    //          But: Since geomLoadDone() may happen much later than model-root load, user or client code may already have modified visibility.
    //          So, handleInitialVisiblity() overwrites this and there is a race condition.
    if (!model.visibilityManager) {
      return;
    }

    function hideInvisibleNodes(instanceTree) {
      // a node is visible if any of its fragments are
      // the LMVTK propagates visibility downwards, and doesn't allow the
      // visibility to be set for fragments directly, so we can infer the visibility
      // of the geometry node

      var frags = model.getFragmentList();
      if (!frags || frags.areAllVisible()) {
        return;
      }

      var invisibleNodes = [];

      if (!instanceTree) {
        return;
      }

      instanceTree.enumNodeChildren(model.getRootId(), function (dbId) {
        var visible = instanceTree.enumNodeFragments(dbId, function (fragId) {
          return frags.isFragVisible(fragId);
        }, true);
        if (!visible && !instanceTree.isNodeHidden(dbId) && !instanceTree.isNodeOff(dbId)) {
          invisibleNodes.push(dbId);
        }
      }, true);

      logger.info("Hiding " + invisibleNodes.length + " objects ...");

      let progress = 0;
      if (invisibleNodes.length > 0) {
        const lastIndex = invisibleNodes.length - 1;
        for (let i = 0; i < invisibleNodes.length; ++i) {
          that.visibilityManager.setVisibilityOnNode(invisibleNodes[i], false, model, i !== lastIndex, false);

          progress = Math.round(i / invisibleNodes.length * 100);
          if (progress > 0 && progress % 10 == 0) {
            logger.info(progress + "%");
          }
        }
      }

      logger.info("Done hiding objects.");
    }

    // Delay hiding invisible nodes to allow other instance tree handlers to execute first
    model.getObjectTree((instanceTree) => setTimeout(() => hideInvisibleNodes(instanceTree)));
  }

  /**
   * Changes the paper visibility for a 2D sheet
   * @param {Autodesk.Viewing.Model} model - the 2D sheet
   * @param {boolean} show
   * @param {boolean} [withTransition] - whether to use optional fading animation
   * @param {function} [onTransitionFinished] - optional callback
   * @param {number} [transitionDuration] - in seconds
   */
  changePaperVisibility(model, show, withTransition = false, onTransitionFinished = null, transitionDuration = 1) {
    if (model.is3d()) {
      onTransitionFinished?.();
      return;
    }

    const frags = model.getFragmentList();

    // Model doesn't have fragments (probably Leaflet). No paper fragment to hide.
    if (!frags) {
      onTransitionFinished?.();
      return;
    }

    if (model.paperVisibilityAnim) {
      model.paperVisibilityAnim.stop();
      model.paperVisibilityAnim = null;
    }

    if (!withTransition || !transitionDuration) {
      model.changePaperVisibility(show);
      this.invalidate(true);
      onTransitionFinished?.();
      return;
    }

    const paperDbId = -1;
    const start = frags.dbIdOpacity[paperDbId] !== undefined ?
    frags.dbIdOpacity[paperDbId] :
    show ? 0 : 1;
    const end = show ? 1 : 0;
    const duration = transitionDuration; // seconds
    const onTimer = (t) => {
      t = Autodesk.Viewing.Private.smootherStep(t);
      frags.setObject2DOpacity(paperDbId, t);
      this.invalidate(true);
    };
    const onFinished = () => {
      model.paperVisibilityAnim = null;
      onTransitionFinished?.();
    };

    model.paperVisibilityAnim = Autodesk.Viewing.Private.fadeValue(start, end, duration, onTimer, onFinished);
  }

  /**
   * Used by Loaders to indicate that the model loading process has began
   * and no geometry is available yet.
   * Works only when there are no models in the scene.
  */
  /**
   * @return {void}
   */
  _signalNoMeshes() {
    if (this._renderScene.isEmpty()) {
      this._geometryAvailable = 0;
    }
  }

  /**
   * Fires NEW_GEOMETRY_ADDED event signaling if a new mesh has been added.
   * Also fires RENDER_FIRST_PIXEL on the first call.
   *
   * @param {number} [geomId] - If supported by the loader, the id of the added mesh
   * @param {RenderModel} model - The model that the geoemtry has been added to
   */
  /**
   * @param {number} geomId
   * @param {Model} model
   * @return {void}
   */
  signalNewGeometryAdded(geomId, model) {
    if (this._geometryAvailable === 0) {
      this._geometryAvailable = 1;
      this.api.fireEvent({ type: et.RENDER_FIRST_PIXEL });
    }
    this.api.fireEvent({ type: et.NEW_GEOMETRY_ADDED, geomId, model });
  }

  /**
   * Fires an event to indicate that the provided material has been loaded.
   * @param {Material} matObj - The material that has been loaded
   * @param {RenderModel} model - The model that the material belongs to
   */
  _signalNewMaterialAdded(matObj, model) {
    this.api.fireEvent({ type: et.NEW_MATERIAL_ADDED, material: matObj, model });
  }

  /**
   * Whether any models have been loaded already.
   * A model is considered loaded as soon as the Model instance has
   * been added to RenderScene.
   */
  hasModels() {
    return !this._renderScene.isEmpty();
  }

  // Update loadingAnimationDuration according to preferences.
  onLoadingAnimationChanged(value) {
    this._webglrender.setLoadingAnimationDuration(value ? this.defaultLoadingAnimationDuration : -1);
    this.invalidate(false, true, false);
  }

  isLoadingAnimationEnabled() {
    return this.api.prefs.get(Prefs2D.LOADING_ANIMATION) && (!this.model || this.model.is2d());
  }

  /**
   * Optional: Exclude all models from near/farPlane computation whose camera distance is beyond the given threshold.
   * This avoids rendering artifacts for aggregated views that contain models with broken georeferencing, so that they
   * are too far away to be rendered properly together with closer content.
   * Note: Make sure that the value is not too small, because this also puts a general limit on the zoom-out distance.
   * @param {number} [maxDist=1e+5] - the maximum far plane distance at which models are ignored
   * @return {void}
   */
  setMaxModelDistance(maxDist = 1e+5) {
    this.maxModelDistance = maxDist;
  }

  // Internally applied during near/far-computation. Only used if a maxModelDistance is set (off by default).
  // No effect as long as near/far distance is uncritical. If the near/far difference is above the threshold,
  // it recomputes the near/far while excluding models that are far away.
  _applyMaxModelDistanceToNearFar() {

    // Do nothing unless we expect z-buffer artifacts: Even a huge far-value is not a problem
    // as long as the near value is large as well.
    var camera = this.camera;
    if (camera.far <= camera.near + this.maxModelDistance) {
      return null;
    }

    // Define bboxFilter that excludes models that are far away
    const dMax = camera.near + this.maxModelDistance;
    var bboxFilter = function (bbox) {
      // Check box distance
      var dist2 = SceneMath.pointToBoxDistance2(camera.position, bbox);
      return dist2 < dMax * dMax;
    };

    // Recompute near/far while excluding models that are too far away
    const worldBox = this.getVisibleBounds(true, this._overlayDirty, bboxFilter);
    this.updateNearFarValues(camera, worldBox);
  }


  /**
   * see RenderModel.consolidateAsync()
   *
   * @param {RenderModel} model - The model to be consolidated
   * @param {number} byteLimit  - see RenderModel.consolidateAsync() for exact description
   * @returns {Promise}
   */
  consolidateModel(model, byteLimit) {
    if (!useEarlyConsolidation(model)) {
      return model.consolidateAsync(this._materials, byteLimit, this._webglrender, this.useDeferredConsolidation);
    } else {
      console.warn("Model consolidation shouldn't be triggered explicitly when using dynamic GPU memory management.");
    }
  }

  /**
   * Internal function, which is called internally to trigger the consolidation of a model.
   * It should not be invoked directly.
   *
   * @param {RenderModel} model - The model to be consolidated
   * @param {number} byteLimit  - see RenderModel.consolidateAsync() for exact description
   * @param {boolean} forceConsolidationMapRecomputation - do we need to recompute the consolidation map?
   * @returns {Promise}
   */
  _consolidateModelInternal(model, forceConsolidationMapRecomputation = false) {
    return model.consolidateAsync(this._materials, undefined, this._webglrender, this.useDeferredConsolidation, forceConsolidationMapRecomputation);
  }

  setDoNotCut(model, doNotCut) {
    model.setDoNotCut(this._materials, doNotCut);
  }

  setViewportBounds(model, bounds) {
    model.setViewportBounds(this._materials, bounds);
    this.invalidate(true);

    this.api.fireEvent({ type: et.MODEL_VIEWPORT_BOUNDS_CHANGED_EVENT, model, bounds });
  }

  isWebGPU() {
    return this._useWebGPU;
  }

  setFrameTimePriority(priority) {
    this._frameTimePriority = priority;
    if (USE_OUT_OF_CORE_TILE_MANAGER) {
      renderBudgetManager.updateViewerPriority(this, priority);
    }
  }

  getFrameTimePriority() {
    return this._frameTimePriority;
  }
}

GlobalManagerMixin.call(Viewer3DImpl.prototype);